import { IDropdownOption, mergeStyleSets } from "@fluentui/react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { Box, Snackbar, SnackbarCloseReason } from "@mui/material";
import readNDJSONStream from "ndjson-readablestream";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STATUS, Step } from "react-joyride";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTemplateManagement } from "./hooks/useTemplateManagement";
import { useSettingsHandlers } from "../../hooks/useSettingsHandlers";
import { useVersionPreservation } from "./hooks/useVersionPreservation";
import { useSetState } from "../../hooks/useSetState";
import store, {
    RootState,
    selectTemplateAppliedFlag,
    selectCurrentSessionId,
    selectRequestedToForceStop,
    selectIsSessionSearchActive,
    selectTemplateContents,
    selectSelectedProduct,
    selectSuggestionsStage
} from "../../Store";
import {
    setDefaultTemplateIdInStore,
    setSaveTemplateIdInStore,
    setSelectedProductInStore,
    setSystemDefaultContentInStore,
    setTemplateContentsInStore,
    setUserDefaultContentInStore
} from "../../Store/TemplateContents";
import {
    AppType,
    chatApi,
    chatPrecheckApi,
    ChatAppRequest,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatModel,
    isReasoningModel,
    isKnownChatModel,
    DataOptions,
    DataPoints,
    deleteFile,
    deleteFileOnUnload,
    DeveloperOptions,
    Feedback,
    feedbackApi,
    getAppTypeBasedOnLastAnswerIsRelatedToAriel,
    getChatSummaryTitle,
    getFileAttachmentServerSideIds,
    getSessionInfoData,
    loadChatApi,
    loadSessionApi,
    loadSharedChatApi,
    loadTemplate,
    QuestionDetails,
    ReasoningEffort,
    RetrievalMode,
    ResponseMessage,
    SaveResponse,
    saveAppSettings,
    saveUserSettings,
    SessionInfo,
    updateAppTypeValueInSessionStorage,
    calculateVersions,
    getIdToCheck,
    loadAnswerRowsFromTree,
    insertRowIntoTree,
    buildTree,
    insertIntoHistories,
    updateHistoriesByQuestionId,
    updateTreeAnswerByQuestionId,
    updateAnswersByIndex,
    RetrievalReasoningEffort
} from "../../api";
import { appInfo, supportEnvBoard, userInfo, userId, isAdmin, isWapUser } from "../../authConfig";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { AllProductSuggestionsPanel } from "../../components/AllProductSuggestionsPanel/AllProductSuggestionsPanel";
import { AnswerError, AnswerLoading, UnifiedAnswer } from "../../components/Answer";
import { parseAnswerToHtml } from "../../components/Answer/AnswerParser";
import { AnswerRegenarate } from "../../components/Answer/AnswerRegenarate";
import {
    AllProductMatchedProduct,
    AllProductPrecheckProgressUpdate,
    AllProductPrecheckResult,
    AllProductSelectionMeta,
    PreparedSendRequest,
    QuestionInputRef
} from "../../components/QuestionInput";
import type { AllProductPrecheckSelectionDecision } from "../../api";

import { Sidebar } from "../../components/Sidebar/Sidebar";
import { UserChatMessage, UserChatMessageRef } from "../../components/UserChatMessage";
import i18next from "../../i18n/i18n";
import styles from "./Chat.module.css";
import DynamicTemplatePrompt from "./DynamicTemplatePrompt";
import { AnswerGenerationActiveDetails, FileAttachmentDetails, QuestionInputReducerAction } from "../../Store/QuestionInputReducer";
import { ChatComponentReducerAction } from "../../Store/ChatComponentReducer";
import { AllProductSuggestionsAction } from "../../Store/AllProductSuggestionsReducer";
import type { AllProductCitationInsight, AllProductScoreInsight } from "../../Store/AllProductSuggestionsReducer";
import { Attachment, FileAttachments } from "../../components/FileAttachments/FileAttachments";
import { TokenLimitErrorDialog } from "../../components/TokenLimitErrorDialog/TokenLimitErrorDialog";
import SessionNotFound from "../../components/Sidebar/SessionNotFound";
import { extractAttachmentNamesFromDataPoints, isPartialAttachmentNameMatch, normalizeAttachmentName } from "../../utils/AttachmentNameUtils";
import { resolvePrecheckAutoSelectedProduct } from "../../utils/precheckSelection";

// Import modular hooks, components, and utilities
import {
    useChatState,
    useSessionState,
    useProductFilterState,
    useTemplateState,
    useDeveloperSettings,
    useUIState,
    useSessionAPI,
    useFeedbackAPI,
    useReduxActions,
    useThoughtsLoader
} from "./hooks";

import { SettingsPanel, WarningPopup, AppTour, ChatMessagesArea, EmptyChatState, ChatInputWrapper } from "./components";
import QAExpandOverlay from "./components/QAExpandOverlay";

import {
    getValidAppType,
    answerKeyFor,
    setProductFilterForRequestPayload,
    setSubProductFilterForRequestPayload,
    setProductVersionForRequestPayload,
    setGroupIdsByAppTypeForRequestPayload,
    getProductDetailsFromAppInfo,
    extractPayloadError,
    hasTemplateReadFailure,
    getSystemDefaultTemplate,
    getUserDefaultContentTemplate,
    hasUsableTemplateEntry,
    isValidTemplateId,
    getTemplateDataById,
    getPromptInstructionsFromQuestion,
    parseTemplatePayload,
    resolveEffectiveTemplateForProduct,
    resolveTemplatePayload,
    syncTemplatePayloadToStore,
    getTemplateContentForPayload,
    reformQuestionContentForAI,
    parseQueryParams,
    updateSessionStorageForSearch,
    shouldDisableInputBox,
    resolveProductLocale
} from "./utils";
import { getAppName, generateNewSessionId, shouldUseCombinedAppTypeResponse } from "./utils/chatHelpers";

// Utility function to deduplicate file attachments by serverSideId
export const deduplicateFileAttachments = (attachments: { serverSideId: string; fileName: string }[]): { serverSideId: string; fileName: string }[] => {
    const seen = new Set<string>();
    return attachments.filter(attachment => {
        if (seen.has(attachment.serverSideId)) {
            return false;
        }
        seen.add(attachment.serverSideId);
        return true;
    });
};

export const isAbortError = (error: unknown): boolean => {
    if (!error) return false;
    if (typeof error === "string") return error === "Abort Active Chat API Request";
    if (error instanceof Error) {
        return error.name === "AbortError" || error.message === "Abort Active Chat API Request";
    }
    const maybeError = error as { name?: string; message?: string };
    return maybeError?.name === "AbortError" || maybeError?.message === "Abort Active Chat API Request";
};

export const normalizeAgenticRetrievalOutputSize = (value: unknown): number | undefined => {
    const parsed = parseAgenticRetrievalOutputSizeInput(value);
    return parsed !== undefined && parsed >= 5000 ? parsed : undefined;
};

const parseAgenticRetrievalOutputSizeInput = (value: unknown): number | undefined => {
    if (typeof value === "number") {
        return Number.isSafeInteger(value) && value > 0 ? value : undefined;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed || trimmed.toLowerCase() === "none") {
            return undefined;
        }
        if (/^\d+$/.test(trimmed)) {
            const parsed = Number.parseInt(trimmed, 10);
            return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
        }
    }

    return undefined;
};

export const normalizeAgenticRetrievalDocumentCount = (value: unknown): number | undefined => {
    const parsed = parseAgenticRetrievalDocumentCountInput(value);
    // Output-level: API allows 1–200
    return parsed !== undefined && parsed >= 1 && parsed <= 200 ? parsed : undefined;
};

const parseAgenticRetrievalDocumentCountInput = (value: unknown): number | undefined => {
    if (typeof value === "number") {
        return Number.isSafeInteger(value) && value > 0 ? value : undefined;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed || trimmed.toLowerCase() === "none") {
            return undefined;
        }
        if (/^\d+$/.test(trimmed)) {
            const parsed = Number.parseInt(trimmed, 10);
            return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
        }
    }

    return undefined;
};

// Source-level: API allows 50–200
export const normalizeAgenticRetrievalSourceDocumentCount = (value: unknown): number | undefined => {
    const parsed = parseAgenticRetrievalDocumentCountInput(value);
    return parsed !== undefined && parsed >= 50 && parsed <= 200 ? parsed : undefined;
};

const useStyles = makeStyles({
    root: {
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        ...shorthands.padding("10px", "20px"),
        rowGap: "10px",
        paddingTop: "0px",
        paddingBottom: "0px",
        paddingLeft: "20px",
        paddingRight: "20px"
    },
    tabStyle: {
        color: "white",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        lineHeight: 2,
        fontSize: "15px",
        cursor: "pointer",
        ":active": {
            fontWeight: "bold"
        },
        ":selected": {
            fontWeight: "bold"
        },
        ":hover": {
            fontWeight: "bold"
        },
        ":target": {
            fontWeight: "bold"
        }
    },
    divOnHover: {
        ":hover": {
            backgroundColor: "#EDEBE9"
        }
    }
});
type TokenValues = {
    isInchargeOfAiWorks: string;
    userId: string;
};
interface Props {
    executeClear: boolean;
    callback: () => void;
    startTourByIcon: boolean;
    startTour: (start: boolean) => void;
}

type Product = {
    manual_key: string;
    faq_key: string;
    text: string;
    versions: ProductVersion[];
    is_sub_only?: string;
    faq_groupids: string[];
    manual_groupids: string[];
};

type SubProduct = {
    manual_sub_key: string;
    faq_sub_key: string;
    text: string;
};

type ProductVersion = {
    manual_version_key: string;
    faq_version_key: string;
    text: string;
    sub_versions: SubProduct[];
    faq_sub_key?: string;
};

type ForcedProductSelection = {
    product_filter: { manual_key: string; faq_key: string };
    sub_product_filter: { manual_sub_key: string; faq_sub_key: string };
    product_version: { manual_version_key: string; faq_version_key: string };
};

type ProductScenarioRegenerateContext = {
    primaryId: number;
    targetAppType: AppType;
    reasoningEffort: ReasoningEffort;
    convId: string;
};

type TemplateApplySwitchSource = "manual-dropdown" | "programmatic" | null;
type RequestSourceMode = "chat" | "history";

const normalizeSessionIdentity = (value: unknown): string => String(value ?? "").trim();

const Chat = (props: Props) => {
    const userName = userId ?? "";
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize all custom hooks to replace 50+ useState declarations
    const initalAppTypeValue = appInfo.use_all_tab === true ? userInfo?.apptype : userInfo?.apptype !== "ALL" ? userInfo?.apptype : "FAQ";

    const validInitialAppType = getValidAppType(initalAppTypeValue);

    // Use extracted custom hooks (replaces 50+ useState)
    const devSettings = useDeveloperSettings(ChatModel.GPT_6_LUNA);
    const templateState = useTemplateState();
    const sessionState = useSessionState();
    const filterState = useProductFilterState(isAdmin || devSettings.enableAllProductForAllUsers);
    const uiState = useUIState();

    // Initialize chat state with initial app type
    const chatStateHook = useChatState(validInitialAppType, ChatModel.GPT_6_LUNA);

    // Version preservation hook for abort operations
    const { snapshotVersionIndices, restorePreservedSelections, clearVersionIndices } = useVersionPreservation();

    const [answerIndexMap, setAnswerIndexMap] = useState<Record<string, number>>({});
    const [regeneratedAnswers, setRegeneratedAnswers] = useState<Record<string, Record<string, ChatAppResponse[]>>>({});
    const [isRegenerateRequestInFlight, setIsRegenerateRequestInFlight] = useState<boolean>(false);
    const [expandedQAIndex, setExpandedQAIndex] = useState<number | null>(null);
    const [thoughtsLoadingIndex, setThoughtsLoadingIndex] = useState<number | null>(null);
    const isRegenerateActive = useRef(false);
    const [cosmosDBRetrievalMode, setCosmosDBRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Vectors);
    const [retrievalReasoningEffort, setRetrievalReasoningEffort] = useState<RetrievalReasoningEffort>(RetrievalReasoningEffort.Low);
    const [show206ErrorDialog, setShow206ErrorDialog] = useState(false);
    const [error206Message, setError206Message] = useState("");
    const [error206Question, setError206Question] = useState("");
    const [clarificationInfo, setClarificationInfo] = useState<{ type: "greeting" | "vague" | "off_topic"; message: string } | null>(null);
    const [clarificationOriginalQuestion, setClarificationOriginalQuestion] = useState<string>("");
    const [clarificationMode, setClarificationMode] = useState<"chat" | "history" | null>(null);
    // Stores the mode + appType (and an optional replay-with-new-text closure for edit requests)
    // needed when the user submits a question from the clarification dialog.
    const clarificationSendContextRef = useRef<{
        mode: "chat" | "history";
        appType: AppType;
        questionContainsTemplate?: boolean;
        replayWithText?: (newText: string) => void;
    } | null>(null);
    const pendingQuestionSendRef = useRef<(() => void) | null>(null);
    const loadedAnswerCountAfterAbortRef = useRef<number | null>(null);
    const lastStableAppTypeBeforeTabSwitchRef = useRef<AppType | null>(null);
    const [clearedTemplateSelectionProduct, setClearedTemplateSelectionProduct] = useState<string | null>(null);
    const [suggestionDrivenTemplateSuppressedProduct, setSuggestionDrivenTemplateSuppressedProduct] = useState<string | null>(null);
    const previousTemplateSelectionProductRef = useRef<string>("");
    const suppressTemplateAutoApplyWhileRegenerateSelectionRef = useRef(false);
    const pendingTemplateApplySourceRef = useRef<{
        source: TemplateApplySwitchSource;
        product: string | null;
        manual_key?: string | null;
        faq_key?: string | null;
    }>({
        source: null,
        product: null
    });
    const markTemplateApplySourceForNextSwitch = (
        source: TemplateApplySwitchSource,
        product?: string | null,
        productKeys?: { manual_key?: string | null; faq_key?: string | null }
    ) => {
        pendingTemplateApplySourceRef.current = {
            source,
            product: typeof product === "string" ? product : null,
            manual_key: productKeys?.manual_key ?? null,
            faq_key: productKeys?.faq_key ?? null
        };
    };

    // Destructure with backward-compatible names
    const {
        answers,
        setAnswers,
        histories,
        setHistories,
        versionIndexMap,
        setVersionIndexMap,
        allAnswersTree,
        setAllAnswersTree,
        conversationDetails,
        setConversationDetails,
        selectedAppType,
        setSelectedAppType,
        chatModel,
        setChatModel,
        currentView,
        setCurrentView,
        currentIndex,
        setCurrentIndex,
        selectedSessionId,
        setSelectedSessionId,
        newChatQuestion,
        setnewChatQuestion,
        isLoading,
        setIsLoading,
        isAnswerLoading,
        setIsAnswerLoading,
        isStreaming,
        setIsStreaming,
        isSessionLoading,
        setIsSessionLoading,
        isHistoryPageInitialLoading,
        setIsHistoryPageInitialLoading,
        isNewChatThreadInitialLoading,
        setIsNewChatThreadInitialLoading,
        error,
        setError,
        selectedAnswer,
        setSelectedAnswer,
        selectedQuestion,
        setSelectedQuestion,
        IsOnEdit,
        setIsOnEdit,
        isOnSave,
        setIsOnSave,
        isEdited,
        setIsEdited,
        isChatShared,
        setIsChatShared,
        sessionId,
        setSessionId,
        clearChatTriggered,
        setClearChatTriggered,
        chatContentCleared,
        setChatContentCleared,
        scrollToAnswer,
        setScrollToAnswer,
        isSessionNotFound,
        setIsSessionNotFound,
        currentQuestionInfo,
        lastQuestionRef,
        lastAnswerRef,
        lastDataPointsRef,
        lastThoughtsRef,
        lastFollowUpRef,
        lastKeyWordRef,
        lastAnswerIndexRef,
        currentTargetQuestionId,
        lastQuestionIndexValue,
        conversationId,
        isTabClick
    } = chatStateHook;

    const setSharedAnswerLoadingState = useCallback(
        (value: boolean) => {
            setIsAnswerLoading(value);
            dispatch(ChatComponentReducerAction.updateIsAnswerLoading(value));
        },
        [dispatch, setIsAnswerLoading]
    );

    // Destructure session state
    const {
        todaySessionsRef,
        setTodaySessionsRef,
        last7SessionsRef,
        setLast7SessionsRef,
        last30SessionsRef,
        setLast30SessionsRef,
        monthlySessions,
        setMonthlySessions,
        nextDateToLoad,
        setNextDateToLoad,
        searchTerm,
        setSearchTerm,
        backupTodaySessions,
        setBackupTodaySessions,
        backupLast7Sessions,
        setBackupLast7Sessions,
        backupLast30Sessions,
        setBackupLast30Sessions,
        backupMonthlySessions,
        setBackupMonthlySessions,
        lastActiveIndexRef,
        lastSessionRef,
        lastActiveSessionRef,
        nextDateToLoadRef
    } = sessionState;

    // Destructure filter state
    const {
        products,
        setProducts,
        productVersions,
        setProductVersions,
        subProducts,
        setSubProducts,
        productIndexes,
        setProductIndexes,
        productVersionIndexes,
        setProductVersionIndexes,
        subProductIndexes,
        setSubProductIndexes,
        productText,
        setProductText,
        productVersionText,
        setProductVersionText,
        subProductText,
        setSubProductText,
        isMainDropdownOpen,
        setIsMainDropdownOpen,
        isVersionsOpen,
        setIsVersionsOpen,
        isSubDropdownOpen,
        setIsSubDropdownOpen,
        emptyProduct,
        setEmptyProduct,
        mainDropdownRef,
        divProdFilterRefs,
        divProdVersionFilterRefs,
        lastHoveredRef,
        setLastHoveredRef,
        lastVerHoveredRef,
        setLastVerHoveredRef
    } = filterState;

    // Destructure template state
    const {
        selectedTemplate,
        setSelectedTemplate,
        selectedTemplateContent,
        setSelectedTemplateContent,
        selectedTemplateContentId,
        setSelectedTemplateContentId,
        hideDynamicTemplatePopup,
        setHideDynamicTemplatePopup,
        templateKeyWords,
        setTemplateKeywords,
        changingTemplateContent,
        setChangingTemplateContent,
        activateTemplateTour,
        setActivateTemplateTour
    } = templateState;

    // Destructure developer settings
    const {
        isConfigPanelOpen,
        setIsConfigPanelOpen,
        promptTemplate,
        setPromptTemplate,
        retrieveCount,
        setRetrieveCount,
        onlyCitations,
        setOnlyCitations,
        retrievalMode,
        setRetrievalMode,
        docTypeFilter,
        setDocTypeFilter,
        useSemanticRanker,
        setUseSemanticRanker,
        shouldStream,
        setShouldStream,
        useSemanticCaptions,
        setUseSemanticCaptions,
        excludeCategory,
        setExcludeCategory,
        useSuggestFollowupQuestions,
        setUseSuggestFollowupQuestions,
        useOidSecurityFilter,
        setUseOidSecurityFilter,
        useGroupsSecurityFilter,
        setUseGroupsSecurityFilter,
        useAgenticKnowledgebase,
        setUseAgenticKnowledgebase,
        enableAllProductForAllUsers,
        setEnableAllProductForAllUsers,
        enableAllProductForUser,
        setEnableAllProductForUser,
        categories,
        setCategories,
        doctype,
        setDocType,
        reasoningEffort,
        setReasoningEffort,
        agenticRetrievalOutputSize,
        setAgenticRetrievalOutputSize,
        agenticRetrievalDocumentCount,
        setAgenticRetrievalDocumentCount,
        agenticRetrievalSourceDocumentCount,
        setAgenticRetrievalSourceDocumentCount,
        allProductAutoSelectEnabled,
        setAllProductAutoSelectEnabled,
        allProductAutoSelectMinConfidence,
        setAllProductAutoSelectMinConfidence,
        allProductAutoSelectMinMargin,
        setAllProductAutoSelectMinMargin,
        allProductAutoSelectMinHits,
        setAllProductAutoSelectMinHits,
        allProductAutoSelectMinAvgRerankerScore,
        setAllProductAutoSelectMinAvgRerankerScore,
        allProductAutoSelectMinAvgRerankerGap,
        setAllProductAutoSelectMinAvgRerankerGap,
        allProductAutoSelectWeightAverage,
        setAllProductAutoSelectWeightAverage,
        allProductAutoSelectWeightPeak,
        setAllProductAutoSelectWeightPeak,
        allProductAutoSelectWeightVolume,
        setAllProductAutoSelectWeightVolume
    } = devSettings;

    // Destructure UI state
    const {
        activeCitation,
        setActiveCitation,
        activeAnalysisPanelTab,
        setActiveAnalysisPanelTab,
        selectedCitation,
        setSelectedCitation,
        warningShown,
        setWarningShown,
        applyRestriction,
        setApplyRestriction,
        showSupportLink,
        setShowSupportLink,
        isSnackOpen,
        setIsSnackOpen,
        sessionStatus,
        setSessionStatus,
        windowSize,
        setWindowSize,
        activateAnswerTour,
        setActivateAnswerTour
    } = uiState;

    // Initialize API hooks for extracted API logic
    const sessionAPI = useSessionAPI();
    const feedbackAPI = useFeedbackAPI();

    // Initialize Redux actions hook for decoupled dispatch operations
    const reduxActions = useReduxActions();

    const { loadThoughts } = useThoughtsLoader({
        answers,
        answerIndexMap,
        sourcePage: "chat",
        setAnswers,
        setRegeneratedAnswers,
        setAllAnswersTree,
        setThoughtsLoadingIndex,
        setActiveAnalysisPanelTab,
        setSelectedAnswer
    });

    // Constants that don't need state
    const aimodelsAdmin = appInfo.aimodel_admin;
    const aimodelsUser = appInfo.aimodel_user;
    const retrievalModes = appInfo.retrieval_mode;
    const selectedLocale = sessionStorage.getItem("selectedLocale") ?? "ja";
    const resolvedProductLocale = resolveProductLocale(selectedLocale, appInfo);
    const selectedLanguage = new Intl.DisplayNames(["en"], { type: "language" }).of(selectedLocale);
    const defaultManualsAiModel = appInfo.aimodel_default.manuals;
    const defaultFaqAiModel = appInfo.aimodel_default.faq;
    const appInfoAgenticRetrievalOutputSizeDefaultRaw = (appInfo as { agentic_retrieval_output_size_default?: unknown })?.agentic_retrieval_output_size_default;
    const appInfoAgenticRetrievalOutputSizeDefault = normalizeAgenticRetrievalOutputSize(appInfoAgenticRetrievalOutputSizeDefaultRaw);
    const appInfoAgenticRetrievalDocumentCountDefaultRaw = (appInfo as { agentic_retrieval_document_count_default?: unknown })
        ?.agentic_retrieval_document_count_default;
    const appInfoAgenticRetrievalDocumentCountDefault = normalizeAgenticRetrievalDocumentCount(appInfoAgenticRetrievalDocumentCountDefaultRaw);
    const appInfoAgenticRetrievalSourceDocumentCountDefaultRaw = (appInfo as { agentic_retrieval_source_document_count_default?: unknown })
        ?.agentic_retrieval_source_document_count_default;
    const appInfoAgenticRetrievalSourceDocumentCountDefault = normalizeAgenticRetrievalSourceDocumentCount(
        appInfoAgenticRetrievalSourceDocumentCountDefaultRaw
    );
    const appInfoAllProductAutoSelectSettings = (appInfo as any)?.all_product_auto_select ?? {};
    const appInfoDefaultWeightAverage = Number(appInfoAllProductAutoSelectSettings?.weight_average);
    const appInfoDefaultWeightPeak = Number(appInfoAllProductAutoSelectSettings?.weight_peak);
    const appInfoDefaultWeightVolume = Number(appInfoAllProductAutoSelectSettings?.weight_volume);

    useEffect(() => {
        setAgenticRetrievalOutputSize(appInfoAgenticRetrievalOutputSizeDefault);
    }, [appInfoAgenticRetrievalOutputSizeDefault, setAgenticRetrievalOutputSize]);

    useEffect(() => {
        setAgenticRetrievalDocumentCount(appInfoAgenticRetrievalDocumentCountDefault);
    }, [appInfoAgenticRetrievalDocumentCountDefault, setAgenticRetrievalDocumentCount]);

    useEffect(() => {
        setAgenticRetrievalSourceDocumentCount(appInfoAgenticRetrievalSourceDocumentCountDefault);
    }, [appInfoAgenticRetrievalSourceDocumentCountDefault, setAgenticRetrievalSourceDocumentCount]);

    // Remaining refs
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const questionInputRefs = useRef<{ [key: string]: QuestionInputRef | null }>({});
    const makeApiRequestControllerRef = useRef<AbortController | null>(null);
    const skipValidationOnceRef = useRef(false);
    const loadChatDataRequestTokenRef = useRef(0);
    // Guard: suppresses the selectedAppType URL-sync effect for one cycle
    // after clearChat so it does not re-navigate with a stale SessionId.
    const suppressAppTypeUrlSyncRef = useRef(false);
    const allProductPrecheckControllerRef = useRef<AbortController | null>(null);
    const activeSuggestionsSessionIdRef = useRef<string>(normalizeSessionIdentity(sessionStorage.getItem("sessionId")));
    // Stable ref so that early callbacks (defined before handleQuestionInputSend) can call the
    // latest version without a TypeScript forward-reference error.
    const handleQuestionInputSendRef = useRef<
        (
            mode: "chat" | "history",
            question: string,
            questionContainsTemplate: boolean,
            appType: AppType,
            forcedProductSelection?: ForcedProductSelection,
            templateLookupProductOverride?: string,
            selectionMeta?: AllProductSelectionMeta
        ) => void
    >(() => {});
    const precheckResultCacheRef = useRef<{
        fingerprint: string;
        keywords: string;
        matchedProducts: AllProductMatchedProduct[];
        searchCitations: AllProductCitationInsight[];
        scoreCandidates: AllProductScoreInsight[];
        selectionDecision?: AllProductPrecheckSelectionDecision | null;
    } | null>(null);
    const userChatMessageRefs = useRef<{ [key: number]: UserChatMessageRef | null }>({});
    const lastFileMappingRef = useRef<any>(null);

    // Tracks previous flag value so we only act on actual toggles, not initial render.
    const previousEnableAllProductForAllUsersRef = useRef(enableAllProductForAllUsers);
    // Guard: set to true by the global toggle so the personal-flag effect knows to skip.
    const isPersonalFlagChangedByGlobalRef = useRef(false);
    // Tracks previous personal flag value.
    const previousEnableAllProductForUserRef = useRef(enableAllProductForUser);
    // Snapshot for personal flag, same shape as the global one.
    const savedProductSelectionForUserRef = useRef<{
        productIndexes: typeof productIndexes;
        subProductIndexes: typeof subProductIndexes;
        productVersionIndexes: typeof productVersionIndexes;
        productText: typeof productText;
        subProductText: typeof subProductText;
        productVersionText: typeof productVersionText;
    } | null>(null);
    // Snapshot of filter selections before the flag was enabled, used to restore on disable.
    const savedProductSelectionRef = useRef<{
        productIndexes: typeof productIndexes;
        subProductIndexes: typeof subProductIndexes;
        productVersionIndexes: typeof productVersionIndexes;
        productText: typeof productText;
        subProductText: typeof subProductText;
        productVersionText: typeof productVersionText;
    } | null>(null);

    useEffect(() => {
        if (previousEnableAllProductForAllUsersRef.current === enableAllProductForAllUsers) {
            return;
        }
        previousEnableAllProductForAllUsersRef.current = enableAllProductForAllUsers;

        if (enableAllProductForAllUsers) {
            // Save current selection before switching to ALL-defaults.
            savedProductSelectionRef.current = {
                productIndexes: { ...productIndexes },
                subProductIndexes: { ...subProductIndexes },
                productVersionIndexes: { ...productVersionIndexes },
                productText,
                subProductText,
                productVersionText
            };
            markTemplateApplySourceForNextSwitch("programmatic", "ALL");
            setProductIndexes({ manual_key: "0", faq_key: "0" });
            setProductText("ALL");
            setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
            setSubProductText("ALL");
            setProductVersionIndexes({ manual_version_key: "0", faq_version_key: "0" });
            setProductVersionText("ALL");
        } else {
            // On disabling ALL, first restore the persisted user selection from userInfo.
            // If not available (or invalid), fall back to in-memory snapshot, then defaults.
            const saved = savedProductSelectionRef.current;
            const userInfoProduct = {
                manual_key: String(userInfo?.product?.manual_key ?? "").trim(),
                faq_key: String(userInfo?.product?.faq_key ?? "").trim()
            };
            const hasPersistedUserSelection = userInfoProduct.manual_key !== "" || userInfoProduct.faq_key !== "";
            const isPersistedAllSelection = userInfoProduct.manual_key === "0" || userInfoProduct.faq_key === "0";

            if (hasPersistedUserSelection && !isPersistedAllSelection) {
                setDropDownFilterText(
                    userInfoProduct,
                    userInfo?.productVersion ?? { manual_version_key: "", faq_version_key: "" },
                    userInfo?.subProduct ?? { manual_sub_key: "", faq_sub_key: "" }
                );
            } else if (saved) {
                markTemplateApplySourceForNextSwitch("programmatic", saved.productText);
                setProductIndexes(saved.productIndexes);
                setProductText(saved.productText);
                setSubProductIndexes(saved.subProductIndexes);
                setSubProductText(saved.subProductText);
                setProductVersionIndexes(saved.productVersionIndexes);
                setProductVersionText(saved.productVersionText);
            } else {
                markTemplateApplySourceForNextSwitch("programmatic", `${i18next.t("chat.productFilterText")}`);
                setProductIndexes({ manual_key: "", faq_key: "" });
                setProductText(`${i18next.t("chat.productFilterText")}`);
                setSubProductIndexes({ manual_sub_key: "", faq_sub_key: "" });
                setSubProductText("...");
                setProductVersionIndexes({ manual_version_key: "", faq_version_key: "" });
                setProductVersionText(`${i18next.t("chat.sub_version")}`);
            }
            savedProductSelectionRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableAllProductForAllUsers]);

    useEffect(() => {
        // If this change was triggered by the global toggle, skip — global effect handles state.
        if (isPersonalFlagChangedByGlobalRef.current) {
            isPersonalFlagChangedByGlobalRef.current = false;
            previousEnableAllProductForUserRef.current = enableAllProductForUser;
            return;
        }
        if (previousEnableAllProductForUserRef.current === enableAllProductForUser) {
            return;
        }
        previousEnableAllProductForUserRef.current = enableAllProductForUser;

        if (enableAllProductForUser) {
            // Admin turning personal ALL ON: snapshot current state, switch to ALL.
            savedProductSelectionForUserRef.current = {
                productIndexes: { ...productIndexes },
                subProductIndexes: { ...subProductIndexes },
                productVersionIndexes: { ...productVersionIndexes },
                productText,
                subProductText,
                productVersionText
            };
            markTemplateApplySourceForNextSwitch("programmatic", "ALL");
            setProductIndexes({ manual_key: "0", faq_key: "0" });
            setProductText("ALL");
            setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
            setSubProductText("ALL");
            setProductVersionIndexes({ manual_version_key: "0", faq_version_key: "0" });
            setProductVersionText("ALL");
        } else {
            // Admin turning personal ALL OFF: first restore persisted user selection from userInfo.
            // If not available (or invalid), fall back to snapshots, then defaults.
            const saved = savedProductSelectionForUserRef.current ?? savedProductSelectionRef.current;
            const userInfoProduct = {
                manual_key: String(userInfo?.product?.manual_key ?? "").trim(),
                faq_key: String(userInfo?.product?.faq_key ?? "").trim()
            };
            const hasPersistedUserSelection = userInfoProduct.manual_key !== "" || userInfoProduct.faq_key !== "";
            const isPersistedAllSelection = userInfoProduct.manual_key === "0" || userInfoProduct.faq_key === "0";

            if (hasPersistedUserSelection && !isPersistedAllSelection) {
                setDropDownFilterText(
                    userInfoProduct,
                    userInfo?.productVersion ?? { manual_version_key: "", faq_version_key: "" },
                    userInfo?.subProduct ?? { manual_sub_key: "", faq_sub_key: "" }
                );
            } else if (saved) {
                markTemplateApplySourceForNextSwitch("programmatic", saved.productText);
                setProductIndexes(saved.productIndexes);
                setProductText(saved.productText);
                setSubProductIndexes(saved.subProductIndexes);
                setSubProductText(saved.subProductText);
                setProductVersionIndexes(saved.productVersionIndexes);
                setProductVersionText(saved.productVersionText);
            } else {
                markTemplateApplySourceForNextSwitch("programmatic", `${i18next.t("chat.productFilterText")}`);
                setProductIndexes({ manual_key: "", faq_key: "" });
                setProductText(`${i18next.t("chat.productFilterText")}`);
                setSubProductIndexes({ manual_sub_key: "", faq_sub_key: "" });
                setSubProductText("...");
                setProductVersionIndexes({ manual_version_key: "", faq_version_key: "" });
                setProductVersionText(`${i18next.t("chat.sub_version")}`);
            }
            savedProductSelectionForUserRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableAllProductForUser]);

    useEffect(() => {
        return () => {
            allProductPrecheckControllerRef.current?.abort();
            allProductPrecheckControllerRef.current = null;
        };
    }, []);

    const abortAllProductCitationPrecheck = useCallback(() => {
        allProductPrecheckControllerRef.current?.abort();
        allProductPrecheckControllerRef.current = null;
    }, []);

    // Redux selectors - using memoized versions for performance
    const templateAppliedFlagValue = useSelector(selectTemplateAppliedFlag);
    const currentSessionIdValue = useSelector(selectCurrentSessionId);
    const requestedToForceStopAIResponseGeneration = useSelector(selectRequestedToForceStop);
    const isSessionSearchActive = useSelector(selectIsSessionSearchActive);

    // All-product suggestions panel
    const suggestionsStage = useSelector(selectSuggestionsStage);
    const showSuggestionsPanel = suggestionsStage !== "idle";
    const allowContinueWithAll = isAdmin ? enableAllProductForAllUsers && enableAllProductForUser : enableAllProductForAllUsers;
    const [productScenarioRegenerateContext, setProductScenarioRegenerateContext] = useState<ProductScenarioRegenerateContext | null>(null);
    const answerProductScenarioRequestIdRef = useRef(0);
    const isAnalysisPanelVisible = activeAnalysisPanelTab !== undefined && answers.length > 0;

    const resetSuggestionsForSessionTransition = useCallback(
        (nextSessionId?: string | null, abortInFlight = true) => {
            if (abortInFlight) {
                abortAllProductCitationPrecheck();
            }
            answerProductScenarioRequestIdRef.current += 1;
            setProductScenarioRegenerateContext(null);
            dispatch(AllProductSuggestionsAction.resetSuggestions());
            activeSuggestionsSessionIdRef.current = normalizeSessionIdentity(nextSessionId);
        },
        [abortAllProductCitationPrecheck, dispatch]
    );

    useEffect(() => {
        if (!showSuggestionsPanel) {
            return;
        }
        setActiveAnalysisPanelTab(undefined);
        setActiveCitation(undefined);
        setSelectedCitation(0);
    }, [showSuggestionsPanel, setActiveAnalysisPanelTab, setActiveCitation, setSelectedCitation]);

    // FileAttachments hook
    const { clearAttachments } = FileAttachments();
    const fileUploadOrDeleteAPITriggerActive = useSelector((store: RootState) => store.questionInput.fileUploadOrDeleteAPITriggerActive);
    const serverSideFileAttachmentsIds = useSelector((store: RootState) => store.questionInput.serverSideFileAttachmentsIds);

    // Session buckets helper
    const sessionBuckets: any = {
        1: { state: backupTodaySessions, setState: setBackupTodaySessions },
        2: { state: backupLast7Sessions, setState: setBackupLast7Sessions },
        3: { state: backupLast30Sessions, setState: setBackupLast30Sessions }
    };

    const onEditClick = (checked: boolean, index: number) => {
        setIsOnEdit(checked);
        setSelectedQuestion(index);
        currentTargetQuestionId.current = index;
        // setSelectedAnswer(index !== 0 ? index - 1 : index);
    };
    interface State {
        run: boolean;
        steps: Step[];
    }

    useEffect(() => {
        if (props.startTourByIcon) {
            setTourOne(prevState => ({
                ...prevState,
                run: true
            }));
        }
    }, [props.startTourByIcon]);

    useEffect(() => {
        const handleLocationChange = async () => {
            const queryParams = parseQueryParams(location.search);
            setIsNewChatThreadInitialLoading(Object.keys(queryParams).length === 0);
            if (Object.keys(queryParams).length === 0) {
                clearChat();
            } else if (queryParams && Object.keys(queryParams).includes("SessionId") && currentSessionIdValue !== queryParams?.SessionId) {
                clearChat();
                setCurrentIndex(queryParams?.SessionId);
                try {
                    await loadChatData(queryParams?.SessionId, false, false);
                } catch (error) {
                    console.error("Error loading chat data:", error);
                }
            }
        };
        handleLocationChange();
    }, [location.search]);

    useEffect(() => {
        const queryParams = parseQueryParams(location.search);
        let appTypeValue: AppType = selectedAppType;
        if (Object.keys(queryParams).length === 0) {
            // new empty chat
            appTypeValue = getAppTypeBasedOnSelectedValueOnProductFilter();
            if ((userInfo.apptype as AppType) !== appTypeValue) {
                userInfo.apptype = appTypeValue;
                setSelectedAppType(appTypeValue);
            }
        } else {
            // chat modes -> history or share chat or chat(non empty new chat)
            if (
                queryParams &&
                Object.keys(queryParams).length > 2 &&
                Object.values(queryParams).includes("chat_share") &&
                queryParams.AppType !== String(selectedAppType)
            ) {
                // share chat load capture the Apptype value from the url.
                if (userInfo.apptype !== queryParams.AppType) {
                    let appTypeValue = appInfo.use_all_tab === true ? queryParams.AppType : queryParams.AppType !== "ALL" ? queryParams.AppType : "FAQ";
                    appTypeValue = getValidAppType(appTypeValue ?? "");
                    userInfo.apptype = appTypeValue;
                    setSelectedAppType(appTypeValue as AppType);
                }
            } else {
                // history page view load.
                if (!sessionStorage.getItem("isTabClicked")) {
                    if (queryParams.AppType !== String(selectedAppType)) {
                        if (userInfo.apptype !== queryParams.AppType) {
                            const targetAppTypeValue =
                                store.getState().questionInput.requestTriggerType === "QuestionInput"
                                    ? (userInfo.isWap === "False" || userInfo.isWap === "false") && appInfo?.use_all_tab === true
                                        ? "ALL"
                                        : userInfo.apptype
                                    : store.getState().questionInput.requestTriggerType === "QuestionEdit" && currentTargetQuestionId.current === 0
                                      ? "ALL"
                                      : queryParams.AppType;
                            const appTypeValue = getValidAppType(targetAppTypeValue);
                            userInfo.apptype = appTypeValue;
                            setSelectedAppType(appTypeValue as AppType);
                        }
                    }
                }
            }
        }
    }, [location.search, selectedAppType]);

    useEffect(() => {
        if (suppressAppTypeUrlSyncRef.current) {
            suppressAppTypeUrlSyncRef.current = false;
            return;
        }
        const queryParams = parseQueryParams(location.search);
        if (!props.executeClear && Object.keys(queryParams).length > 0 && queryParams.AppType !== String(selectedAppType)) {
            // chat modes -> history or share chat or chat(non empty new chat)
            const urlParams = new URLSearchParams();
            urlParams.append("SessionId", queryParams.SessionId);
            if (sessionStorage.getItem("isTabClicked")) {
                urlParams.append("AppType", selectedAppType);
                userInfo.apptype = selectedAppType;
                sessionStorage.removeItem("isTabClicked");
            } else {
                const targetAppTypeValue =
                    store.getState().questionInput.requestTriggerType === "QuestionInput"
                        ? (userInfo.isWap === "False" || userInfo.isWap === "false") && appInfo?.use_all_tab === true
                            ? "ALL"
                            : userInfo.apptype
                        : store.getState().questionInput.requestTriggerType === "QuestionEdit" && currentTargetQuestionId.current === 0
                          ? "ALL"
                          : queryParams.AppType;
                const appTypeValue = getValidAppType(targetAppTypeValue);
                urlParams.append("AppType", appTypeValue);
                userInfo.apptype = appTypeValue;
            }
            navigate(`?${urlParams.toString()}`);
        }
    }, [selectedAppType]);

    const [tourOne, setTourOne] = useSetState<State>({
        run: userInfo.chatTourDone === "false" ? true : false,
        steps: [
            {
                content: i18next.t(`appTour.chatTour.appType`),
                target: "#AppToggle",
                disableBeacon: true,
                floaterProps: {
                    disableAnimation: true,
                    autoOpen: true
                }
            },
            {
                content: i18next.t(`appTour.chatTour.productFilter`),
                target: "#ToolBarProductSettingOptions"
            },
            {
                content: i18next.t(`appTour.chatTour.guidelines`),
                target: "#UserGuidlines"
            },
            {
                content: i18next.t(`appTour.chatTour.newChat`),
                target: "#ClearChat",
                locale: {
                    last: i18next.t(`appTour.next`)
                }
            }
        ]
    });

    const endTourOne = useCallback(() => {
        setTourOne(prevState => ({
            ...prevState,
            run: false
        }));
        if (userInfo.chatTourDone === "false") {
            saveUserSettings({
                chatTourDone: "true",
                templateTourDone: "false",
                fileAttachmentTourDone: userInfo.fileAttachmentTourDone,
                answerTourDone: "false",
                answerTabTourDone: "false"
            });
        }

        userInfo.chatTourDone = "true";
        setActivateTemplateTour(true);
        props.startTour(false);
    }, []);

    const Spinner = () => {
        return (
            <div className="full-page-spinner">
                <div className="spinner"></div>
            </div>
        );
    };

    const retrievalOptions = useMemo(
        () =>
            Object.keys(retrievalModes).map(key => ({
                key: key.toLowerCase(),
                text: retrievalModes[key],
                selected: retrievalMode === key,
                data: key
            })),
        [retrievalModes, retrievalMode]
    );
    const cosmosDBRetrievalOptions = useMemo(
        () =>
            Object.keys(retrievalModes).map(key => ({
                key: key.toLowerCase(),
                text: retrievalModes[key],
                selected: cosmosDBRetrievalMode === key,
                data: key
            })),
        [retrievalModes, cosmosDBRetrievalMode]
    );
    const aiModel_admin = useMemo(
        () =>
            Object.keys(aimodelsAdmin).map(key => ({
                key: key.toLowerCase(),
                text: aimodelsAdmin[key],
                selected: chatModel === key,
                data: key
            })),
        [aimodelsAdmin, chatModel]
    );
    const aimodel_user = useMemo(
        () =>
            Object.keys(aimodelsUser).map(key => ({
                key: key.toLowerCase(),
                text: aimodelsUser[key],
                selected: chatModel === key,
                data: key
            })),
        [aimodelsUser, chatModel]
    );
    const retrievalReasoningOptions = useMemo(
        () =>
            Object.keys(appInfo.retrieval_reasoning_effort ?? {}).map(key => ({
                key: key.toLowerCase(),
                text: i18next.t(`reasoningEffort.${key}`),
                selected: retrievalReasoningEffort === key,
                data: key
            })),
        [appInfo.retrieval_reasoning_effort, retrievalReasoningEffort]
    );
    const modelsToValidate = isAdmin ? aimodelsAdmin : aimodelsUser;
    const popupStyles = mergeStyleSets({
        root: {
            background: "rgba(0, 0, 0, 0.2)",
            bottom: "0",
            left: "0",
            position: "fixed",
            right: "0",
            top: "0"
        },
        content: {
            background: "white",
            left: "50%",
            maxWidth: "1000px",
            padding: "0 0em 2em",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)"
        }
    });

    const handleAsyncRequest = async (
        question: string,
        responseBody: ReadableStream<any>,
        appType: AppType,
        templateId: string | undefined,
        extractedQuestion: string,
        indexToReplace: number,
        isRegenerated: boolean
    ) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {
            output_text: "",
            context: {} as any,
            session_state: null
        } as ChatAppResponse;
        let isStreamingAborted = false;

        const updateState = async (newContent: string) => {
            answer += newContent;
            askResponse = { ...askResponse, output_text: answer };
            const latestResponse: ChatAppResponse = { ...askResponse };

            const updateAnswers = () => {
                setAnswers(prevAnswers => {
                    const newAnswers = [...prevAnswers];
                    newAnswers[indexToReplace][1][appType] = latestResponse;
                    return newAnswers;
                });
            };

            if (isTabClick.current === "true" || isRegenerated) {
                updateAnswers();
            } else {
                setAnswers([
                    ...answers,
                    [
                        {
                            question,
                            templateId: templateId ?? "None",
                            extractedQuestion,
                            isFilesAttached: lastQuestionRef.current.isFilesAttached,
                            filesAttached: lastQuestionRef.current.filesAttached,
                            combinedAppTypeResponse: lastQuestionRef.current.combinedAppTypeResponse
                        },
                        {
                            [appType]: latestResponse
                        }
                    ]
                ]);
            }
        };

        dispatch(QuestionInputReducerAction.updateAbortStageLevel("ResponseStreaming"));
        setIsStreaming(true);
        for await (const event of readNDJSONStream(responseBody)) {
            if (store.getState().questionInput.requestedToForceStopAIResponseGeneration) {
                isStreamingAborted = true;
                break;
            }
            if (event?.type === "response.context" && event?.context && typeof event.context === "object") {
                askResponse = {
                    ...askResponse,
                    context: {
                        ...(askResponse.context ?? {}),
                        ...event.context
                    },
                    session_state: event?.session_state !== undefined ? event.session_state : askResponse.session_state
                };
            } else if (event?.type === "response.output_text.delta" && typeof event?.delta === "string") {
                setIsLoading(false);
                if (isRegenerated) {
                    isRegenerateActive.current = false;
                }
                await updateState(event.delta);
            } else if (event?.error) {
                throw Error(event.error);
            }
        }
        if (isStreamingAborted) {
            return askResponse;
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            output_text: answer
        };
        return fullResponse;
    };

    const normalizeParsedResponse = (parsedResponse: ChatAppResponse | ChatAppResponseOrError): ChatAppResponse => {
        const normalizedResponse = parsedResponse as ChatAppResponse;
        const responseObject = normalizedResponse as any;
        responseObject.output_text = typeof responseObject.output_text === "string" ? responseObject.output_text : "";
        responseObject.context = responseObject.context ?? {};
        responseObject.session_state = responseObject.session_state ?? null;

        return responseObject as ChatAppResponse;
    };

    // helper: build the question meta block (keeps logic identical)
    const buildQuestionMeta = (
        question: string,
        templateId: string | undefined,
        extractedQuestion: string,
        isFilesAttached: boolean,
        filesAttached: any,
        question_id: string,
        versionHistory: any,
        combinedAppTypeResponseValue: boolean
    ) => ({
        question,
        templateId: templateId ?? "None",
        extractedQuestion,
        isFilesAttached,
        filesAttached,
        combinedAppTypeResponse: combinedAppTypeResponseValue,
        question_id,
        ...(versionHistory ?? {})
    });

    const findAttachmentOwnerIndex = (
        filename: string,
        preferredIndex?: number,
        fallbackNames?: Set<string>
    ): { index: number; matchedName: string } | null => {
        const target = normalizeAttachmentName(filename);
        if (!target) return null;

        const findByName = (name: string, startIndex?: number) => {
            const matchesAt = (i: number) => {
                const details = answers[i]?.[0];
                const files = details?.filesAttached ?? [];
                if (!Array.isArray(files)) return null;
                for (const att of files) {
                    const attName = normalizeAttachmentName(att.file?.name);
                    if (attName === name || isPartialAttachmentNameMatch(attName, name)) {
                        return attName;
                    }
                }
                return null;
            };

            if (typeof startIndex === "number" && startIndex >= 0 && startIndex < answers.length) {
                const matchAtStart = matchesAt(startIndex);
                if (matchAtStart) return { index: startIndex, matchedName: matchAtStart };
                for (let i = startIndex - 1; i >= 0; i--) {
                    const matchAt = matchesAt(i);
                    if (matchAt) return { index: i, matchedName: matchAt };
                }
                for (let i = startIndex + 1; i < answers.length; i++) {
                    const matchAt = matchesAt(i);
                    if (matchAt) return { index: i, matchedName: matchAt };
                }
                return null;
            }

            for (let i = answers.length - 1; i >= 0; i--) {
                const matchAt = matchesAt(i);
                if (matchAt) return { index: i, matchedName: matchAt };
            }
            return null;
        };

        let match = findByName(target, preferredIndex);
        if (match) return match;

        if (fallbackNames && fallbackNames.size > 0) {
            for (const name of fallbackNames) {
                if (!name || name === target) continue;
                match = findByName(name, preferredIndex);
                if (match) return match;
            }
        }

        return null;
    };

    const makeApiRequest = async (
        question: string,
        extractedQuestion: string,
        appType: AppType,
        combinedAppTypeResponse: boolean,
        fileAttachments?: Array<Attachment>,
        regenerated?: boolean,
        docType?: string,
        selectedChatIndex?: number,
        isEdit?: boolean,
        templateId?: string,
        answer?: any,
        forcedProductSelection?: ForcedProductSelection,
        selectionMeta?: AllProductSelectionMeta,
        sourceMode: RequestSourceMode = currentView === "history" ? "history" : "chat"
    ) => {
        const templateDetailsForEdit =
            isEdit && isValidTemplateId(templateId)
                ? getTemplateDetailsForQuestionEdit(templateId!)
                : { templateContent: undefined, templateKeyWords: undefined };
        const resolvedTemplatePayload = resolveTemplatePayload(
            question,
            extractedQuestion,
            templateContents,
            templateId,
            isEdit ? templateDetailsForEdit.templateContent : selectedTemplateContent,
            isEdit ? templateDetailsForEdit.templateKeyWords : templateKeyWords,
            isValidTemplateId
        );
        const templateKeysValue = resolvedTemplatePayload.templateKeys;
        const templateContentsValue = resolvedTemplatePayload.templateContents;
        const promptInstructionsValue = resolvedTemplatePayload.promptInstructions;
        const questionContentForAI = resolvedTemplatePayload.questionContentForAI;
        const hasTemplateForSummary = !!(resolvedTemplatePayload.templateId && templateContentsValue);
        templateId = resolvedTemplatePayload.templateId;
        question = resolvedTemplatePayload.questionWithInstructions;
        const appName = getAppName(appType);
        const appTypeBasedOnUser = getAppTypeBasedOnUser(appType);
        let question_id: string | undefined;
        let has_question_id: boolean | undefined;

        const appTypesToCheck = {
            ALL: ["FAQ", "Manuals"],
            FAQ: ["ALL", "Manuals"],
            Manuals: ["ALL", "FAQ"]
        };
        const typesToCheck = appTypesToCheck[appType] || [];
        for (const typeToCheck of typesToCheck) {
            const questionId = answer?.[typeToCheck]?.context?.question_id;
            const hasQuestionId = answer?.[typeToCheck]?.context?.has_question_id;

            if (questionId && hasQuestionId) {
                question_id = questionId;
                has_question_id = hasQuestionId;
                break;
            }
        }
        const original_question_id = question_id ? question_id : answer?.[appName]?.context?.question_id;
        if (question_id === undefined || has_question_id === undefined || isEdit) {
            question_id = generateNewSessionId();
            has_question_id = true;
        }
        const editedQuestionId = isEdit ? (original_question_id ?? null) : null;
        const old_conv_id = answer?.[appName]?.context?.conv_id;
        if (emptyProduct) {
            return;
        }
        let shouldClearVersionSnapshot = true;
        // Snapshot version indices BEFORE aborting (for abort restoration)
        // This preserves user's selected versions for all abort scenarios:
        // new question, tab switch, retry/regenerate, edit, follow-up
        const currentSessionId = sessionStorage.getItem("sessionId");
        const requestTriggerType = store.getState().questionInput.requestTriggerType;
        lastStableAppTypeBeforeTabSwitchRef.current = getValidAppType(selectedAppType);
        const selectedAnswerToSnapshot =
            requestTriggerType === "TabSwitch" && Number.isFinite(currentTargetQuestionId.current)
                ? Math.max(currentTargetQuestionId.current, 0)
                : typeof selectedChatIndex === "number" && Number.isFinite(selectedChatIndex)
                  ? Math.max(selectedChatIndex, 0)
                  : Number.isFinite(selectedAnswer)
                    ? Math.max(selectedAnswer, 0)
                    : 0;
        snapshotVersionIndices(currentSessionId, versionIndexMap, answerIndexMap, selectedAnswerToSnapshot);

        if (makeApiRequestControllerRef.current) {
            makeApiRequestControllerRef.current.abort();
        }
        updateDisclaimerStatus();
        const urlParams = new URLSearchParams();
        if (isChatShared) {
            setCurrentView("chat");
            const newSessionId = generateNewSessionId();
            sessionStorage.setItem("sessionId", newSessionId);
            setSelectedSessionId(newSessionId);
        }
        if (regenerated) {
            setCurrentView("chat");
        }

        // Ensure /chat always gets a concrete session id from frontend.
        const requestSessionId = (() => {
            const existingSessionId = sessionStorage.getItem("sessionId") || selectedSessionId;
            if (existingSessionId && existingSessionId !== "") {
                return existingSessionId;
            }
            const newSessionId = generateNewSessionId();
            sessionStorage.setItem("sessionId", newSessionId);
            setSelectedSessionId(newSessionId);
            return newSessionId;
        })();
        const requestSessionIdAtStart = requestSessionId;
        // Any outgoing answer generation (question send, tab-triggered send, edit, regenerate)
        // must own the right panel area; clear stale suggestions state first.
        resetSuggestionsForSessionTransition(requestSessionId, true);
        lastQuestionRef.current = {
            question: question,
            templateId: templateId ?? "None",
            extractedQuestion: extractedQuestion,
            isFilesAttached: !!fileAttachments && fileAttachments.length > 0,
            filesAttached: fileAttachments ? getFileAttachmentsWithPreviewAsBase64String(fileAttachments) : [],
            combinedAppTypeResponse: shouldUseCombinedAppTypeResponse(Boolean(combinedAppTypeResponse), answer, Boolean(isEdit))
        };
        lastAnswerRef.current = "";
        lastFileMappingRef.current = null;
        lastDataPointsRef.current = [];
        lastThoughtsRef.current = "";
        lastFollowUpRef.current = [];
        lastKeyWordRef.current = "";
        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setSelectedCitation(0);
        setActiveAnalysisPanelTab(undefined);
        setIsOnSave(true);
        setScrollToAnswer(true);
        setSharedAnswerLoadingState(true);
        setSelectedAnswer(answers?.length > 0 ? answers?.length - 1 : answers?.length);
        reduxActions.initializeAnswerGeneration({
            isEditedQuestion: isEdit ? isEdit : false,
            editedQuestionId: isEdit ? (editedQuestionId ?? null) : null,
            isChatShared,
            selectedAppType: appType,
            questionSendToAI: question,
            questionContentToReRenderInQuestionInput: extractedQuestion,
            sessionId: requestSessionId ?? null,
            templateId: templateId ?? null,
            promptInstructions: promptInstructionsValue || null,
            fileAttachments: fileAttachments ? fileAttachments : []
        });
        if (newChatQuestion === "" || regenerated || isChatShared) {
            setnewChatQuestion(question);
            if (!regenerated && currentView === "history" && !isChatShared) {
                let indexToRemove = -1;
                if (lastActiveIndexRef.current === 1) {
                    const indexes = [];
                    for (let i = 0; i < lastActiveSessionRef.current.length; i++) {
                        if (lastActiveSessionRef?.current[i]?.session_id === currentIndex) {
                            indexes.push(i);
                        }
                    }
                    indexToRemove = indexes.length > 0 ? indexes[indexes.length - 1] : -1;
                } else {
                    indexToRemove = lastActiveSessionRef.current.findIndex(obj => obj.session_id === currentIndex);
                }
                if (indexToRemove !== -1) {
                    lastActiveSessionRef.current.splice(indexToRemove, 1);
                    if (isSessionSearchActive) {
                        updateSessionStorage("remove", lastSessionRef.current);
                    }
                }
                if (Object.keys(lastSessionRef.current).length !== 0) {
                    const indexTarget: number = todaySessionsRef.findIndex((session: SessionInfo) => session.session_id === lastSessionRef.current.session_id);
                    if (indexTarget === -1 || indexTarget !== 0) {
                        todaySessionsRef.unshift(lastSessionRef.current);
                        if (isSessionSearchActive) {
                            updateSessionStorage("shift", lastSessionRef.current);
                        }
                    }
                }
            } else {
                const sessionIDValue = sessionStorage.getItem("sessionId");
                if (sessionIDValue && sessionIDValue !== "") {
                    const indexTarget: number = todaySessionsRef.findIndex((session: SessionInfo) => session.session_id === sessionIDValue);
                    if (indexTarget === -1 || indexTarget !== 0) {
                        const session = {
                            session_id: sessionStorage.getItem("sessionId") ?? "",
                            title: question,
                            chat_summary_title: hasTemplateForSummary
                                ? getChatSummaryTitle(extractedQuestion, templateId as string, templateContents)
                                : extractedQuestion,
                            conversation_date: new Date().toString()
                        };
                        todaySessionsRef.unshift(session);
                        if (isSessionSearchActive) {
                            updateSessionStorage("shift", session);
                        }
                    }
                }
                setCurrentIndex(sessionStorage.getItem("sessionId") ?? "");
            }
        }
        if (IsOnEdit && selectedChatIndex === 0) {
            setShowSupportLink(false);
            todaySessionsRef[0] = {
                session_id: sessionStorage.getItem("sessionId") ?? "",
                title: question,
                chat_summary_title: hasTemplateForSummary ? getChatSummaryTitle(extractedQuestion, templateId as string, templateContents) : extractedQuestion,
                conversation_date: new Date().toString()
            };
        }
        const versionHistory: any = {};
        const requestSelectedLocale = sessionStorage.getItem("selectedLocale") ?? "ja";
        const requestProductLocale = resolveProductLocale(requestSelectedLocale, appInfo);
        let idToCheck = getIdToCheck(currentQuestionInfo.current);
        if (currentQuestionInfo.current) {
            const totalVersions = calculateVersions(histories, idToCheck);
            versionHistory["parentMessageId"] = idToCheck;
            versionHistory["rootId"] = idToCheck;
            versionHistory["edited_version"] = IsOnEdit ? totalVersions : 1;
            versionHistory["question_order"] = currentQuestionInfo.current?.question_order ?? 0;
            if (versionHistory["edited_version"] && question_id) {
                setVersionIndexMap(prev => ({
                    ...prev,
                    [idToCheck]: versionHistory["edited_version"]
                }));
            }
        } else {
            const lastAnswer = answers && answers.length > 0 ? answers[answers.length - 1] : null;
            idToCheck = lastAnswer ? (lastAnswer[0]?.rootId ?? "") : "";
            versionHistory["rootId"] = idToCheck;
            versionHistory["edited_version"] = lastAnswer ? (lastAnswer[0]?.edited_version ?? 0) : 0;
            versionHistory["question_order"] = histories?.[idToCheck]?.[versionHistory["edited_version"]]?.length ?? 0;
        }
        let responseStatus: number | undefined;
        try {
            const shouldSkipQuestionValidation =
                skipValidationOnceRef.current ||
                Boolean(regenerated) ||
                Boolean(isEdit) ||
                isTabClick.current === "true" ||
                (fileAttachments?.length ?? 0) > 0 ||
                store.getState().questionInput.serverSideFileAttachmentsIds.length > 0 ||
                answers.length > 0;
            skipValidationOnceRef.current = false;
            setClarificationInfo(null);
            const messages: ResponseMessage[] = [];
            let previousQuestionsFileAttachments: { serverSideId: string; fileName: string }[] = [];
            let previousQuestionIds: string[] = [];
            let questionIndex = 0;
            const effectiveProductIndexes = forcedProductSelection?.product_filter ?? productIndexes;
            for (const ans of answers) {
                if (isTabClick.current === "true" && questionIndex >= lastAnswerIndexRef.current) {
                    continue;
                }
                const userMessage = { content: ans[0].question, role: "user" };
                const assistantContent = ans[1]?.[appType]?.output_text ?? "";
                const currentIndexQuestionId = ans[1]?.[appType]?.context?.question_id ?? "";
                previousQuestionIds.push(currentIndexQuestionId);

                const appKeys = Object.keys(ans[1] || {});
                const validKey = appKeys[0] as AppType;
                const productFilter = ans[1]?.[validKey]?.context?.product_filter;
                const product = appInfo.product_categories[requestProductLocale]?.find(
                    (item: any) =>
                        (item.manual_key && item.manual_key === productFilter?.manual_key) || (item.faq_key && item.faq_key === productFilter?.faq_key)
                );

                if (
                    (appType === "ALL" &&
                        (product?.manual_key === effectiveProductIndexes.manual_key || product?.faq_key === effectiveProductIndexes.faq_key)) ||
                    (appType === "Manuals" && product?.manual_key === effectiveProductIndexes.manual_key) ||
                    (appType === "FAQ" && product?.faq_key === effectiveProductIndexes.faq_key)
                ) {
                    messages.push(userMessage, { content: assistantContent, role: "assistant" });
                }
                questionIndex = questionIndex + 1;
            }
            // Fetch the real serverSideIds from Cosmos for all previous questions in both
            // "chat" and "history" views.  This is the only reliable source — the transient
            // Redux serverSideFileAttachmentsIds list is reset to [] after every request so
            // it cannot be used for attachments from earlier turns.
            previousQuestionIds = previousQuestionIds.filter(id => id && id !== "");
            if (answers && answers.length > 0 && previousQuestionIds.length > 0) {
                await getFileAttachmentServerSideIds(previousQuestionIds)
                    .then(response => {
                        return response.json();
                    })
                    .then(result => {
                        previousQuestionsFileAttachments = result.file_ids ? result.file_ids : [];
                    })
                    .catch(error => {
                        console.error("Error fetching data:", error);
                        throw error instanceof Error ? error : new Error(String(error));
                    });
            }
            const appNameValue = answer?.[appName] ? appName : appType;
            const product_filter = forcedProductSelection?.product_filter
                ? forcedProductSelection.product_filter
                : setProductFilterForRequestPayload(answer, appNameValue, currentView, productIndexes, requestProductLocale);
            const sub_product_filter = forcedProductSelection?.sub_product_filter
                ? forcedProductSelection.sub_product_filter
                : setSubProductFilterForRequestPayload(answer, appNameValue, currentView, subProductIndexes, requestProductLocale);
            const product_version = forcedProductSelection?.product_version
                ? forcedProductSelection.product_version
                : setProductVersionForRequestPayload(answer, appNameValue, currentView, productVersionIndexes, requestProductLocale);
            const filtered_groupids: { appType: AppType; groupIds: Set<string>; sub_group_ids: Set<string> | undefined } | undefined =
                setGroupIdsByAppTypeForRequestPayload(appType, product_filter, sub_product_filter, requestProductLocale);
            let serverSideFileAttachmentsIds: { serverSideId: string; fileName: string }[] = [];
            const groupIds =
                filtered_groupids && filtered_groupids.groupIds && filtered_groupids.groupIds.size > 0
                    ? [...filtered_groupids?.groupIds].filter(id => id !== "").join(",")
                    : "0";
            const subgroupIds = filtered_groupids
                ? filtered_groupids?.sub_group_ids && filtered_groupids?.sub_group_ids.size > 0
                    ? [...filtered_groupids?.sub_group_ids].filter(id => id !== "").join(",")
                    : "0"
                : "0";
            if ((isEdit || isTabClick.current === "true") && original_question_id) {
                await getFileAttachmentServerSideIds([original_question_id])
                    .then(response => {
                        return response.json();
                    })
                    .then(result => {
                        serverSideFileAttachmentsIds = result.file_ids ? result.file_ids : [];
                    })
                    .catch(error => {
                        console.error("Error fetching data:", error);
                        throw Error(error);
                    });
            }
            const lastResponseForAppType = answers.length ? answers[answers.length - 1][1][appType] : null;
            const request: ChatAppRequest = {
                messages: [
                    ...messages,
                    {
                        content: questionContentForAI,
                        role: "user"
                    }
                ],
                stream: shouldStream,
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        language: selectedLanguage,
                        top: retrieveCount,
                        chat_model: chatModel,
                        reasoning_effort: isReasoningModel(chatModel) ? reasoningEffort : undefined,
                        retrieval_reasoning_effort: retrievalReasoningEffort,
                        only_citations: onlyCitations,
                        retrieval_mode: retrievalMode,
                        cosmosdb_retrieval_mode: cosmosDBRetrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        enable_OCR: appInfo?.enable_OCR ?? false,
                        doctype_filter: !isWapUser ? "External" : docType ? docType : docTypeFilter,
                        // Note : answer will be undefined for new Question or edit question & show follow up calls of this method.
                        // so using answer as work around.
                        product_filter: product_filter,
                        sub_product_filter: sub_product_filter,
                        product_version: product_version,
                        group_ids: groupIds,
                        sub_group_ids: subgroupIds,
                        template_keys: templateKeysValue,
                        templateContents: templateContentsValue,
                        templateId: templateId,
                        extractedQuestion: extractedQuestion,
                        promptInstructions: promptInstructionsValue,
                        // Re-use keywords from the precheck to skip a redundant query-rewrite LLM call
                        keywords: precheckResultCacheRef.current?.keywords || undefined,
                        agentic_retrieval_output_size: normalizeAgenticRetrievalOutputSize(agenticRetrievalOutputSize),
                        agentic_retrieval_document_count: normalizeAgenticRetrievalDocumentCount(agenticRetrievalDocumentCount),
                        agentic_retrieval_source_document_count: normalizeAgenticRetrievalSourceDocumentCount(agenticRetrievalSourceDocumentCount),
                        chat_summary_title: hasTemplateForSummary
                            ? getChatSummaryTitle(extractedQuestion, templateId as string, templateContents)
                            : extractedQuestion,
                        fromHistory: !regenerated && currentView === "history" && !isChatShared,
                        sessionId: requestSessionId,
                        versions: versionHistory,
                        question_id,
                        has_question_id,
                        old_chat_id: old_conv_id,
                        edited_question_id: editedQuestionId,
                        is_chat_shared: isChatShared,
                        source_shared_session_id: isChatShared ? sessionId : null,
                        all_product_selection_meta: selectionMeta ?? null,
                        combinedAppTypeResponse,
                        isAppTypeIntegrated: appTypeBasedOnUser === "ALL",
                        isRegenerated: Boolean(regenerated),
                        file_attachment_ids: (() => {
                            const isEditOrTab = isEdit || isTabClick.current === "true";
                            const currentAttachments = isEditOrTab
                                ? serverSideFileAttachmentsIds.map(value => ({
                                      serverSideId: value.serverSideId,
                                      fileName: value.fileName
                                  }))
                                : store.getState().questionInput.serverSideFileAttachmentsIds.map(value => ({
                                      serverSideId: value.serverSideId,
                                      fileName: value.original_filename
                                  }));
                            // Always include prior questions' attachments for RAG/Agentic context so
                            // follow-up questions can still reference files uploaded in earlier turns.
                            return deduplicateFileAttachments(previousQuestionsFileAttachments.concat(currentAttachments));
                        })(),
                        // Only the current question's attachments — used by the backend to save
                        // file_attachment_details for this question only, never merged with prior turns.
                        current_question_file_attachment_ids: (() => {
                            const isEditOrTab = isEdit || isTabClick.current === "true";
                            if (isEditOrTab) {
                                return deduplicateFileAttachments(
                                    serverSideFileAttachmentsIds.map(value => ({
                                        serverSideId: value.serverSideId,
                                        fileName: value.fileName
                                    }))
                                );
                            }
                            return deduplicateFileAttachments(
                                store.getState().questionInput.serverSideFileAttachmentsIds.map(value => ({
                                    serverSideId: value.serverSideId,
                                    fileName: value.original_filename
                                }))
                            );
                        })(),
                        use_agentic_knowledgebase: useAgenticKnowledgebase,
                        skip_validation: shouldSkipQuestionValidation
                    }
                },
                // ChatAppProtocol: Client must pass on any session state received from the server
                session_state: lastResponseForAppType?.session_state ?? null
            };

            const controller = new AbortController();
            makeApiRequestControllerRef.current = controller;
            const response = await chatApi(request, appTypeBasedOnUser, userInfo.corp_id, controller.signal);

            // Capture response status for error handling
            responseStatus = response?.status;

            if ((!response || !response?.body) && store.getState().questionInput.requestedToForceStopAIResponseGeneration) {
                // if request is forced stopped by user.
                await fallBackUIOnAbortChatAPI(isEdit ? isEdit : false, appTypeBasedOnUser as AppType);
                return;
            } else if ((!response || !response?.body) && !store.getState().questionInput.requestedToForceStopAIResponseGeneration) {
                throw Error("No response body");
            }

            let answerToBeAdded: any = [];
            let updatedAnswers: [user: QuestionDetails, response: { FAQ?: ChatAppResponse; Manuals?: ChatAppResponse; ALL?: ChatAppResponse }][] = [];
            let latestSaveContext: any = null;
            // helper: apply parsed response to state/UI (shared by both branches)
            const applyParsedResponse = async (parsedResponse: ChatAppResponse) => {
                const normalizedParsedResponse = normalizeParsedResponse(parsedResponse);
                const responseContext = normalizedParsedResponse.context ?? ({} as any);

                // ── Question-validation early-exit ──────────────────────────────────────────
                // The backend validated the question and found it vague/greeting/off-topic.
                // Don't add anything to the chat; show the clarification dialog instead.
                if (responseContext.question_validation?.is_vague) {
                    const qv = responseContext.question_validation;
                    const capturedMode: RequestSourceMode = sourceMode;
                    pendingQuestionSendRef.current = () => {
                        // Re-call makeApiRequest with the same params; skipValidationOnceRef=true
                        // ensures skip_validation:true is sent, bypassing backend validation.
                        makeApiRequest(
                            question,
                            extractedQuestion,
                            appType as AppType,
                            combinedAppTypeResponse,
                            fileAttachments,
                            regenerated,
                            docType,
                            selectedChatIndex,
                            isEdit,
                            templateId,
                            answer,
                            forcedProductSelection,
                            selectionMeta,
                            sourceMode
                        );
                    };
                    setClarificationInfo({ type: qv.type as "greeting" | "vague" | "off_topic", message: qv.message || "" });
                    setClarificationOriginalQuestion(extractedQuestion);
                    clarificationSendContextRef.current = {
                        mode: capturedMode,
                        appType: appType as AppType,
                        questionContainsTemplate: !!(templateId && templateId !== "" && templateId !== "None"),
                        // For edit requests, store a closure that replays makeApiRequest with
                        // the new text but preserves the edit context (isEdit, selectedChatIndex,
                        // answer). Without this, the modified-text path in handleClarificationProceed
                        // would call handleQuestionInputSend and discard the edit context.
                        replayWithText: isEdit
                            ? (newText: string) =>
                                  makeApiRequest(
                                      newText,
                                      newText,
                                      appType as AppType,
                                      combinedAppTypeResponse,
                                      fileAttachments,
                                      regenerated,
                                      docType,
                                      selectedChatIndex,
                                      isEdit,
                                      templateId,
                                      answer,
                                      forcedProductSelection,
                                      selectionMeta,
                                      sourceMode
                                  )
                            : undefined
                    };
                    // Show validation feedback inline in the question input instead of a dialog
                    setClarificationMode(capturedMode);
                    if (answers.length === 0) {
                        lastQuestionRef.current = {
                            question: "",
                            templateId: "None",
                            extractedQuestion: "",
                            combinedAppTypeResponse: false,
                            isFilesAttached: false,
                            filesAttached: []
                        };
                        setnewChatQuestion("");
                    }
                    setTimeout(() => {
                        questionInputRefs.current[capturedMode]?.setQuestionInputValue(extractedQuestion);
                    }, 0);
                    // Reset UI to pre-send state
                    setIsLoading(false);
                    setIsOnSave(false);
                    setScrollToAnswer(false);
                    setSharedAnswerLoadingState(false);
                    reduxActions.resetAnswerGenerationActiveDetails();
                    return true; // signal: caller must not proceed with post-save/navigate
                }
                // ── End question-validation ────────────────────────────────────────────────

                // set context fields (same as before)
                responseContext.chat_model = chatModel;
                responseContext.reasoning_effort = isReasoningModel(chatModel) ? reasoningEffort : null;
                responseContext.retrieval_reasoning_effort = retrievalReasoningEffort;
                responseContext.document_category = docTypeFilter;
                responseContext.question_id = question_id;
                responseContext.has_question_id = has_question_id;
                responseContext.product_group_ids = groupIds;
                responseContext.sub_product_group_ids = subgroupIds;
                normalizedParsedResponse.context = responseContext;
                if (selectionMeta) {
                    (normalizedParsedResponse as ChatAppResponse & { allProductSelectionMeta?: AllProductSelectionMeta | null }).allProductSelectionMeta =
                        selectionMeta;
                }
                const key = answerKeyFor(appTypeBasedOnUser as AppType);

                if (isTabClick.current === "true") {
                    const idx = lastAnswerIndexRef.current;
                    const targetQuestionId: string = answers[lastAnswerIndexRef.current][0].question_id ?? "";
                    const questionPatch: Partial<any> = {
                        combinedAppTypeResponse: lastQuestionRef.current?.combinedAppTypeResponse
                    };
                    if (answers?.[lastAnswerIndexRef.current]?.[0]?.rootId) {
                        versionHistory["rootId"] = answers[lastAnswerIndexRef.current][0]?.rootId;
                        versionHistory["edited_version"] = answers[lastAnswerIndexRef.current][0]?.edited_version;
                    }
                    if (answers?.[lastAnswerIndexRef.current]?.[0]?.parentMessageId) {
                        versionHistory["parentMessageId"] = answers[lastAnswerIndexRef.current][0]?.parentMessageId;
                    }
                    setAnswers(prev => {
                        const newAnswer = updateAnswersByIndex(prev as any, idx, targetQuestionId, key, normalizedParsedResponse, questionPatch) as any;
                        setOverAllCurrentSessionDataPoints(newAnswer);
                        return newAnswer;
                    });
                    setHistories(
                        prev => updateHistoriesByQuestionId(prev as any, targetQuestionId, key, normalizedParsedResponse, questionPatch).histories as any
                    );
                    setAllAnswersTree(prev => updateTreeAnswerByQuestionId(prev as any, targetQuestionId, key, normalizedParsedResponse, questionPatch));
                } else {
                    // build question meta depending on appType
                    const isFaq = appTypeBasedOnUser === "FAQ";
                    const meta = buildQuestionMeta(
                        question,
                        templateId,
                        extractedQuestion,
                        lastQuestionRef.current.isFilesAttached,
                        lastQuestionRef.current.filesAttached,
                        question_id,
                        versionHistory,
                        isFaq ? false : lastQuestionRef.current.combinedAppTypeResponse
                    );
                    const answerBlock = key === "FAQ" ? { FAQ: normalizedParsedResponse } : { [key]: normalizedParsedResponse };

                    answerToBeAdded = [meta, answerBlock];
                    updatedAnswers = [...answers, answerToBeAdded];
                    setOverAllCurrentSessionDataPoints(updatedAnswers);
                    setAnswers([...updatedAnswers]);
                }

                // refs and flags (unchanged)
                lastAnswerRef.current = normalizedParsedResponse.output_text ?? "";
                lastFileMappingRef.current = responseContext?.file_mapping ?? null;
                lastDataPointsRef.current = responseContext?.data_points ?? [];
                lastThoughtsRef.current = responseContext?.thoughts ?? "";
                lastKeyWordRef.current = responseContext?.keywords ?? "";
                lastFollowUpRef.current = responseContext?.followup_questions ?? [];
                latestSaveContext = responseContext ?? null;
            };

            if (shouldStream) {
                dispatch(QuestionInputReducerAction.updateAbortStageLevel("ResponseReceived"));
                try {
                    const parsedResponse: ChatAppResponse = await handleAsyncRequest(
                        question,
                        response.body,
                        appTypeBasedOnUser as AppType,
                        templateId,
                        extractedQuestion,
                        lastAnswerIndexRef.current,
                        false
                    );
                    if (store.getState().questionInput.requestedToForceStopAIResponseGeneration) {
                        await fallBackUIOnAbortChatAPI(isEdit ? isEdit : false, appTypeBasedOnUser as AppType);
                        return;
                    }
                    if (sessionStorage.getItem("sessionId") !== requestSessionIdAtStart) {
                        return;
                    }
                    dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                    if (await applyParsedResponse(parsedResponse)) return;
                } finally {
                    setIsStreaming(false);
                }
            } else {
                const parsedResponseOrError: ChatAppResponseOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    reduxActions.resetAnswerGenerationActiveDetails();
                    throw Error(parsedResponseOrError.error || "Unknown error");
                }
                const parsedResponse = normalizeParsedResponse(parsedResponseOrError);
                if (sessionStorage.getItem("sessionId") !== requestSessionIdAtStart) {
                    return;
                }
                reduxActions.resetAnswerGenerationActiveDetails();
                if (await applyParsedResponse(parsedResponse)) return;
            }
            if (sessionStorage.getItem("sessionId") !== requestSessionIdAtStart) {
                return;
            }

            if (isTabClick.current !== "true" && answerToBeAdded.length > 0) {
                setHistories((prev: any) => insertIntoHistories(prev, idToCheck, versionHistory["edited_version"], answerToBeAdded));
                if (versionHistory["edited_version"] === 0) {
                    setAllAnswersTree(prev => insertRowIntoTree(prev, null, answerToBeAdded));
                } else {
                    setAllAnswersTree(prev => insertRowIntoTree(prev, idToCheck, answerToBeAdded));
                }
            }
            clearAttachments();
            saveChatHistory(appTypeBasedOnUser as AppType, selectedChatIndex, latestSaveContext);
            if (sessionStorage.getItem("sessionId") !== requestSessionIdAtStart) {
                return;
            }

            const hasSaveError = latestSaveContext?.save_error === true;
            const resolvedSessionId = (latestSaveContext?.groupId as string | undefined) || requestSessionId;
            // reset server side file attachments ids
            dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds([]));
            const sessionIDValue = resolvedSessionId;
            if (!hasSaveError) {
                if (resolvedSessionId && sessionStorage.getItem("sessionId") !== resolvedSessionId) {
                    sessionStorage.setItem("sessionId", resolvedSessionId);
                    setSelectedSessionId(resolvedSessionId);
                }
                if (resolvedSessionId) {
                    setCurrentIndex(resolvedSessionId);
                }

                if (isChatShared && sessionIDValue) {
                    await getSessionInfoDataForChatSummaryTitleRetain(sessionIDValue);
                }
                updateAppTypeValueInSessionStorage(appTypeBasedOnUser as AppType);
                if (!sessionIDValue) {
                    dispatch(ChatComponentReducerAction.resetOverAllCurrentSessionDataPoints());
                }
                urlParams.append("SessionId", sessionIDValue ?? "");
                urlParams.append("AppType", appTypeBasedOnUser as AppType);
                navigate(`?${urlParams.toString()}`);
                if (isChatShared) {
                    setIsChatShared(false);
                }
            }
            setIsOnEdit(false);
            lastStableAppTypeBeforeTabSwitchRef.current = null;
            setTemplateKeywords("");
        } catch (e) {
            const requestedStop = store.getState().questionInput.requestedToForceStopAIResponseGeneration;
            if (isAbortError(e)) {
                setError(undefined);
                if (requestedStop) {
                    await fallBackUIOnAbortChatAPI(Boolean(isEdit), appTypeBasedOnUser as AppType);
                }
            } else {
                const isTokenLimitError =
                    responseStatus === 206 ||
                    (e &&
                        (e as any).message &&
                        ((e as any).message.includes("token_limit_exceeded") ||
                            (e as any).message.includes("Token limit exceeded") ||
                            (e as any).message.includes("query_token_count"))) ||
                    (typeof e === "string" && (e.includes("token_limit_exceeded") || e.includes("Token limit exceeded")));

                if (isTokenLimitError) {
                    let errorMessage = "";
                    try {
                        if ((e as any)?.message) {
                            errorMessage = (e as any).message;
                        } else if (typeof e === "string") {
                            errorMessage = e;
                        } else if (e && (e as any).toString) {
                            errorMessage = (e as any).toString();
                        }
                    } catch {
                        errorMessage = "Token limit exceeded. Please reduce the length of your question or remove some file attachments and try again.";
                    }
                    setError206Message(errorMessage);
                    setError206Question(lastQuestionRef.current.question);
                    setShow206ErrorDialog(true);
                } else {
                    setError(e);
                }
            }
            setIsOnSave(false);
            setIsOnEdit(false);
            console.error("Error in makeApiRequest:", e);
        } finally {
            setIsLoading(false);
            setScrollToAnswer(false);
            setSharedAnswerLoadingState(false);
            reduxActions.resetAnswerGenerationActiveDetails();
            // Do not clear on abort: older aborted requests can race and wipe newer snapshots.
            if (shouldClearVersionSnapshot) {
                clearVersionIndices();
            }
        }
    };

    const getServerSideIdsOfAttachments = async (questionIds: string[]) => {
        try {
            const response = await getFileAttachmentServerSideIds(questionIds);
            const result = await response.json();
            return result.file_ids ? result.file_ids : [];
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const buildRegenerateMessages = (
        answers: any[],
        primaryId: number,
        selectedAppType: AppType,
        productIndexes: any,
        appInfo: any,
        selectedLocale: string
    ) => {
        const messages: ResponseMessage[] = [];
        const previousQuestionIds = new Set<string>();

        for (let i = 0; i < primaryId; i++) {
            const ans = answers[i];
            if (!ans) continue;
            const userQ = ans?.[0]?.question ?? "";
            const userMessage: ResponseMessage = { content: userQ, role: "user" };
            const assistantContent = ans?.[1]?.[selectedAppType]?.output_text ?? "";
            const qid = ans?.[1]?.[selectedAppType]?.context?.question_id;
            if (qid) previousQuestionIds.add(qid);
            const appKeys = Object.keys(ans?.[1] || []);
            const validKey = appKeys[0] as AppType | undefined;
            if (!validKey) continue;
            const productFilter = ans?.[1]?.[validKey]?.context?.product_filter;
            const product = appInfo.product_categories?.[selectedLocale]?.find(
                (item: any) => (item.manual_key && item.manual_key === productFilter?.manual_key) || (item.faq_key && item.faq_key === productFilter?.faq_key)
            );
            const include =
                (selectedAppType === "ALL" && (product?.manual_key === productIndexes.manual_key || product?.faq_key === productIndexes.faq_key)) ||
                (selectedAppType === "Manuals" && product?.manual_key === productIndexes.manual_key) ||
                (selectedAppType === "FAQ" && product?.faq_key === productIndexes.faq_key);
            if (include) {
                messages.push(userMessage, { content: assistantContent, role: "assistant" });
            }
        }
        return { messages, previousQuestionIds: [...previousQuestionIds] };
    };

    const getAnswerResponseForAppType = (answerSet: Record<string, ChatAppResponse> | undefined, appType: AppType): ChatAppResponse | undefined =>
        answerSet?.[appType] ?? answerSet?.ALL ?? answerSet?.FAQ ?? answerSet?.Manuals;

    const updateRegeneratedAnswerInState = (prevAnswers: any[], primaryId: number, selectedAppType: AppType, parsedResponse: ChatAppResponse) => {
        const next = [...prevAnswers];
        const prevBlock = next[primaryId];
        if (!prevBlock) return prevAnswers;
        const newBlock0 = prevBlock[0];
        const newBlock1 = { ...(prevBlock[1] || {}) };
        newBlock1[selectedAppType] = parsedResponse;
        next[primaryId] = [newBlock0, newBlock1];
        return next;
    };

    const regenerateRequest = async (
        reasoningEffortSelected: ReasoningEffort,
        primaryId: number,
        convId: string,
        options?: {
            targetAppType?: AppType;
            forcedProductSelection?: ForcedProductSelection;
            selectionMeta?: AllProductSelectionMeta;
        }
    ) => {
        if (primaryId === null || primaryId === undefined) return;
        let shouldClearVersionSnapshot = true;
        const regenerateAppType = options?.targetAppType ?? selectedAppType;
        const forcedProductSelection = options?.forcedProductSelection;
        const selectionMeta = options?.selectionMeta;
        let appTypeBasedOnUser: string | null = null;
        const previousQuestionDetails: any = JSON.parse(JSON.stringify(answers[primaryId]));
        if (!previousQuestionDetails?.[0]) return;
        let { question, question_id, extractedQuestion, combinedAppTypeResponse, templateId, filesAttached } = previousQuestionDetails[0];
        if (emptyProduct) return;
        lastStableAppTypeBeforeTabSwitchRef.current = getValidAppType(selectedAppType);
        const currentSessionId = sessionStorage.getItem("sessionId");
        snapshotVersionIndices(currentSessionId, versionIndexMap, answerIndexMap, primaryId);
        if (makeApiRequestControllerRef.current) {
            makeApiRequestControllerRef.current.abort();
        }
        const resolvedTemplatePayload = resolveTemplatePayload(
            question,
            extractedQuestion,
            templateContents,
            templateId,
            undefined,
            undefined,
            isValidTemplateId
        );
        const templateKeysValue = resolvedTemplatePayload.templateKeys;
        const templateContentsValue = resolvedTemplatePayload.templateContents;
        const promptInstructions = resolvedTemplatePayload.promptInstructions;
        const userQuestionContent = resolvedTemplatePayload.questionContentForAI;
        const hasTemplateForSummary = !!(resolvedTemplatePayload.templateId && templateContentsValue);
        const hasTemplateContext = resolvedTemplatePayload.hasTemplateContext;
        templateId = resolvedTemplatePayload.templateId;
        question = resolvedTemplatePayload.questionWithInstructions;
        const urlParams = new URLSearchParams();
        if (isChatShared) {
            setCurrentView("chat");
            setnewChatQuestion(question);
            const newSessionId = generateNewSessionId();
            sessionStorage.setItem("sessionId", newSessionId);
            setSelectedSessionId(newSessionId);
        }
        const regenerateRequestSessionId = sessionStorage.getItem("sessionId") ?? "";
        lastQuestionRef.current = {
            question,
            templateId: templateId ?? "None",
            extractedQuestion,
            isFilesAttached: !!filesAttached?.length,
            filesAttached: filesAttached ?? [],
            combinedAppTypeResponse: shouldUseCombinedAppTypeResponse(Boolean(combinedAppTypeResponse), previousQuestionDetails?.[1], false),
            isRegenerate: true
        };
        if (error) setError(undefined);
        setIsRegenerateRequestInFlight(true);
        setIsLoading(true);
        setActiveCitation(undefined);
        setSelectedCitation(0);
        setActiveAnalysisPanelTab(undefined);
        setIsOnSave(true);
        setScrollToAnswer(true);
        setSharedAnswerLoadingState(true);
        dispatch(QuestionInputReducerAction.updateAnswerGenerationActive(true));
        dispatch(
            QuestionInputReducerAction.updateAnswerGenerationActiveDetails({
                isEditedQuestion: false,
                editedQuestionId: null,
                isChatShared,
                selectedAppType: regenerateAppType,
                questionSendToAI: question,
                questionContentToReRenderInQuestionInput: extractedQuestion,
                sessionId: sessionStorage.getItem("sessionId") ?? null,
                templateId: templateId ?? null,
                promptInstructions: promptInstructions || null,
                fileAttachments: filesAttached ?? []
            })
        );
        dispatch(QuestionInputReducerAction.updateAbortStageLevel("RequestInitiated"));
        lastAnswerRef.current = "";
        lastFileMappingRef.current = null;
        lastDataPointsRef.current = [];
        lastThoughtsRef.current = "";
        lastFollowUpRef.current = [];
        lastKeyWordRef.current = "";
        try {
            if (currentView === "history" && !isChatShared) {
                let indexToRemove = -1;
                if (lastActiveIndexRef.current === 1) {
                    const indexes = [];
                    for (let i = 0; i < lastActiveSessionRef.current.length; i++) {
                        if (lastActiveSessionRef?.current[i]?.session_id === currentIndex) {
                            indexes.push(i);
                        }
                    }
                    indexToRemove = indexes.length > 0 ? indexes[indexes.length - 1] : -1;
                } else {
                    indexToRemove = lastActiveSessionRef.current.findIndex(obj => obj.session_id === currentIndex);
                }
                if (indexToRemove !== -1) {
                    lastActiveSessionRef.current.splice(indexToRemove, 1);
                    if (isSessionSearchActive) {
                        updateSessionStorage("remove", lastSessionRef.current);
                    }
                }
                if (Object.keys(lastSessionRef.current).length !== 0) {
                    const indexTarget: number = todaySessionsRef.findIndex((session: SessionInfo) => session.session_id === lastSessionRef.current.session_id);
                    if (indexTarget === -1 || indexTarget !== 0) {
                        todaySessionsRef.unshift(lastSessionRef.current);
                        if (isSessionSearchActive) {
                            updateSessionStorage("shift", lastSessionRef.current);
                        }
                    }
                }
            } else {
                const sessionIDValue = sessionStorage.getItem("sessionId");
                if (!!(sessionIDValue && sessionIDValue !== "")) {
                    const indexTarget: number = todaySessionsRef.findIndex((session: SessionInfo) => session.session_id === sessionIDValue);
                    if (indexTarget === -1 || indexTarget !== 0) {
                        const session = {
                            session_id: sessionStorage.getItem("sessionId") ?? "",
                            title: question,
                            chat_summary_title: hasTemplateForSummary
                                ? getChatSummaryTitle(extractedQuestion, templateId as string, templateContents)
                                : extractedQuestion,
                            conversation_date: new Date().toString()
                        };
                        todaySessionsRef.unshift(session);
                        if (isSessionSearchActive) {
                            updateSessionStorage("shift", session);
                        }
                    }
                }
                setCurrentIndex(sessionStorage.getItem("sessionId") ?? "");
            }
            const effectiveRegenerateProductIndexes = forcedProductSelection?.product_filter ?? productIndexes;
            const { messages, previousQuestionIds } = buildRegenerateMessages(
                answers,
                primaryId,
                regenerateAppType,
                effectiveRegenerateProductIndexes,
                appInfo,
                resolvedProductLocale
            );
            // Fetch in parallel: history IDs and current question ID separately.
            // Keeps question_id queried exactly once — history call only covers prior turns.
            const historyIdsToLookup = Array.from(new Set(previousQuestionIds)).filter(Boolean);
            const [historyAttachmentIds, currentQuestionOnlyAttachmentIds] = await Promise.all([
                historyIdsToLookup.length > 0 ? getServerSideIdsOfAttachments(historyIdsToLookup) : Promise.resolve([]),
                question_id ? getServerSideIdsOfAttachments([question_id as string]) : Promise.resolve([])
            ]);

            const previousAttachmentIds = [...(historyAttachmentIds ?? []), ...(currentQuestionOnlyAttachmentIds ?? [])];
            const ctx = previousQuestionDetails?.[1]?.[regenerateAppType]?.context ?? {};
            const { document_category, product_group_ids, sub_product_group_ids } = ctx;
            const product_filter = forcedProductSelection?.product_filter
                ? forcedProductSelection.product_filter
                : setProductFilterForRequestPayload(previousQuestionDetails[1], regenerateAppType, currentView, productIndexes, resolvedProductLocale);
            const sub_product_filter = forcedProductSelection?.sub_product_filter
                ? forcedProductSelection.sub_product_filter
                : setSubProductFilterForRequestPayload(previousQuestionDetails[1], regenerateAppType, currentView, subProductIndexes, resolvedProductLocale);
            const product_version = forcedProductSelection?.product_version
                ? forcedProductSelection.product_version
                : setProductVersionForRequestPayload(previousQuestionDetails[1], regenerateAppType, currentView, productVersionIndexes, resolvedProductLocale);
            // When a forced product selection is provided (e.g., user switched product in answer area),
            // recompute group_ids from the new product_filter instead of reusing the previous answer's
            // context values, which still contain the old (auto-selected) product's index.
            let effectiveGroupIds: string;
            let effectiveSubGroupIds: string;
            if (forcedProductSelection) {
                const filtered_groupids = setGroupIdsByAppTypeForRequestPayload(regenerateAppType, product_filter, sub_product_filter, resolvedProductLocale);
                effectiveGroupIds =
                    filtered_groupids && filtered_groupids.groupIds && filtered_groupids.groupIds.size > 0
                        ? [...filtered_groupids.groupIds].filter(id => id !== "").join(",")
                        : "0";
                effectiveSubGroupIds = filtered_groupids
                    ? filtered_groupids.sub_group_ids && filtered_groupids.sub_group_ids.size > 0
                        ? [...filtered_groupids.sub_group_ids].filter(id => id !== "").join(",")
                        : "0"
                    : "0";
            } else {
                effectiveGroupIds = product_group_ids as string;
                effectiveSubGroupIds = sub_product_group_ids as string;
            }
            const request: ChatAppRequest = {
                messages: [...messages, { content: userQuestionContent, role: "user" }],
                stream: shouldStream,
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length ? promptTemplate : undefined,
                        exclude_category: excludeCategory.length ? excludeCategory : undefined,
                        language: selectedLanguage,
                        top: retrieveCount,
                        chat_model: chatModel as ChatModel,
                        reasoning_effort: reasoningEffortSelected,
                        retrieval_reasoning_effort: retrievalReasoningEffort,
                        only_citations: onlyCitations,
                        retrieval_mode: retrievalMode,
                        cosmosdb_retrieval_mode: cosmosDBRetrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        enable_OCR: appInfo?.enable_OCR ?? false,
                        doctype_filter: document_category as string,
                        product_filter,
                        sub_product_filter,
                        product_version,
                        group_ids: effectiveGroupIds,
                        sub_group_ids: effectiveSubGroupIds,
                        template_keys: templateKeysValue,
                        templateContents: templateContentsValue,
                        templateId: templateId,
                        extractedQuestion,
                        promptInstructions,
                        agentic_retrieval_output_size: normalizeAgenticRetrievalOutputSize(agenticRetrievalOutputSize),
                        agentic_retrieval_document_count: normalizeAgenticRetrievalDocumentCount(agenticRetrievalDocumentCount),
                        agentic_retrieval_source_document_count: normalizeAgenticRetrievalSourceDocumentCount(agenticRetrievalSourceDocumentCount),
                        chat_summary_title: hasTemplateForSummary
                            ? getChatSummaryTitle(extractedQuestion, templateId as string, templateContents)
                            : extractedQuestion,
                        fromHistory: false,
                        sessionId: sessionStorage.getItem("sessionId"),
                        versions: {
                            rootId: previousQuestionDetails[0]?.rootId ?? "",
                            parentMessageId: previousQuestionDetails[0]?.parentMessageId ?? "",
                            edited_version: previousQuestionDetails[0]?.edited_version ?? 0
                        },
                        question_id: question_id as string,
                        has_question_id: true,
                        old_chat_id: convId,
                        is_chat_shared: isChatShared,
                        source_shared_session_id: isChatShared ? sessionId : null,
                        all_product_selection_meta: selectionMeta ?? null,
                        combinedAppTypeResponse,
                        isAppTypeIntegrated: regenerateAppType === "ALL",
                        isRegenerated: true,
                        file_attachment_ids: Array.from(new Set(previousAttachmentIds)),
                        // For regenerate, save only this question's own attachments to Cosmos.
                        current_question_file_attachment_ids: Array.from(new Set(currentQuestionOnlyAttachmentIds)),
                        use_agentic_knowledgebase: useAgenticKnowledgebase,
                        skip_validation: true
                    }
                },
                session_state: null
            };
            const controller = new AbortController();
            makeApiRequestControllerRef.current = controller;
            appTypeBasedOnUser = getAppTypeBasedOnUser(regenerateAppType);
            const response = await chatApi(request, appTypeBasedOnUser, userInfo.corp_id, controller.signal);
            if ((!response || !response?.body) && store.getState().questionInput.requestedToForceStopAIResponseGeneration) {
                await fallBackUIOnAbortChatAPI(false, appTypeBasedOnUser as AppType);
                isRegenerateActive.current = false;
                return;
            }
            if (!response || !response?.body) {
                isRegenerateActive.current = false;
                throw new Error("No response body");
            }
            let latestSaveContext: any = null;
            const applyParsedResponse = async (parsedResponse: ChatAppResponse) => {
                const normalizedParsedResponse = normalizeParsedResponse(parsedResponse);
                const responseContext = normalizedParsedResponse.context ?? ({} as any);
                responseContext.chat_model = chatModel as string;
                responseContext.document_category = document_category as string;
                responseContext.reasoning_effort = reasoningEffortSelected;
                responseContext.retrieval_reasoning_effort = retrievalReasoningEffort;
                responseContext.question_id = question_id as string;
                responseContext.has_question_id = true;
                // When a forced product selection was used (e.g., SwapHoriz product switch),
                // persist the new product's group IDs so subsequent regenerations read the
                // correct values instead of the original auto-selected product's IDs.
                responseContext.product_group_ids = forcedProductSelection ? effectiveGroupIds : product_group_ids;
                responseContext.sub_product_group_ids = forcedProductSelection ? effectiveSubGroupIds : sub_product_group_ids;
                normalizedParsedResponse.context = responseContext;
                (normalizedParsedResponse as ChatAppResponse & { allProductSelectionMeta?: AllProductSelectionMeta | null }).allProductSelectionMeta =
                    selectionMeta ?? null;
                lastAnswerRef.current = normalizedParsedResponse.output_text ?? "";
                lastFileMappingRef.current = responseContext?.file_mapping ?? null;
                lastDataPointsRef.current = responseContext?.data_points ?? [];
                lastThoughtsRef.current = responseContext?.thoughts ?? "";
                lastKeyWordRef.current = responseContext?.keywords ?? "";
                lastFollowUpRef.current = responseContext?.followup_questions ?? [];
                latestSaveContext = responseContext ?? null;
                setAnswers(prev => {
                    const next = updateRegeneratedAnswerInState(prev, primaryId, regenerateAppType, normalizedParsedResponse);
                    setOverAllCurrentSessionDataPoints(next);
                    return next;
                });
                setRegeneratedAnswers(prev => {
                    const qid = question_id as string;
                    const prevQ = prev[qid];
                    const prevList = prevQ?.[regenerateAppType];
                    const previousResponse = previousQuestionDetails?.[1]?.[regenerateAppType];
                    const incoming = [normalizedParsedResponse];
                    const previousResponseList = previousResponse === undefined ? [] : Array.isArray(previousResponse) ? previousResponse : [previousResponse];
                    const nextList = prevList !== undefined ? [...prevList, ...incoming] : [...previousResponseList, ...incoming];
                    const nextRegeneratedAnswers = {
                        ...prev,
                        [qid]: {
                            ...(prevQ ?? {}),
                            [regenerateAppType]: nextList
                        }
                    };
                    const length = nextList.length;
                    setAnswerIndexMap(prevMap => ({
                        ...prevMap,
                        [`${question_id}__${regenerateAppType}`]: length - 1
                    }));
                    return nextRegeneratedAnswers;
                });
            };
            if (shouldStream) {
                dispatch(QuestionInputReducerAction.updateAbortStageLevel("ResponseReceived"));
                try {
                    const parsedResponse = await handleAsyncRequest(
                        question,
                        response.body,
                        appTypeBasedOnUser as AppType,
                        templateId,
                        extractedQuestion,
                        primaryId,
                        true
                    );
                    if (store.getState().questionInput.requestedToForceStopAIResponseGeneration) {
                        await fallBackUIOnAbortChatAPI(false, appTypeBasedOnUser as AppType);
                        return;
                    }
                    if (sessionStorage.getItem("sessionId") !== regenerateRequestSessionId) {
                        return;
                    }
                    dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                    await applyParsedResponse(parsedResponse);
                } finally {
                    setIsStreaming(false);
                }
            } else {
                const parsedResponseOrError: ChatAppResponseOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                    throw new Error((parsedResponseOrError as any)?.error || "Unknown error");
                }
                if (sessionStorage.getItem("sessionId") !== regenerateRequestSessionId) {
                    return;
                }
                dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                await applyParsedResponse(normalizeParsedResponse(parsedResponseOrError));
            }
            if (sessionStorage.getItem("sessionId") !== regenerateRequestSessionId) {
                return;
            }
            dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds([]));
            const sessionIDValue = sessionStorage.getItem("sessionId");
            if (isChatShared && sessionIDValue) {
                await getSessionInfoDataForChatSummaryTitleRetain(sessionIDValue);
            }
            updateAppTypeValueInSessionStorage(appTypeBasedOnUser as AppType);
            setIsOnEdit(false);
            dispatch(ChatComponentReducerAction.updateCurrentSessionId(sessionIDValue ?? null));
            if (!sessionIDValue) {
                dispatch(ChatComponentReducerAction.resetOverAllCurrentSessionDataPoints());
            }
            urlParams.append("SessionId", sessionIDValue ?? "");
            urlParams.append("AppType", appTypeBasedOnUser as AppType);
            navigate(`?${urlParams.toString()}`);
            lastStableAppTypeBeforeTabSwitchRef.current = null;
            if (isChatShared) setIsChatShared(false);
            setTemplateKeywords("");
            const result = {
                conv_id: latestSaveContext?.conv_id,
                groupId: latestSaveContext?.groupId,
                title: latestSaveContext?.title,
                conversationDate: latestSaveContext?.conversationDate
            };
            const hasRequiredSaveFields = result.conv_id && result.groupId && result.title && result.conversationDate && latestSaveContext?.save_error !== true;
            if (hasRequiredSaveFields) {
                conversationId.current = result.conv_id;
                setAnswers(prev => {
                    const next = [...prev];
                    const currentContext = next[lastAnswerIndexRef.current]?.[1]?.[regenerateAppType]?.context;
                    if (currentContext) currentContext.conv_id = result.conv_id;
                    return next;
                });
                const updatedRecords = [...conversationDetails];
                let newConvDate = { [regenerateAppType]: result.conversationDate };
                newConvDate = { ...newConvDate, ...updatedRecords[lastAnswerIndexRef.current]?.conversationDate };
                updatedRecords[lastAnswerIndexRef.current] = {
                    userName,
                    groupId: result.groupId,
                    title: result.title,
                    conversationDate: newConvDate
                };
                setConversationDetails(updatedRecords);
            }
            setIsOnSave(false);
            const shouldOpenSupportingContent = hasNonAttachmentCitationsForContent(
                lastAnswerRef.current,
                (lastDataPointsRef.current as DataPoints[]) ?? [],
                lastFileMappingRef.current,
                regenerateAppType
            );
            if (shouldOpenSupportingContent) {
                setActiveAnalysisPanelTab(AnalysisPanelTabs.SupportingContentTab);
                setSelectedAnswer(lastAnswerIndexRef.current);
            }
        } catch (e) {
            const requestedStop = store.getState().questionInput.requestedToForceStopAIResponseGeneration;
            if (isAbortError(e)) {
                shouldClearVersionSnapshot = false;
                setError(undefined);
                if (requestedStop) {
                    await fallBackUIOnAbortChatAPI(false, (appTypeBasedOnUser ?? regenerateAppType) as AppType);
                }
            } else {
                setError(e);
            }
            isRegenerateActive.current = false;
            setIsOnSave(false);
            setIsOnEdit(false);
            console.log(e);
        } finally {
            isRegenerateActive.current = false;
            setIsLoading(false);
            setScrollToAnswer(false);
            setSharedAnswerLoadingState(false);
            dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
            setIsRegenerateRequestInFlight(false);
            // Do not clear on abort: older aborted requests can race and wipe newer snapshots.
            if (shouldClearVersionSnapshot) {
                clearVersionIndices();
            }
        }
    };

    const updateSessionStorage = (action: "shift" | "remove", activeSession: SessionInfo) => {
        updateSessionStorageForSearch(
            action,
            activeSession,
            currentIndex,
            lastActiveIndexRef.current,
            {
                todaySessions: backupTodaySessions,
                last7Sessions: backupLast7Sessions,
                last30Sessions: backupLast30Sessions,
                monthlySessions: backupMonthlySessions
            },
            {
                setBackupTodaySessions,
                setBackupLast7Sessions,
                setBackupLast30Sessions,
                setBackupMonthlySessions
            }
        );
    };

    const saveChatHistory = async (appType: AppType, selectedChatIndex?: number, saveContext?: any) => {
        try {
            const result = {
                conv_id: saveContext?.conv_id,
                groupId: saveContext?.groupId,
                title: saveContext?.title,
                conversationDate: saveContext?.conversationDate
            };

            if (!result.conv_id || saveContext?.save_error) {
                setIsOnSave(false);
                return;
            }

            conversationId.current = result.conv_id;
            const convIndex = isTabClick.current === "true" ? lastAnswerIndexRef.current : answers?.length;
            setAnswers((prevAnswers: any) => {
                const newAnswers = [...prevAnswers];
                const currentChoice = newAnswers[convIndex]?.[1]?.[appType];
                if (currentChoice?.context) {
                    currentChoice.context.conv_id = result.conv_id;
                }
                return newAnswers;
            });
            let updatedRecords = [...conversationDetails];
            if (IsOnEdit) {
                updatedRecords[selectedQuestion] = {
                    userName: userName,
                    groupId: result.groupId,
                    title: result.title,
                    conversationDate: { [appType]: result.conversationDate }
                };
            } else {
                let newConvDate = { [appType]: result.conversationDate };
                if (isTabClick.current === "true") {
                    newConvDate = { ...newConvDate, ...updatedRecords[lastAnswerIndexRef.current]["conversationDate"] };
                    updatedRecords[lastAnswerIndexRef.current] = {
                        userName: userName,
                        groupId: result.groupId,
                        title: result.title,
                        conversationDate: newConvDate
                    };
                } else {
                    updatedRecords = [
                        ...conversationDetails,
                        {
                            userName: userName,
                            groupId: result.groupId,
                            title: result.title,
                            conversationDate: newConvDate
                        }
                    ];
                }
            }
            setConversationDetails(updatedRecords);
            setIsOnSave(false);
            const selectedIndex = selectedChatIndex ?? 0;
            const shouldOpenSupportingContent = hasNonAttachmentCitationsForContent(
                lastAnswerRef.current,
                (lastDataPointsRef.current as DataPoints[]) ?? [],
                lastFileMappingRef.current,
                appType
            );
            if (shouldOpenSupportingContent) {
                setActiveAnalysisPanelTab(AnalysisPanelTabs.SupportingContentTab);
                if (IsOnEdit) {
                    setSelectedAnswer(selectedIndex);
                } else {
                    if (isTabClick.current === "true") {
                        setSelectedAnswer(lastAnswerIndexRef.current);
                    } else {
                        setSelectedAnswer(answers?.length > 0 ? answers.length : 0);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsOnSave(false);
        }
    };

    const validateAndSetOptions = (options: DataOptions) => {
        if (options && Object.keys(options).length > 0) {
            setOptions(options);
        }
    };

    const loadChatData = async (
        sessionId: string,
        isSideMenuItemClicked: boolean,
        isChatAPIRequestAborted: boolean,
        preloadedResult?: any
    ): Promise<boolean> => {
        const loadChatDataRequestToken = ++loadChatDataRequestTokenRef.current;
        const targetSessionId = normalizeSessionIdentity(sessionId);
        const previousSuggestionsSessionId = normalizeSessionIdentity(activeSuggestionsSessionIdRef.current);
        const isSessionTransition = targetSessionId !== previousSuggestionsSessionId;
        if (isSessionTransition) {
            resetSuggestionsForSessionTransition(targetSessionId, true);
        } else {
            activeSuggestionsSessionIdRef.current = targetSessionId;
        }

        dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds([]));
        dispatch(ChatComponentReducerAction.updateCurrentSessionId(sessionId ?? null));
        if (!sessionId) {
            dispatch(ChatComponentReducerAction.resetOverAllCurrentSessionDataPoints());
        }
        if (isChatAPIRequestAborted) {
            // null means "abort reload did not complete yet"; 0 is a valid loaded count.
            loadedAnswerCountAfterAbortRef.current = null;
        }

        // Clear version preservation when loading different session (unless it's abort reload)
        const currentStoredSessionId = sessionStorage.getItem("sessionId");
        if (!isChatAPIRequestAborted || currentStoredSessionId !== sessionId) {
            clearVersionIndices();
            // Clear precheck cache on session navigation
            precheckResultCacheRef.current = null;
        }
        try {
            setIsHistoryPageInitialLoading(true);
            setIsEdited(true);
            setIsNewChatThreadInitialLoading?.(false);
            resetTemplateSelection();
            setSharedAnswerLoadingState(true);
            setError(undefined);
            setConversationDetails([]);
            setCurrentView("history");
            setnewChatQuestion("");
            setDocType(false);
            setIsOnEdit(false);
            setShowSupportLink(false);
            // When the caller already has the data (e.g. loadSharedChat fetched it via
            // /load-shared-chat), skip the duplicate /load-chat round-trip entirely.
            const response = preloadedResult ? null : await loadChatApi(sessionId, "chat");
            const result = preloadedResult ?? (await response!.json());
            if (loadChatDataRequestToken !== loadChatDataRequestTokenRef.current) {
                return false;
            }
            if (!result.answers || result.answers.length === 0) {
                loadedAnswerCountAfterAbortRef.current = 0;
                setIsSessionNotFound(true);
                clearChatState();
                setSelectedSessionId(sessionId);
                setDeveloperSettings(result.settings ?? {});
                setCurrentView("history");
                validateAndSetOptions(result.options ?? {});
                if (sessionId) {
                    await handleDeleteSession(sessionId);
                }
                return true;
            } else {
                setIsSessionNotFound(false);
            }
            const preservedSelections = isChatAPIRequestAborted ? restorePreservedSelections(sessionId) : null;
            const defaultSelectedAnswerIndex = result.answers.length - 1;
            updateDisclaimerStatus();
            if (!isChatAPIRequestAborted) {
                currentTargetQuestionId.current = defaultSelectedAnswerIndex;
                setSelectedAnswer(defaultSelectedAnswerIndex);
                setScrollToAnswer(true);
                validateAndSetOptions(result.options ?? {});
            }
            type RegeneratedAnswersMap = Record<string, Partial<Record<AppType, ChatAppResponse[]>>>;
            type QuestionVersionRestoreTarget = {
                answerIndex: number;
                parentMessageId: string;
                questionId: string;
                selectedQuestionVersion: number;
            };

            const regeneratedAnswersFromServer: RegeneratedAnswersMap = result.regenerated_answers ?? {};
            const restoreTree = buildTree(result.answers, result.histories ?? {});

            const defaultQuestionVersionIndices = Object.fromEntries(Object.keys(result.histories ?? {}).map(id => [id, 0]));
            const defaultAnswerVersionIndices = Object.fromEntries(
                Object.entries(regeneratedAnswersFromServer).flatMap(([key, appTypes]) => {
                    return Object.keys(appTypes).map(appType => [`${key}__${appType}`, 0]);
                })
            );

            const mergedQuestionVersionIndices = {
                ...defaultQuestionVersionIndices,
                ...(preservedSelections?.versionIndexMap ?? {})
            };
            const mergedAnswerVersionIndices = {
                ...defaultAnswerVersionIndices,
                ...(preservedSelections?.answerIndexMap ?? {})
            };
            const normalizedAnswerVersionIndices = Object.fromEntries(
                Object.keys(defaultAnswerVersionIndices).map(answerMapKey => {
                    const separatorIndex = answerMapKey.lastIndexOf("__");
                    if (separatorIndex <= 0) {
                        return [answerMapKey, 0];
                    }

                    const questionId = answerMapKey.slice(0, separatorIndex);
                    const appType = answerMapKey.slice(separatorIndex + 2) as AppType;
                    const answerVersions = regeneratedAnswersFromServer?.[questionId]?.[appType];
                    const maxAnswerVersionIndex = Array.isArray(answerVersions) ? Math.max(answerVersions.length - 1, 0) : 0;
                    const preservedAnswerVersion = mergedAnswerVersionIndices[answerMapKey];
                    const numericPreservedAnswerVersion = typeof preservedAnswerVersion === "number" ? preservedAnswerVersion : Number(preservedAnswerVersion);
                    const safePreservedAnswerVersion = Number.isFinite(numericPreservedAnswerVersion) ? numericPreservedAnswerVersion : 0;

                    return [answerMapKey, Math.min(Math.max(safePreservedAnswerVersion, 0), maxAnswerVersionIndex)];
                })
            ) as Record<string, number>;
            const normalizedQuestionVersionIndices: Record<string, number> = { ...defaultQuestionVersionIndices };
            let hydratedAnswers = [...result.answers];
            const toQuestionVersionRestoreTarget = (answerRow: unknown, answerIndex: number): QuestionVersionRestoreTarget | null => {
                if (!Array.isArray(answerRow)) {
                    return null;
                }

                const questionDetails = answerRow[0] as QuestionDetails | undefined;
                if (!questionDetails) {
                    return null;
                }

                const parentMessageId = getIdToCheck(questionDetails);
                if (!parentMessageId) {
                    return null;
                }

                const questionId = questionDetails.question_id;
                if (!questionId) {
                    return null;
                }

                const selectedQuestionVersion = mergedQuestionVersionIndices[parentMessageId] ?? 0;
                if (selectedQuestionVersion === 0) {
                    return null;
                }

                return {
                    answerIndex,
                    parentMessageId,
                    questionId,
                    selectedQuestionVersion
                };
            };

            const questionVersionRestoreTargets = result.answers
                .map((answerRow: unknown, answerIndex: number) => toQuestionVersionRestoreTarget(answerRow, answerIndex))
                .filter((restoreTarget: QuestionVersionRestoreTarget | null): restoreTarget is QuestionVersionRestoreTarget => restoreTarget !== null);

            for (const restoreTarget of questionVersionRestoreTargets) {
                const currentAnswerIndex = hydratedAnswers.findIndex(answerRow => answerRow?.[0]?.question_id === restoreTarget.questionId);
                const fallbackIndex = Math.min(restoreTarget.answerIndex, hydratedAnswers.length);
                const effectiveAnswerIndex = currentAnswerIndex >= 0 ? currentAnswerIndex : fallbackIndex;

                const restoredAnswers = loadAnswerRowsFromTree(
                    hydratedAnswers,
                    effectiveAnswerIndex,
                    restoreTree,
                    restoreTarget.parentMessageId,
                    restoreTarget.selectedQuestionVersion
                );

                const restoredVersionRows = Array.isArray(restoredAnswers) ? restoredAnswers.slice(Math.max(effectiveAnswerIndex, 0)) : [];
                const hasMatchingVersionRows = restoredVersionRows.some(answerRow => {
                    const restoredQuestionDetails = answerRow?.[0];
                    if (!restoredQuestionDetails) return false;

                    return (
                        getIdToCheck(restoredQuestionDetails) === restoreTarget.parentMessageId &&
                        String(restoredQuestionDetails.edited_version ?? 0) === String(restoreTarget.selectedQuestionVersion)
                    );
                });

                if (hasMatchingVersionRows) {
                    hydratedAnswers = restoredAnswers;
                    normalizedQuestionVersionIndices[restoreTarget.parentMessageId] = restoreTarget.selectedQuestionVersion;
                } else {
                    normalizedQuestionVersionIndices[restoreTarget.parentMessageId] = 0;
                }
            }

            const applySelectedAnswerVersionOverrides = (questionId: string | undefined, responseByAppType: any) => {
                if (!questionId || !responseByAppType) {
                    return {
                        updatedResponses: responseByAppType,
                        hasAnswerVersionOverride: false
                    };
                }

                let hasAnswerVersionOverride = false;
                let updatedResponses = responseByAppType;

                (["FAQ", "Manuals", "ALL"] as AppType[]).forEach(appType => {
                    const answerMapKey = `${questionId}__${appType}`;
                    const selectedAnswerVersion = normalizedAnswerVersionIndices[answerMapKey] ?? 0;
                    if (selectedAnswerVersion === 0) return;

                    const selectedAnswerPayload = regeneratedAnswersFromServer?.[questionId]?.[appType]?.[selectedAnswerVersion];
                    if (!selectedAnswerPayload) return;

                    hasAnswerVersionOverride = true;
                    updatedResponses = {
                        ...updatedResponses,
                        [appType]: selectedAnswerPayload
                    };
                });

                return {
                    updatedResponses,
                    hasAnswerVersionOverride
                };
            };

            hydratedAnswers = hydratedAnswers.map(answerRow => {
                const questionDetails = answerRow?.[0];
                const responseByAppType = answerRow?.[1];
                const questionId = questionDetails?.question_id;

                if (!questionId || !responseByAppType) {
                    return answerRow;
                }

                const { updatedResponses, hasAnswerVersionOverride } = applySelectedAnswerVersionOverrides(questionId, responseByAppType);

                if (!hasAnswerVersionOverride) {
                    return answerRow;
                }

                return [{ ...questionDetails }, updatedResponses];
            });
            const hydrateTreeNodeWithSelectedAnswerVersions = (treeNode: any): any => {
                if (!treeNode || typeof treeNode !== "object") {
                    return treeNode;
                }

                const nextChildren = Array.isArray(treeNode.children)
                    ? treeNode.children.map((childNode: any) => hydrateTreeNodeWithSelectedAnswerVersions(childNode))
                    : treeNode.children;

                if (!treeNode?.record?.question) {
                    return nextChildren === treeNode.children
                        ? treeNode
                        : {
                              ...treeNode,
                              children: nextChildren
                          };
                }

                const questionId = treeNode.record.question.question_id;
                const responseByAppType = treeNode?.record?.answer;
                const { updatedResponses } = applySelectedAnswerVersionOverrides(questionId, responseByAppType);

                return {
                    ...treeNode,
                    children: nextChildren,
                    record: {
                        ...treeNode.record,
                        answer: updatedResponses
                    }
                };
            };

            const hydratedRestoreTree = Array.isArray(restoreTree)
                ? restoreTree.map((treeNode: any) => hydrateTreeNodeWithSelectedAnswerVersions(treeNode))
                : restoreTree;
            setConversationDetails(
                hydratedAnswers.map((answerRow: any) => ({
                    userName: userName,
                    groupId: sessionId,
                    title: answerRow[0]?.extractedQuestion ?? answerRow[0]?.question,
                    conversationDate: {
                        Manuals: answerRow[1]["Manuals"]?.context?.feedback?.c_date ?? "NA",
                        FAQ: answerRow[1]["FAQ"]?.context?.feedback?.c_date ?? "NA",
                        ALL: answerRow[1]["ALL"]?.context?.feedback?.c_date ?? "NA"
                    },
                    conversations: {
                        Manuals: answerRow[1]["Manuals"]?.context ?? "NA",
                        FAQ: answerRow[1]["FAQ"]?.context ?? "NA",
                        ALL: answerRow[1]["ALL"]?.context ?? "NA"
                    }
                }))
            );
            setSelectedCitation(0);
            setRegeneratedAnswers(regeneratedAnswersFromServer);
            setAnswerIndexMap(normalizedAnswerVersionIndices);
            loadedAnswerCountAfterAbortRef.current = hydratedAnswers.length;
            setAnswers([...hydratedAnswers]);
            setOverAllCurrentSessionDataPoints(hydratedAnswers);
            setAllAnswersTree(hydratedRestoreTree);
            setHistories(result.histories ?? {});
            setVersionIndexMap(normalizedQuestionVersionIndices);

            setSelectedSessionId(sessionId);
            setDeveloperSettings(result.settings);
            const defaultSelectedAnswerEntry = hydratedAnswers?.[defaultSelectedAnswerIndex];
            const defaultSelectedAnswerAppType = getAnalysisAppTypeForAnswer(defaultSelectedAnswerEntry);
            const defaultSelectedAnswer = defaultSelectedAnswerEntry?.[1]?.[defaultSelectedAnswerAppType];
            const shouldOpenSupportingContent =
                !isChatAPIRequestAborted && hasNonAttachmentCitationsForAnswer(defaultSelectedAnswer, defaultSelectedAnswerAppType);
            setActiveAnalysisPanelTab(shouldOpenSupportingContent ? AnalysisPanelTabs.SupportingContentTab : undefined);
            const queryParams = parseQueryParams(location.search);
            const restoredAppTypeAfterAbort =
                isChatAPIRequestAborted && lastStableAppTypeBeforeTabSwitchRef.current ? getValidAppType(lastStableAppTypeBeforeTabSwitchRef.current) : null;
            const urlAppType = appInfo.use_all_tab === true ? queryParams.AppType : queryParams.AppType !== "ALL" ? queryParams.AppType : "FAQ";
            const normalizedUrlAppType = getValidAppType(urlAppType ?? "");
            let appTypeValue: AppType = (restoredAppTypeAfterAbort ?? normalizedUrlAppType) as AppType;
            if ((queryParams && !Object.values(queryParams).includes("chat_share")) || isSideMenuItemClicked) {
                // means history load from side menu.
                // append Apptype to url.
                const urlParams = new URLSearchParams();
                urlParams.append("SessionId", sessionId ?? "");
                appTypeValue = (
                    isSideMenuItemClicked ? getAppTypeBasedOnLastAnswerIsRelatedToAriel([...result.answers]) : getValidAppType(appTypeValue)
                ) as AppType;
                const appTypeBasedOnUser = getAppTypeBasedOnUser(appTypeValue);
                urlParams.append("AppType", getValidAppType(appTypeBasedOnUser));
                navigate(`?${urlParams.toString()}`, { replace: !isSideMenuItemClicked });
                setSelectedAppType(appTypeBasedOnUser as AppType);
            } else {
                // means share chat.
                if (!isChatAPIRequestAborted) {
                    appTypeValue = getValidAppType(appTypeValue) as AppType;
                    userInfo.apptype = appTypeValue;
                    setSelectedAppType(appTypeValue as AppType);
                } else {
                    // if chat api request is Aborted for share chat -> even if request aborted rerender the url to new session Id.
                    const urlParams = new URLSearchParams();
                    urlParams.append("SessionId", sessionId ?? "");
                    appTypeValue = (
                        isSideMenuItemClicked ? getAppTypeBasedOnLastAnswerIsRelatedToAriel([...result.answers]) : getValidAppType(appTypeValue)
                    ) as AppType;
                    const appTypeBasedOnUser = getAppTypeBasedOnUser(appTypeValue);
                    urlParams.append("AppType", getValidAppType(appTypeBasedOnUser));
                    navigate(`?${urlParams.toString()}`, { replace: !isSideMenuItemClicked });
                    setSelectedAppType(appTypeBasedOnUser as AppType);
                }
            }
            if (restoredAppTypeAfterAbort) {
                userInfo.apptype = restoredAppTypeAfterAbort;
                lastStableAppTypeBeforeTabSwitchRef.current = null;
            }
            if (!isChatAPIRequestAborted) {
                const appTypeValue = getAppTypeBasedOnLastAnswerIsRelatedToAriel([...result.answers]);
                const answersCount = result.answers.length;
                const lastAnswerValue = result.answers[answersCount > 0 ? answersCount - 1 : 0]?.[1];
                const appName = lastAnswerValue[appTypeValue] ? appTypeValue : answerKeyFor(appTypeValue as AppType);
                const selectedProductValuetoRetain: Product = getProductDetailsFromAppInfo(lastAnswerValue, appName, resolvedProductLocale);
                const productFilterValue = {
                    manual_key: selectedProductValuetoRetain?.manual_key?.toString() ?? "",
                    faq_key: selectedProductValuetoRetain?.faq_key?.toString() ?? ""
                };
                const hasOptionProductSelection = !!(result?.options?.product?.manual_key || result?.options?.product?.faq_key);
                const hasRetainedProductSelection = !!(productFilterValue.manual_key || productFilterValue.faq_key);
                if (!hasOptionProductSelection && hasRetainedProductSelection) {
                    setDropDownFilterText(
                        productFilterValue,
                        result?.options?.product_version ?? { manual_version_key: "", faq_version_key: "" },
                        result?.options?.subProduct ?? { manual_sub_key: "", faq_sub_key: "" }
                    );
                }
                userInfo.product = productFilterValue;
                await saveUserSettings({
                    product: productFilterValue
                });
            }
            return true;
        } catch (error) {
            console.error("Error fetching data:", error);
            return false;
        } finally {
            setSharedAnswerLoadingState(false);
            setScrollToAnswer(false);
        }
    };

    const setDeveloperSettings = async (settings: DeveloperOptions) => {
        try {
            setRetrieveCount(settings.top);
            setChatModel(settings.chat_model in modelsToValidate ? settings.chat_model : defaultFaqAiModel);
            setReasoningEffort(settings.reasoning_effort ?? ReasoningEffort.Low);
            setOnlyCitations(settings.only_citations);
            setRetrievalMode(settings.retrieval_mode);
            setCosmosDBRetrievalMode(settings.cosmosdb_retrieval_mode ?? RetrievalMode.Vectors);
            setUseSemanticRanker(settings.semantic_ranker);
            setUseSemanticCaptions(settings.semantic_captions);
            setUseSuggestFollowupQuestions(settings.suggest_followup_questions);
        } catch (error) {
            console.error("Error on setting developer options data:", error);
        }
    };

    const setOptions = async (options: DataOptions) => {
        try {
            setChatModel(
                options.chat_model in modelsToValidate ? options.chat_model : options?.app_type === "Manuals" ? defaultManualsAiModel : defaultFaqAiModel
            );
            setDropDownFilterText(options.product, options.product_version, options?.subProduct);
            setDocTypeFilter(options.doctype_filter ?? docTypeFilter);
        } catch (error) {
            console.error("Error on setting developer options data:", error);
        }
    };

    const setDropDownFilterText = (prod_filter: any, productVersion: any, sub_product_filter: any) => {
        const setDefaultDropDownFilterState = () => {
            // Show ALL defaults when: admin, OR (global flag enabled AND user's personal flag is enabled)
            const useAllDefaults = isAdmin ? enableAllProductForUser : enableAllProductForAllUsers && enableAllProductForUser;
            markTemplateApplySourceForNextSwitch("programmatic", useAllDefaults ? "ALL" : `${i18next.t("chat.productFilterText")}`);
            setProductIndexes(useAllDefaults ? { manual_key: "0", faq_key: "0" } : { manual_key: "", faq_key: "" });
            setProductText(useAllDefaults ? "ALL" : `${i18next.t("chat.productFilterText")}`);
            setSubProductIndexes(useAllDefaults ? { manual_sub_key: "0", faq_sub_key: "0" } : { manual_sub_key: "", faq_sub_key: "" });
            setSubProductText(useAllDefaults ? "ALL" : "...");
            setProductVersionIndexes(useAllDefaults ? { manual_version_key: "0", faq_version_key: "0" } : { manual_version_key: "", faq_version_key: "" });
            setProductVersionText(useAllDefaults ? "ALL" : `${i18next.t("chat.sub_version")}`);
            const appTypeValueData = appInfo.use_all_tab === true ? userInfo?.apptype : userInfo?.apptype !== "ALL" ? userInfo?.apptype : "FAQ";
            const normalizedAppType = getValidAppType(appTypeValueData ?? "");
            setSelectedAppType(useAllDefaults ? normalizedAppType : userInfo?.apptype === "Manuals" ? "FAQ" : normalizedAppType);
        };

        if (!prod_filter?.manual_key && !prod_filter?.faq_key) {
            setDefaultDropDownFilterState();
            return;
        }

        const product = appInfo.product_categories[resolvedProductLocale]?.filter(
            (item: any) => (item.manual_key && item.manual_key === prod_filter.manual_key) || (item.faq_key && item.faq_key === prod_filter.faq_key)
        );
        if (product?.length > 0) {
            if (prod_filter.manual_key === "" && prod_filter.faq_key !== "") {
                prod_filter.manual_key = product[0].manual_key;
                const appTypeValueData = appInfo.use_all_tab === true ? userInfo?.apptype : userInfo?.apptype !== "ALL" ? userInfo?.apptype : "FAQ";
                const appTypeValue = userInfo?.apptype === "Manuals" && product[0].manual_key === "" ? "FAQ" : getValidAppType(appTypeValueData ?? "");
                userInfo.apptype = appTypeValue;
                setSelectedAppType(appTypeValue);
            }
            if (prod_filter.manual_key !== "" && prod_filter.faq_key === "") {
                prod_filter.faq_key = product[0].faq_key;
                // no product exist with empty faq key
                userInfo.apptype = "FAQ";
                setSelectedAppType("FAQ");
            }

            markTemplateApplySourceForNextSwitch("programmatic", product[0]?.text ?? null);
            setProductIndexes(prod_filter);
            setProductText(product[0]?.text);
            if (product[0]?.is_sub_only === "true") {
                const subVersion = product[0]?.versions?.filter(
                    (sv: any) =>
                        (sv.manual_sub_key && sv.manual_sub_key === sub_product_filter.manual_sub_key) ||
                        (sv.faq_sub_key && sv.faq_sub_key === sub_product_filter.faq_sub_key)
                )[0];
                if (subVersion) {
                    setSubProductIndexes(sub_product_filter);
                    setSubProductText(sub_product_filter.manual_sub_key === "0" || sub_product_filter.faq_sub_key === "0" ? "ALL" : (subVersion?.text ?? ""));
                } else {
                    setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
                    setSubProductText("ALL");
                }
                setProductVersionIndexes({ manual_version_key: "", faq_version_key: "" });
                setProductVersionText("");
            } else if ((productVersion?.manual_version_key !== "" || productVersion?.faq_version_key !== "") && product[0]?.versions?.length > 0) {
                const selectedVersion = product[0]?.versions?.filter(
                    (item: any) =>
                        (item.manual_version_key && item.manual_version_key === productVersion?.manual_version_key) ||
                        (item.faq_version_key && item.faq_version_key === productVersion?.faq_version_key)
                )[0];

                if (selectedVersion) {
                    if (productVersion.manual_version_key === "" && productVersion.faq_version_key !== "") {
                        productVersion.manual_version_key = selectedVersion.manual_version_key;
                    }
                    if (productVersion.manual_version_key !== "" && (productVersion.faq_version_key === "" || productVersion.faq_version_key === "null")) {
                        productVersion.faq_version_key = selectedVersion.faq_version_key;
                    }
                }
                setProductVersionIndexes(productVersion);
                setProductVersionText(
                    productVersion?.manual_version_key === "0" || productVersion?.faq_version_key === "0" ? "ALL" : (selectedVersion?.text ?? "")
                );
                if (sub_product_filter?.manual_sub_key !== "" || sub_product_filter?.faq_sub_key !== "") {
                    const subVersion = selectedVersion?.sub_versions?.filter(
                        (sv: any) =>
                            (sv.manual_sub_key && sv.manual_sub_key === sub_product_filter.manual_sub_key) ||
                            (sv.faq_sub_key && sv.faq_sub_key === sub_product_filter.faq_sub_key)
                    )[0];
                    setSubProductIndexes(sub_product_filter);
                    setSubProductText(sub_product_filter.manual_sub_key === "0" || sub_product_filter.faq_sub_key === "0" ? "ALL" : (subVersion?.text ?? ""));
                } else {
                    setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
                    setSubProductText("ALL");
                }
            } else {
                if (product[0]?.versions?.length > 0) {
                    setProductVersionIndexes({ manual_version_key: "0", faq_version_key: "0" });
                    setProductVersionText("ALL");
                    setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
                    setSubProductText("ALL");
                } else {
                    setProductVersionIndexes({ manual_version_key: "", faq_version_key: "" });
                    setProductVersionText("");
                    setSubProductIndexes({ manual_sub_key: "", faq_sub_key: "" });
                    setSubProductText("");
                }
            }
        } else {
            setDefaultDropDownFilterState();
        }
    };

    const loadSessionData = async (sessionId: string, searchTerm: string = "", isNextLoadClicked: boolean = false) => {
        try {
            setIsSessionLoading(true);
            const response = await loadSessionApi(nextDateToLoad, searchTerm);
            const result = await response.json();
            if (nextDateToLoad === "" || searchTerm) {
                setTodaySessionsRef(result["today"] ?? []);
                setLast7SessionsRef(result["7days"] ?? []);
                setLast30SessionsRef(result["30days"] ?? []);
            }
            if (isNextLoadClicked) {
                setMonthlySessions(monthlySessions?.length > 0 ? [...monthlySessions, ...result["months"]] : (result["months"] ?? []));
            } else {
                setMonthlySessions(result["months"] ?? []);
            }
            setNextDateToLoad(result["nextDate"] ?? "");
        } catch (error) {
            console.error("Error fetching session data:", error);
        } finally {
            setIsSessionLoading(false);
        }
    };

    const loadProductFilter = async () => {
        try {
            const product_categories = Array.isArray(appInfo?.product_categories?.[resolvedProductLocale])
                ? [...appInfo.product_categories[resolvedProductLocale]]
                : [];
            const allIndex = product_categories.findIndex((item: any) => item.key === "ALL");

            if (allIndex > -1) {
                const allItem = product_categories.splice(allIndex, 1)[0];
                product_categories.unshift(allItem);
            }

            // Sort categories based on suffix: "" -> "移行前" -> "CONVERSION"
            const sortedCategories = product_categories.sort((a: any, b: any) => {
                const suffixA = a.text.endsWith("移行前") ? 1 : a.text.endsWith("CONVERSION") ? 2 : 0;
                const suffixB = b.text.endsWith("移行前") ? 1 : b.text.endsWith("CONVERSION") ? 2 : 0;
                return suffixA - suffixB;
            });

            setProducts([...sortedCategories]);
            dispatch(AllProductSuggestionsAction.setAllProducts([...sortedCategories]));
            const subProducts =
                product_categories.filter((data: any) => data.manual_key === userInfo?.faqProduct || data.faq_key === userInfo?.faqProduct) ?? [];
            setSubProducts([
                {
                    manual_sub_key: "0",
                    faq_sub_key: "0",
                    text: "ALL"
                },
                ...(subProducts[0]?.versions ?? [])
            ]);
        } catch (error) {
            console.error("Error assigning data from appInfo:", error);
        }
    };

    const loadDocCategories = async () => {
        try {
            if (isWapUser) {
                const productsValues = Object.keys(appInfo.wap_doc_categories).map(key => ({
                    key,
                    text: i18next.t(`chat.${appInfo.wap_doc_categories[key]}`)
                }));
                setCategories(productsValues);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const onFeedbackSubmitClicked = async (feedback: Feedback, conv_id: string, appTypeValue: AppType) => {
        error && setError(undefined);
        try {
            const request: SaveResponse = {
                feedbacks: feedback
            };
            const response = await feedbackApi(request, conv_id, appTypeValue);
            if (!response.body) {
                throw Error("No response body");
            }
        } catch (e) {
            setError(e);
        } finally {
        }
    };

    const clearChatState = () => {
        resetSuggestionsForSessionTransition(null, true);
        setSharedAnswerLoadingState(false);
        setScrollToAnswer(false);
        setConversationDetails([]);
        setAnswers([]);
        setRegeneratedAnswers({});
        setAnswerIndexMap({});
        isRegenerateActive.current = false;
        setHistories({});
        setAllAnswersTree([]);
        setOverAllCurrentSessionDataPoints([]);
        setSelectedCitation(0);
        setDocType(false);
        setActiveAnalysisPanelTab(undefined);
        setSelectedAnswer(0);
        error && setError(undefined);
        setActiveCitation(undefined);
        setShowSupportLink(false);
        lastQuestionRef.current = {
            question: "",
            templateId: "None",
            extractedQuestion: "",
            combinedAppTypeResponse: false,
            isFilesAttached: false,
            filesAttached: []
        };
        lastAnswerRef.current = "";
        lastFileMappingRef.current = null;
        lastDataPointsRef.current = [];
        lastThoughtsRef.current = "";
        lastAnswerIndexRef.current = -1;
        dispatch(QuestionInputReducerAction.resetQuestionInputHistoryStacks());
        dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
        dispatch(QuestionInputReducerAction.updateServerSideFileAttachmentsIds([]));
        dispatch(ChatComponentReducerAction.updateCurrentSessionId(null));
        dispatch(ChatComponentReducerAction.resetOverAllCurrentSessionDataPoints());
        setConversationDetails([]);
        currentQuestionInfo.current = null;
        resetTemplateSelection();
        // Clear version preservation when clearing chat
        clearVersionIndices();
        // Clear precheck cache when clearing chat
        precheckResultCacheRef.current = null;
    };

    const clearChat = (isTokenLimitErrorCall: boolean = false) => {
        loadChatDataRequestTokenRef.current++;
        if (makeApiRequestControllerRef.current) {
            makeApiRequestControllerRef.current.abort();
            makeApiRequestControllerRef.current = null;
        }
        loadedAnswerCountAfterAbortRef.current = null;
        lastStableAppTypeBeforeTabSwitchRef.current = null;
        questionInputRefs.current["chat"]?.clearTemplateContentFromQuestionInput();
        questionInputRefs.current["history"]?.clearTemplateContentFromQuestionInput();
        if (serverSideFileAttachmentsIds && serverSideFileAttachmentsIds.length > 0 && !isTokenLimitErrorCall) {
            deleteFile(serverSideFileAttachmentsIds);
        }
        if (skipValidationOnceRef.current) {
            skipValidationOnceRef.current = false;
        }
        let appTypeValue = getAppTypeBasedOnSelectedValueOnProductFilter();
        userInfo.apptype = appTypeValue;
        updateAppTypeValueInSessionStorage(appTypeValue);
        setSelectedAppType(getValidAppType(userInfo.apptype ?? ""));
        setIsChatShared(false);
        clearChatState();
        // Reset session tracking to null for new thread — makeApiRequest will
        // generate a proper session ID when the user actually sends a message.
        sessionStorage.removeItem("sessionId");
        setSelectedSessionId("");
        setIsLoading(false);
        setIsStreaming(false);
        setnewChatQuestion("");
        setCurrentView("chat");
        setCurrentIndex("");
        setClearChatTriggered(false);
        setClarificationInfo(null);
        Promise.resolve().then(() => {
            setClearChatTriggered(true);
        });
        setChatContentCleared(true);
        setIsHistoryPageInitialLoading(false);
        setIsNewChatThreadInitialLoading?.(true);
        // When ALL mode is active, reset product filter to ALL so the saved/last-used
        // product is never retained across new threads.
        const useAllDefaultsForNewThread = isAdmin ? enableAllProductForUser : enableAllProductForAllUsers && enableAllProductForUser;
        if (useAllDefaultsForNewThread) {
            markTemplateApplySourceForNextSwitch("programmatic", "ALL");
            setProductIndexes({ manual_key: "0", faq_key: "0" });
            setProductText("ALL");
            setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
            setSubProductText("ALL");
            setProductVersionIndexes({ manual_version_key: "0", faq_version_key: "0" });
            setProductVersionText("ALL");
        }
        if (
            (selectedAppType === "Manuals" && productIndexes?.manual_key === "" && productIndexes?.faq_key !== "") ||
            (selectedAppType === "FAQ" && productIndexes?.manual_key !== "" && productIndexes?.faq_key === "") ||
            selectedAppType === "ALL"
        ) {
            const newAppType = getValidAppType(selectedAppType === "ALL" ? "ALL" : selectedAppType === "FAQ" ? "Manuals" : "FAQ");
            userInfo.apptype = newAppType;
            setSelectedAppType(newAppType);
        }
    };

    const showWarning = () => {
        localStorage.setItem("show_warning", "false");
        setWarningShown(false);
        if (
            (userInfo.answerTourDone === "false" && userInfo.answerTabTourDone === "false") ||
            (userInfo.answerTourDone === "true" && userInfo.answerTabTourDone === "false")
        ) {
            setActivateAnswerTour(true);
        } else {
            setActivateAnswerTour(false);
        }
    };

    const updateModelData = () => {
        const storedModel = userInfo?.aimodel;
        if (isKnownChatModel(storedModel)) {
            setChatModel(storedModel);
        }
    };

    const handleResize = () => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight
        });
    };

    const handleSharedChat = async () => {
        const queryParams = parseQueryParams(location.search);
        const sessionValue = queryParams.SessionId;
        setSessionId(sessionValue);
        if (sessionValue != null && sessionValue != undefined && sessionValue != "") {
            try {
                await loadSharedChat(sessionValue);
            } catch (error) {
                console.error("Error loading shared chat:", error);
            }
        } else {
            clearChat();
        }
    };

    useEffect(() => {
        if (localStorage.getItem("private_warning") === null) {
            localStorage.setItem("private_warning", "false");
        }
        if (localStorage.getItem("show_warning") === null) {
            localStorage.setItem("show_warning", "true");
        }
        if (!localStorage.getItem("disclaimerHide")) {
            localStorage.setItem("disclaimerHide", "initial");
        }
        if (userInfo?.docValue) {
            setDocTypeFilter(userInfo?.docValue);
        }
        // When ALL mode is active, always force ALL regardless of saved userInfo.product.
        // This ensures retained product from a previous session does not override ALL mode.
        const useAllDefaultsOnMount = isAdmin ? enableAllProductForUser : enableAllProductForAllUsers && enableAllProductForUser;
        if (useAllDefaultsOnMount) {
            markTemplateApplySourceForNextSwitch("programmatic", "ALL");
            setProductIndexes({ manual_key: "0", faq_key: "0" });
            setProductText("ALL");
            setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
            setSubProductText("ALL");
            setProductVersionIndexes({ manual_version_key: "0", faq_version_key: "0" });
            setProductVersionText("ALL");
        } else {
            setDropDownFilterText(userInfo?.product, userInfo.productVersion, userInfo?.subProduct);
        }
        if (shouldDisableInputBox(resolvedProductLocale, appInfo) === false) {
            setApplyRestriction(true);
        }
        updateModelData();
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
        setnewChatQuestion("");
        // Initialize async functions
        const initializeAsync = async () => {
            try {
                await Promise.all([await loadSessionData(""), loadProductFilter(), loadDocCategories()]);
            } catch (error) {
                console.error("Error during initialization:", error);
            }
        };
        initializeAsync();
        handleSharedChat();
        const appTypeValue = getAppTypeBasedOnSelectedValueOnProductFilter();
        userInfo.apptype = appTypeValue;
        setSelectedAppType(appTypeValue);

        const wasClosed = sessionStorage.getItem("wasClosed");
        const wasRefreshed = sessionStorage.getItem("wasRefreshed");
        if (wasClosed && !wasRefreshed) {
            // Tab was closed, remove item from localStorage
            localStorage.removeItem("disclaimerHide");
        }
        // Cleanup session storage keys after checking
        sessionStorage.removeItem("wasRefreshed");
        sessionStorage.removeItem("wasClosed");

        const redirectData = localStorage.getItem("redirectAfterLogin");
        if (redirectData) {
            const { pathname, searchParams } = JSON.parse(redirectData);
            // Construct proper URL
            const redirectUrl = pathname + (Object.keys(searchParams).length ? "?" + new URLSearchParams(searchParams).toString() : "");
            localStorage.removeItem("redirectAfterLogin"); // Clear after reading
            navigate(redirectUrl); // Proper redirection
        }

        window.addEventListener("resize", handleResize);
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", handleResize);
            updateAppTypeValueInSessionStorage(appTypeValue);
        };
    }, []);

    useEffect(() => {
        if (answerAreaRef.current) {
            answerAreaRef.current.scrollTop = answerAreaRef.current.scrollHeight;
        }
    }, [isLoading]);

    useEffect(() => {
        if (answerAreaRef.current) {
            answerAreaRef.current.scrollTop = answerAreaRef.current.scrollHeight;
        }
    }, [answers]);

    useEffect(() => {
        const queryParams = parseQueryParams(location.search);
        if (props.executeClear && Object.keys(queryParams).length === 0) {
            clearChat();
            navigate("/assistant/chat");
            props.callback();
        }
    }, [props.executeClear]);

    const loadSharedChat = async (sessionvalue: string) => {
        sessionStorage.setItem("sessionId", sessionvalue);
        try {
            // Validating access via /load-shared-chat.
            // This call also returns the full chat data so we can avoid a second '/load-chat' round-trip if access is granted.
            const response = await loadSharedChatApi(sessionvalue, userInfo.corp_id, "chat");
            const result = await response.json();

            if (result != null && result != undefined && result != "" && result.answers.length > 0) {
                setIsSessionNotFound(false);
                const isOtherUser = userName !== result.options.userId;
                // Determined whether this chat is being opened as a shared view (i.e. the requesting user is not the original owner).
                setIsChatShared(isOtherUser);
                const isAccessAllowed = result.options.shared_chat === true || !isOtherUser;
                if (isAccessAllowed) {
                    // Passing the already-fetched result directly to loadChatData so it does not make a redundant /load-chat API call.
                    await loadChatData(sessionvalue, false, false, result);
                    setCurrentIndex(sessionvalue ?? "");
                } else {
                    clearChat();
                }
            } else {
                setIsSessionNotFound(true);
                clearChat();
            }
        } catch (error) {
            console.error("Error fetching shared chat data:", error);
        }
    };

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue ?? "");
    };

    const onAgenticRetrievalOutputSizeChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setAgenticRetrievalOutputSize(parseAgenticRetrievalOutputSizeInput(newValue));
    };

    const onAgenticRetrievalOutputSizeBlur = (ev?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextValue = ev?.currentTarget?.value ?? agenticRetrievalOutputSize;
        setAgenticRetrievalOutputSize(normalizeAgenticRetrievalOutputSize(nextValue));
    };

    const onAgenticRetrievalDocumentCountChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setAgenticRetrievalDocumentCount(parseAgenticRetrievalDocumentCountInput(newValue));
    };

    const onAgenticRetrievalDocumentCountBlur = (ev?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextValue = ev?.currentTarget?.value ?? agenticRetrievalDocumentCount;
        setAgenticRetrievalDocumentCount(normalizeAgenticRetrievalDocumentCount(nextValue));
    };

    const onAgenticRetrievalSourceDocumentCountChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setAgenticRetrievalSourceDocumentCount(parseAgenticRetrievalDocumentCountInput(newValue));
    };

    const onAgenticRetrievalSourceDocumentCountBlur = (ev?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextValue = ev?.currentTarget?.value ?? agenticRetrievalSourceDocumentCount;
        setAgenticRetrievalSourceDocumentCount(normalizeAgenticRetrievalSourceDocumentCount(nextValue));
    };

    // Use custom hook for settings handlers
    const {
        onRetrieveCountChange,
        onChatModelChange,
        onOnlyCitationsChange,
        onRetrievalModeChange,
        onUseSemanticRankerChange,
        onUseSemanticCaptionsChange,
        onShouldStreamChange,
        onExcludeCategoryChanged,
        onUseSuggestFollowupQuestionsChange,
        onUseOidSecurityFilterChange,
        onUseGroupsSecurityFilterChange,
        onReasoningEffortChange
    } = useSettingsHandlers({
        setRetrieveCount,
        setChatModel,
        setOnlyCitations,
        setShouldStream,
        setUseSuggestFollowupQuestions,
        setRetrievalMode,
        setUseSemanticRanker,
        setUseSemanticCaptions,
        setExcludeCategory,
        setUseOidSecurityFilter,
        setUseGroupsSecurityFilter,
        onReasoningEffortChange: setReasoningEffort,
        reasoningEffort
    });
    const onCosmosDBRetrievalModeChange = useCallback((_ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<RetrievalMode> | undefined) => {
        setCosmosDBRetrievalMode(option?.data ?? RetrievalMode.Vectors);
    }, []);

    const onRetrievalReasoningEffortChange = useCallback(
        (_ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<RetrievalReasoningEffort> | undefined) => {
            setRetrievalReasoningEffort(option?.data ?? RetrievalReasoningEffort.Low);
        },
        []
    );

    const onUseAgenticKnowledgebaseChange = useCallback((_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseAgenticKnowledgebase(!!checked);
    }, []);

    const onEnableAllProductForAllUsersChange = useCallback(async (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        const newValue = !!checked;
        setEnableAllProductForAllUsers(newValue);
        // Guard the personal-flag effect so it doesn't double-handle this change.
        isPersonalFlagChangedByGlobalRef.current = true;
        // Sync the personal checkbox to the same value as the global flag
        setEnableAllProductForUser(newValue);
        try {
            await saveAppSettings(JSON.stringify({ enable_all_product_for_all_users: newValue }));
            await saveUserSettings({ enable_all_product: newValue });
        } catch (error) {
            console.error("Failed to save enable_all_product_for_all_users setting:", error);
        }
    }, []);

    const onEnableAllProductForUserChange = useCallback(async (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        const newValue = !!checked;
        setEnableAllProductForUser(newValue);
        try {
            await saveUserSettings({ enable_all_product: newValue });
        } catch (error) {
            console.error("Failed to save enable_all_product user setting:", error);
        }
    }, []);

    const clampRuntimeThreshold = (value: unknown, fallback: number, min: number, max: number): number => {
        const parsedValue = Number(value);
        if (!Number.isFinite(parsedValue)) {
            return fallback;
        }
        return Math.min(max, Math.max(min, Math.round(parsedValue)));
    };

    const clampRuntimeWeight = (value: unknown, fallback: number): number => {
        const parsedValue = Number(value);
        if (!Number.isFinite(parsedValue)) {
            return fallback;
        }
        const clamped = Math.min(1, Math.max(0, parsedValue));
        return Number(clamped.toFixed(4));
    };

    const clampRuntimeDecimalThreshold = (value: unknown, fallback: number, min: number, max: number): number => {
        const parsedValue = Number(value);
        if (!Number.isFinite(parsedValue)) {
            return fallback;
        }
        const clamped = Math.min(max, Math.max(min, parsedValue));
        return Number(clamped.toFixed(4));
    };

    const onAllProductAutoSelectEnabledChange = useCallback((_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setAllProductAutoSelectEnabled(checked !== false);
    }, []);

    const onAllProductAutoSelectMinConfidenceChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectMinConfidence(previousValue => clampRuntimeThreshold(newValue, previousValue, 0, 100));
    }, []);

    const onAllProductAutoSelectMinMarginChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectMinMargin(previousValue => clampRuntimeThreshold(newValue, previousValue, 0, 100));
    }, []);

    const onAllProductAutoSelectMinHitsChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectMinHits(previousValue => clampRuntimeThreshold(newValue, previousValue, 1, 99));
    }, []);

    const onAllProductAutoSelectMinAvgRerankerScoreChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectMinAvgRerankerScore(previousValue => clampRuntimeDecimalThreshold(newValue, previousValue, 0, 10));
    }, []);

    const onAllProductAutoSelectMinAvgRerankerGapChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectMinAvgRerankerGap(previousValue => clampRuntimeDecimalThreshold(newValue, previousValue, 0, 10));
    }, []);

    const onAllProductAutoSelectWeightAverageChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectWeightAverage(previousValue => clampRuntimeWeight(newValue, previousValue));
    }, []);

    const onAllProductAutoSelectWeightPeakChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectWeightPeak(previousValue => clampRuntimeWeight(newValue, previousValue));
    }, []);

    const onAllProductAutoSelectWeightVolumeChange = useCallback((_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setAllProductAutoSelectWeightVolume(previousValue => clampRuntimeWeight(newValue, previousValue));
    }, []);

    const hasNonAttachmentCitations = (citations?: string[]) => (citations ?? []).some(citation => !citation.startsWith("attachment:"));

    const getAnalysisAppTypeForAnswer = (answerEntry: any): AppType => {
        if (answerEntry?.[1] && Object.keys(answerEntry[1]).length === 1 && answerEntry[1]["ALL"] && appInfo.use_all_tab === true) {
            return isWapUser ? selectedAppType : "ALL";
        }
        if (selectedAppType !== "ALL") {
            return selectedAppType;
        }
        if (isWapUser) {
            return selectedAppType;
        }
        return appInfo.use_all_tab === true ? "ALL" : "FAQ";
    };

    const hasNonAttachmentCitationsForAnswer = (answer: ChatAppResponse | undefined, appType: AppType): boolean => {
        const messageContent = typeof answer?.output_text === "string" ? answer.output_text : "";
        if (!messageContent) return false;
        const dataPoints = answer?.context?.data_points ?? [];
        const fileMapping = answer?.context?.file_mapping;
        const parsed = parseAnswerToHtml(messageContent, false, () => undefined, dataPoints, fileMapping, appType, "Chat");
        return hasNonAttachmentCitations(parsed.citations);
    };

    const hasNonAttachmentCitationsForContent = (messageContent: string, dataPoints: DataPoints[], fileMapping: any, appType: AppType): boolean => {
        if (!messageContent) return false;
        const parsed = parseAnswerToHtml(messageContent, false, () => undefined, dataPoints ?? [], fileMapping, appType, "Chat");
        return hasNonAttachmentCitations(parsed.citations);
    };

    const onShowCitation = (citation: string, citationIndex: number, index: number) => {
        setActiveCitation(citation);
        setActiveAnalysisPanelTab(AnalysisPanelTabs.SupportingContentTab);
        setSelectedAnswer(index);
        setSelectedCitation(citationIndex);
    };

    const onToggleTab = async (tab: AnalysisPanelTabs, index: number) => {
        currentTargetQuestionId.current = index;
        if (tab === AnalysisPanelTabs.SupportingContentTab) {
            const answerEntry = answers[index];
            const appTypeForPanel = getAnalysisAppTypeForAnswer(answerEntry);
            const answerForType = answerEntry?.[1]?.[appTypeForPanel];
            if (!hasNonAttachmentCitationsForAnswer(answerForType, appTypeForPanel)) {
                return;
            }
        }

        if (tab === AnalysisPanelTabs.ThoughtProcessTab && !(activeAnalysisPanelTab === tab && selectedAnswer === index)) {
            const answerEntry = answers[index];
            const appTypeForPanel = getAnalysisAppTypeForAnswer(answerEntry);
            const answerForType = answerEntry?.[1]?.[appTypeForPanel];
            if (answerForType?.context?.thoughts === undefined) {
                setSelectedCitation(0);
                const fetched = await loadThoughts(index, appTypeForPanel, answerForType);
                if (fetched) return;
            }
        }

        setSelectedCitation(0);
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const tabstyles = useStyles();

    // Feature flag: Set to true to enable Azure AD document-level access control
    // Requires AZURE_USE_AUTHENTICATION env var and Azure AD app setup
    // See LoginAndAclSetup.md for configuration details
    const hidetoken = false;

    const handleClickOutside = (event: { target: any }) => {
        if (mainDropdownRef.current && !mainDropdownRef.current.contains(event.target)) {
            setIsMainDropdownOpen(false);
            setIsSubDropdownOpen(false);
            setIsVersionsOpen(false);
        }
    };

    const templateContents = useSelector(selectTemplateContents);
    const selectedProduct = useSelector(selectSelectedProduct);
    const latestSelectedProductRef = useRef(selectedProduct);
    latestSelectedProductRef.current = selectedProduct;

    useEffect(() => {
        if (!productText || productText === `${i18next.t("chat.productFilterText")}`) {
            return;
        }

        let active = true;

        const loadTemplatesForSelectedProduct = async () => {
            try {
                const response = await loadTemplate(productText);
                if (!response || typeof response.json !== "function" || response.ok === false) {
                    throw new Error("Invalid template load response");
                }
                const payload = await parseTemplatePayload(response);
                const payloadError = extractPayloadError(payload);
                if (payloadError) {
                    throw new Error(payloadError);
                }
                if (hasTemplateReadFailure(payload)) {
                    throw new Error("Template read failed");
                }

                if (!active) {
                    return;
                }

                syncTemplatePayloadToStore(payload, productText, dispatch);
            } catch (error) {
                console.error("Error loading templates for selected product: ", error);
                if (active) {
                    const latestSelectedProduct = latestSelectedProductRef.current;
                    if (latestSelectedProduct !== productText) {
                        dispatch(setTemplateContentsInStore([]));
                        dispatch(setSelectedProductInStore(productText));
                    }
                }
            }
        };

        loadTemplatesForSelectedProduct();

        return () => {
            active = false;
        };
    }, [dispatch, productText]);

    useEffect(() => {
        const userDefaultTemplate = getUserDefaultContentTemplate(selectedProduct, userInfo.userFavourite, templateContents);
        const userDefaultContentValue = userDefaultTemplate?.template?.[0]?.previewPrompt ?? "";
        const systemDefaultTemplate = getSystemDefaultTemplate(selectedProduct, templateContents);

        dispatch(setUserDefaultContentInStore(userDefaultContentValue));
        dispatch(setSystemDefaultContentInStore(systemDefaultTemplate?.template?.[0]?.previewPrompt ?? ""));
        dispatch(setDefaultTemplateIdInStore(systemDefaultTemplate?.id ?? ""));
    }, [templateContents, selectedProduct]);

    useEffect(() => {
        if (!selectedProduct || selectedProduct === `${i18next.t("chat.productFilterText")}`) {
            return;
        }

        const hasSelectedProductChanged = !!previousTemplateSelectionProductRef.current && previousTemplateSelectionProductRef.current !== selectedProduct;
        const normalizeProductLabel = (value: unknown): string =>
            String(value ?? "")
                .trim()
                .toLowerCase();
        const currentUiProductLabel = normalizeProductLabel(productText || selectedProduct);

        if (suppressTemplateAutoApplyWhileRegenerateSelectionRef.current) {
            dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
            if (selectedTemplateContentId || selectedTemplateContent) {
                setSelectedTemplateContentId("");
                setSelectedTemplateContent("");
            }
            setClearedTemplateSelectionProduct(productText || selectedProduct);
            setChatContentCleared(true);
            return;
        }

        if (hasSelectedProductChanged) {
            const pendingSwitchSource = pendingTemplateApplySourceRef.current;
            const expectedProductLabel = normalizeProductLabel(pendingSwitchSource.product);
            const currentProductLabel = normalizeProductLabel(selectedProduct);
            const isManualDropdownSwitch =
                pendingSwitchSource.source === "manual-dropdown" && (!expectedProductLabel || expectedProductLabel === currentProductLabel);

            if (!isManualDropdownSwitch) {
                const isProgrammaticSwitch = pendingSwitchSource.source === "programmatic";
                // Programmatic switches are consumed by the productIndexes effect so
                // same-render selectedProduct/productIndexes updates stay suppressible.
                if (!isProgrammaticSwitch) {
                    pendingTemplateApplySourceRef.current = { source: null, product: null, manual_key: null, faq_key: null };
                }
                dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                setSelectedTemplateContentId("");
                setSelectedTemplateContent("");
                setClearedTemplateSelectionProduct(selectedProduct);
                setChatContentCleared(true);
                return;
            }

            pendingTemplateApplySourceRef.current = { source: null, product: null, manual_key: null, faq_key: null };
        }

        const isSuggestionTemplateSuppressedForCurrentProduct =
            !!suggestionDrivenTemplateSuppressedProduct && normalizeProductLabel(suggestionDrivenTemplateSuppressedProduct) === currentUiProductLabel;

        if (isSuggestionTemplateSuppressedForCurrentProduct) {
            return;
        }

        if (currentView === "history" && (isHistoryPageInitialLoading || !hasSelectedProductChanged)) {
            return;
        }

        const isTemplateClearSuppressed =
            normalizeProductLabel(clearedTemplateSelectionProduct) === currentUiProductLabel &&
            !selectedTemplateContentId &&
            !selectedTemplateContent &&
            !hasSelectedProductChanged &&
            !isNewChatThreadInitialLoading;

        const currentSelectedTemplateEntry =
            selectedTemplateContentId && templateContents.length > 0
                ? templateContents.find((tempContent: any) => tempContent.id === selectedTemplateContentId)
                : null;
        const currentTemplateSelectionStillExists = !!(currentSelectedTemplateEntry && hasUsableTemplateEntry(currentSelectedTemplateEntry));
        const { templateEntry, templateId } = resolveEffectiveTemplateForProduct(selectedProduct, userInfo.userFavourite, templateContents);

        if (isTemplateClearSuppressed) {
            return;
        }

        if (!templateEntry) {
            if (hasSelectedProductChanged || !currentTemplateSelectionStillExists) {
                dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                setSelectedTemplateContentId("");
                setSelectedTemplateContent("");
            }
            return;
        }

        const shouldApplyResolvedTemplate =
            !selectedTemplateContentId || !currentTemplateSelectionStillExists || (hasSelectedProductChanged && selectedTemplateContentId !== templateId);

        if (shouldApplyResolvedTemplate) {
            dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(true));
            setSelectedTemplateContentId(templateId);
            setSelectedTemplateContent(templateEntry?.template?.[0] ?? "");
        }
    }, [
        clearedTemplateSelectionProduct,
        selectedProduct,
        selectedTemplateContent,
        templateContents,
        selectedTemplateContentId,
        currentView,
        isHistoryPageInitialLoading,
        isNewChatThreadInitialLoading,
        suggestionDrivenTemplateSuppressedProduct,
        productText,
        userInfo["userFavourite"]?.[selectedProduct],
        userInfo["userFavourite"]?.["ALL"]
    ]);

    useEffect(() => {
        if (selectedTemplateContentId) {
            setClearedTemplateSelectionProduct(null);
        }
    }, [selectedTemplateContentId]);

    useEffect(() => {
        const normalizeProductLabel = (value: unknown): string =>
            String(value ?? "")
                .trim()
                .toLowerCase();

        if (clearedTemplateSelectionProduct) {
            const clearedSelectionLabel = normalizeProductLabel(clearedTemplateSelectionProduct);
            const currentSelectedProductLabel = normalizeProductLabel(selectedProduct);
            const currentUiProductLabel = normalizeProductLabel(productText);

            if (clearedSelectionLabel !== currentSelectedProductLabel && clearedSelectionLabel !== currentUiProductLabel) {
                setClearedTemplateSelectionProduct(null);
            }
        }
        previousTemplateSelectionProductRef.current = selectedProduct;
    }, [clearedTemplateSelectionProduct, productText, selectedProduct]);

    useEffect(() => {
        const normalizeProductLabel = (value: unknown): string =>
            String(value ?? "")
                .trim()
                .toLowerCase();

        if (suggestionDrivenTemplateSuppressedProduct) {
            const suppressedProductLabel = normalizeProductLabel(suggestionDrivenTemplateSuppressedProduct);
            const currentSelectedProductLabel = normalizeProductLabel(selectedProduct);
            const currentUiProductLabel = normalizeProductLabel(productText);

            if (suppressedProductLabel !== currentSelectedProductLabel && suppressedProductLabel !== currentUiProductLabel) {
                setSuggestionDrivenTemplateSuppressedProduct(null);
            }
        }
    }, [productText, selectedProduct, suggestionDrivenTemplateSuppressedProduct]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Cleanup version preservation on component unmount (tab close, navigation away)
    useEffect(() => {
        return () => {
            clearVersionIndices();
        };
    }, [clearVersionIndices]);

    const [isHovered, setIsHovered] = useState(-1);
    const [isSubHovered, setIsSubHovered] = useState(-1);
    const [isVersionsHovered, setIsVersionsHovered] = useState(-1);

    const answerAreaRef = useRef<HTMLDivElement>(null);
    const chatResponseAreaRefValue = useRef<HTMLDivElement | null>(null);
    const answersRefs = useRef<(HTMLDivElement | null)[]>([]);
    const streamedAnswersRefs = useRef<(HTMLDivElement | null)[]>([]);

    const answerAreaRefScrollPosition = useRef({
        scrollTop: 0,
        scrollHeight: 0
    });

    const scrollToBottom = () => {
        if (answerAreaRef.current) {
            answerAreaRef.current.scrollTop = answerAreaRef.current.scrollHeight;
        }
    };

    const scrollToSavedPosition = () => {
        // this method is to retain the scroll if no streaming is active
        if (answerAreaRef.current) {
            const targetIndexValue = currentTargetQuestionId.current;
            const scrollTopValue = answersRefs.current[targetIndexValue]
                ? answersRefs.current[targetIndexValue].offsetTop
                : answerAreaRefScrollPosition.current.scrollTop;
            answerAreaRef.current.scrollTop = scrollTopValue - 100;
        }
    };

    const scrollToAnswerWithActiveResponseGenerationIndex = () => {
        if (answerAreaRef.current && scrollToAnswer) {
            const targetIndexValue = currentTargetQuestionId.current;
            const questionWithAnswerHeights = Array.from(answerAreaRef.current.children).map(child => (child as HTMLElement).offsetHeight);
            const targetScrollTop = questionWithAnswerHeights.slice(0, targetIndexValue + 1).reduce((a, b) => a + b, 0);
            answerAreaRef.current.scrollTop = targetScrollTop - answerAreaRef.current.clientHeight + 50;
        }
    };

    useEffect(() => {
        if (answerAreaRef.current) {
            answerAreaRefScrollPosition.current = {
                scrollTop: answerAreaRef.current.scrollTop,
                scrollHeight: answerAreaRef.current.scrollHeight
            };
        }
    }, [answerAreaRef.current?.scrollTop, currentTargetQuestionId.current]);

    const setRefsForAnswersRender = (node: HTMLDivElement | null, index: number) => {
        chatResponseAreaRefValue.current = node;
        answersRefs.current[index] = node;
    };

    const setRefsForStreamedAnswersRender = (node: HTMLDivElement | null, index: number) => {
        chatResponseAreaRefValue.current = node;
        streamedAnswersRefs.current[index] = node;
    };

    useEffect(() => {
        if (answerAreaRef.current) {
            if (scrollToAnswer) {
                if (currentTargetQuestionId.current >= answers?.length) {
                    // means for 1st question & for every currently asked new question currentTargetQuestionId will be greater
                    //  before the streamed answers got updated so scroll to bottom.
                    scrollToBottom();
                } else if (0 <= currentTargetQuestionId.current && currentTargetQuestionId.current < answers?.length) {
                    // scroll to answer is true means -> streaming is active then maintain scroll along with the generation.
                    scrollToAnswerWithActiveResponseGenerationIndex();
                }
            } else {
                // no streaming -> means answers have updated for response generated.
                if (currentTargetQuestionId.current === 0) {
                    answerAreaRef.current.scrollTop = 0;
                    answerAreaRefScrollPosition.current = {
                        scrollTop: 0,
                        scrollHeight: answerAreaRef.current.scrollHeight
                    };
                } else if (currentTargetQuestionId.current === answers?.length - 1) {
                    if (currentView === "chat") {
                        scrollToSavedPosition();
                    } else {
                        // for history view load from side menu -> scroll to bottom since last answer the spec to select by default.
                        scrollToBottom();
                    }
                } else if (0 < currentTargetQuestionId.current && currentTargetQuestionId.current < answers?.length) {
                    // instead of scrolling to saved position -> scroll to current targeted generated response starting position.
                    scrollToSavedPosition();
                }
            }
        }
    }, [answers, isStreaming, error, isLoading, currentTargetQuestionId, scrollToAnswer, selectedAppType]);

    useEffect(() => {
        const isFilesAttached = serverSideFileAttachmentsIds.length > 0;
        if (!isAnswerLoading && !fileUploadOrDeleteAPITriggerActive.triggerActive && !isFilesAttached) return;

        // Prevent the user from reloading/closing the tab while the answer is loading
        function handleOnBeforeUnload(event: BeforeUnloadEvent) {
            if (isFilesAttached) {
                // if files Attached on question input auto delete trigger to remove recently uploaded files from storage container.
                deleteFileOnUnload(serverSideFileAttachmentsIds);
            } else {
                sessionStorage.setItem("wasRefreshed", "true");
                event.preventDefault();
                return (event.returnValue = "");
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                sessionStorage.setItem("wasClosed", "true"); // Mark tab as closing
            }
        };

        window.addEventListener("beforeunload", handleOnBeforeUnload);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            window.removeEventListener("beforeunload", handleOnBeforeUnload);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isAnswerLoading, fileUploadOrDeleteAPITriggerActive, serverSideFileAttachmentsIds]);

    useEffect(() => {
        const pendingSwitch = pendingTemplateApplySourceRef.current;
        const pendingSwitchSource = pendingSwitch.source;
        const normalizeProductLabel = (value: unknown): string =>
            String(value ?? "")
                .trim()
                .toLowerCase();
        const currentProductSelectionLabel = normalizeProductLabel(productText || selectedProduct);
        const isSameProduct = normalizeProductLabel(pendingSwitch.product) === currentProductSelectionLabel;
        const pendingManualKey = normalizeComparableProductKey(pendingSwitch.manual_key);
        const pendingFaqKey = normalizeComparableProductKey(pendingSwitch.faq_key);
        const currentManualKey = normalizeComparableProductKey(productIndexes.manual_key);
        const currentFaqKey = normalizeComparableProductKey(productIndexes.faq_key);
        const hasPendingKeyMarker = !!(pendingManualKey || pendingFaqKey);
        const isSameProductByKeys =
            hasPendingKeyMarker &&
            ((!!pendingManualKey && !!currentManualKey && pendingManualKey === currentManualKey) ||
                (!!pendingFaqKey && !!currentFaqKey && pendingFaqKey === currentFaqKey));
        const shouldSuppressTemplateHydration = pendingSwitchSource === "programmatic" && (isSameProduct || (!!pendingSwitch.product && isSameProductByKeys));

        // Consume one-shot marker for programmatic switches when indexes update
        // within the same selected product before selectedProduct catches up.
        if (pendingSwitchSource === "programmatic" && (!pendingSwitch.product || isSameProduct || isSameProductByKeys)) {
            pendingTemplateApplySourceRef.current = { source: null, product: null, manual_key: null, faq_key: null };
        }

        resetTemplateSelection(shouldSuppressTemplateHydration, productText || selectedProduct || null);
        setChatContentCleared(shouldSuppressTemplateHydration);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed to productIndexes updates only
    }, [productIndexes]);

    useEffect(() => {
        lastQuestionIndexValue.current = answers?.length;

        if (answerAreaRef.current) {
            answerAreaRef.current.addEventListener("scroll", handleAnswerAreaRefScroll);
        }

        return () => {
            if (answerAreaRef.current) {
                answerAreaRef.current.removeEventListener("scroll", handleAnswerAreaRefScroll);
            }
        };
    }, [answers, isStreaming, error, isLoading]);

    useEffect(() => {
        const requestAbortStage = store.getState().questionInput.abortStageLevel;
        if (requestedToForceStopAIResponseGeneration && makeApiRequestControllerRef.current && requestAbortStage && requestAbortStage === "RequestInitiated") {
            makeApiRequestControllerRef.current.abort("Abort Active Chat API Request");
        }
    }, [requestedToForceStopAIResponseGeneration]);

    const handleAnswerAreaRefScroll = (event: any) => {
        if (answerAreaRef.current && !isStreaming) {
            const { scrollTop, scrollHeight } = event.target;
            answerAreaRefScrollPosition.current = {
                scrollTop: scrollTop,
                scrollHeight: scrollHeight
            };
        } else {
            event.preventDefault();
        }
    };

    const clearWarning = () => {
        setEmptyProduct(false);
    };

    const handleJoyrideCallback = (data: any) => {
        const { status } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            endTourOne();
        }
    };

    // Template management hook
    const {
        handleTemplateIconClick,
        handleTemplateClick,
        handleTemplateContents: applyTemplateContents,
        handleTemplateContentsBySuggestions: applyTemplateContentsBySuggestions,
        handleClearTemplateContent,
        getTemplateDetailsForQuestionEdit
    } = useTemplateManagement({
        setHideDynamicTemplatePopup,
        setSelectedTemplate,
        setSelectedTemplateContent,
        setSelectedTemplateContentId,
        setTemplateKeywords,
        setIsHistoryPageInitialLoading,
        setIsNewChatThreadInitialLoading,
        questionInputRefs,
        currentView,
        QuestionInputReducerAction,
        templateContents
    });

    const resetTemplateSelection = useCallback(
        (suppressAutoApplyForCurrentProduct = false, suppressedProductLabel?: string | null) => {
            const productForSuppression = suppressedProductLabel !== undefined ? suppressedProductLabel : selectedProduct || null;
            setClearedTemplateSelectionProduct(suppressAutoApplyForCurrentProduct ? productForSuppression : null);
            handleClearTemplateContent();
        },
        [handleClearTemplateContent, selectedProduct]
    );

    const handleTemplateContentsBySuggestions = useCallback(
        (contents: any, tempId: string) => {
            setClearedTemplateSelectionProduct(null);
            setSuggestionDrivenTemplateSuppressedProduct(null);
            applyTemplateContentsBySuggestions(contents, tempId);
        },
        [applyTemplateContentsBySuggestions]
    );

    const handleTemplateContents = useCallback(
        (contents: any, tempId: string) => {
            setClearedTemplateSelectionProduct(null);
            setSuggestionDrivenTemplateSuppressedProduct(null);
            applyTemplateContents(contents, tempId);
        },
        [applyTemplateContents]
    );

    interface TourState {
        run: boolean;
        steps: Step[];
    }

    const handleTopPosition = (divReference: any, index: number, itemsLength: number) => {
        const hoveredDiv = divReference.current[index];
        if (hoveredDiv) {
            if (hoveredDiv.getBoundingClientRect().top + itemsLength * 33 > window.innerHeight) {
                const remHeight = window.innerHeight - 200 - hoveredDiv.getBoundingClientRect().top;
                if (itemsLength * 33 > window.innerHeight - 200 || (itemsLength * 33 > window.innerHeight - 200 && (itemsLength * 33) / 2 > remHeight)) {
                    return 0;
                }

                if (itemsLength * 33 < window.innerHeight) {
                    if (hoveredDiv.getBoundingClientRect().top - (itemsLength * 33 - remHeight) - 100 > 45)
                        return hoveredDiv.getBoundingClientRect().top - (itemsLength * 33 - remHeight);
                }
                return 0;
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    };

    const proceedAppTourForAnswer = useCallback(() => {
        if (lastQuestionIndexValue.current === 0) {
            // when no answer present end the over all tour.
            props.startTour(false);
        } else {
            // when some answers available already & tour is reactivated
            // then target the last question always if single or multiple question exists for answer chat & answer tab tour.
            const isAnswerPrivateDlgActive = localStorage.getItem("private_warning") === "true" && localStorage.getItem("show_warning") === "true";
            setActivateAnswerTour(isAnswerPrivateDlgActive ? false : true);
        }
    }, [props.startTour, setActivateAnswerTour]);

    const handleSnackBarClose = (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
        if (reason === "clickaway") {
            return;
        }

        setIsSnackOpen(false);
    };

    const handleSnackBar = () => {
        setIsSnackOpen(!isSnackOpen);
    };

    const clearExternalQuestionInput = useCallback((mode: "chat" | "history") => {
        const inputRef = questionInputRefs.current[mode];
        if (!inputRef) {
            return;
        }
        if (typeof inputRef.clearAfterExternalSend === "function") {
            inputRef.clearAfterExternalSend();
            return;
        }
        // inputRef.setQuestionInputValue?.("");
    }, []);

    const handleClarificationSendAnyway = useCallback(() => {
        const ctx = clarificationSendContextRef.current;
        const questionToSend = clarificationOriginalQuestion.trim();
        setClarificationMode(null);
        skipValidationOnceRef.current = true;
        if (ctx?.appType === "ALL" && questionToSend) {
            questionInputRefs.current[ctx.mode]?.setQuestionInputValue(questionToSend);
            questionInputRefs.current[ctx.mode]?.triggerSendWithQuestion(questionToSend);
        } else {
            if (ctx) {
                clearExternalQuestionInput(ctx.mode);
            }
            pendingQuestionSendRef.current?.();
        }
        pendingQuestionSendRef.current = null;
        clarificationSendContextRef.current = null;
        setClarificationInfo(null);
        setClarificationOriginalQuestion("");
    }, [clearExternalQuestionInput, clarificationOriginalQuestion]);

    const handleClarificationDismiss = useCallback(
        (skipValidation: boolean = false, softDismiss: boolean = false) => {
            const mode = clarificationSendContextRef.current?.mode ?? "chat";
            const questionToRestore = clarificationOriginalQuestion.trim();
            setClarificationMode(null);
            setClarificationInfo(null);
            setClarificationOriginalQuestion("");
            pendingQuestionSendRef.current = null;
            clarificationSendContextRef.current = null;
            skipValidationOnceRef.current = skipValidation ?? false;
            precheckResultCacheRef.current = null;
            if (!softDismiss && (answers.length === 0 || (answers?.length !== 0 && isChatShared))) {
                // First question: clear the speculative session entry, then restore the question text
                const cancelledSessionId = sessionStorage.getItem("sessionId") || selectedSessionId;
                if (answers.length === 0) clearChat();
                if (cancelledSessionId) {
                    setTodaySessionsRef(prev => prev.filter(item => item.session_id !== cancelledSessionId));
                }
                if (questionToRestore) {
                    setTimeout(() => {
                        questionInputRefs.current[mode]?.setQuestionInputValue(questionToRestore);
                        dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                    }, 500);
                }
            } else if (!softDismiss && questionToRestore) {
                questionInputRefs.current[mode]?.setQuestionInputValue(questionToRestore);
            }
        },
        [answers.length, clarificationOriginalQuestion, selectedSessionId, clearChat, setTodaySessionsRef, dispatch]
    );

    const handle206ErrorDialogConfirm = () => {
        // Close the dialog
        setShow206ErrorDialog(false);

        // Store the question to be set after clearing
        const questionToSet = error206Question;

        // Clean up any uploaded attachments before clearing state
        if (serverSideFileAttachmentsIds && serverSideFileAttachmentsIds.length > 0) {
            deleteFile(serverSideFileAttachmentsIds);
        }

        // Clear the current chat and create a new thread
        clearChat(true);

        // Set the question from the error to the new question input after a short delay
        // to ensure the component has been cleared and re-rendered
        setTimeout(() => {
            if (questionToSet && questionInputRefs?.current?.["chat"]) {
                questionInputRefs.current["chat"]?.setQuestionInputValue(questionToSet);
                // Explicitly ensure template flag is false after setting the question
                dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
            }
        }, 500);

        // Clear error states
        setError206Message("");
        setError206Question("");
    };

    const handleDeleteSession = async (sessionId: string) => {
        const prevToday = [...todaySessionsRef];
        const prev7 = [...last7SessionsRef];
        const prev30 = [...last30SessionsRef];
        const prevMonthly = [...monthlySessions];
        setTodaySessionsRef(prevToday.filter(item => item.session_id !== sessionId));
        setLast7SessionsRef(prev7.filter(item => item.session_id !== sessionId));
        setLast30SessionsRef(prev30.filter(item => item.session_id !== sessionId));
        setMonthlySessions(prevMonthly =>
            prevMonthly.map(month => ({
                ...month,
                sessions: month.sessions.map(s => ({
                    ...s,
                    records: s.records.filter(item => item.session_id !== sessionId)
                }))
            }))
        );
    };

    const handleProductClick = (item: Product) => {
        setClarificationInfo(null);
        setClarificationMode(null);
        setClarificationOriginalQuestion("");
        clarificationSendContextRef.current = null;
        pendingQuestionSendRef.current = null;
        setIsHistoryPageInitialLoading(false);
        setIsNewChatThreadInitialLoading?.(false);
        dispatch(QuestionInputReducerAction.resetQuestionInputHistoryStacks());
        markTemplateApplySourceForNextSwitch("manual-dropdown", item.text);
        if (appInfo?.default_manual_products?.some((key: string) => key === item?.manual_key || key === item?.faq_key)) {
            setSelectedAppType("Manuals");
        } else {
            const appTypeValue = userInfo.apptype === "ALL" ? "ALL" : userInfo.apptype === "FAQ" ? "FAQ" : item?.manual_key === "" ? "ALL" : "Manuals";
            setSelectedAppType(appTypeValue);
        }
        setProductIndexes({
            manual_key: item?.manual_key?.toString() ?? "",
            faq_key: item?.faq_key?.toString() ?? ""
        });
        setProductText(item.text);
        const isVersionAvailable = item?.versions?.length > 0;
        const isSubProductsOnly = item?.is_sub_only === "true";
        let versionValue = {
            manual_version_key: "",
            faq_version_key: ""
        };
        let subVersionValue = {
            manual_sub_key: "",
            faq_sub_key: ""
        };

        if (isSubProductsOnly) {
            subVersionValue = {
                manual_sub_key: "0",
                faq_sub_key: "0"
            };
            setProductVersionText("");
            setSubProductText("ALL");
        } else if (isVersionAvailable && !isSubProductsOnly) {
            versionValue = {
                manual_version_key: "0",
                faq_version_key: "0"
            };
            subVersionValue = {
                manual_sub_key: "0",
                faq_sub_key: "0"
            };
            setProductVersionText("ALL");
            setSubProductText("ALL");
        } else {
            setProductVersionText("");
            setSubProductText("");
        }
        setProductVersionIndexes(versionValue);
        setSubProductIndexes(subVersionValue);
        userInfo.product = {
            manual_key: item?.manual_key?.toString() ?? "",
            faq_key: item?.faq_key?.toString() ?? ""
        };
        saveUserSettings({
            product: {
                manual_key: item?.manual_key?.toString() ?? "",
                faq_key: item?.faq_key?.toString() ?? ""
            },
            productVersion: versionValue,
            subProduct: subVersionValue
        });
        handleCloseDropDown();
    };

    const handleVersionClick = (item: ProductVersion, lastHoveredRef?: Product) => {
        if (!lastHoveredRef) return;
        setClarificationInfo(null);
        setClarificationMode(null);
        setClarificationOriginalQuestion("");
        clarificationSendContextRef.current = null;
        pendingQuestionSendRef.current = null;
        setIsHistoryPageInitialLoading(false);
        setIsNewChatThreadInitialLoading?.(false);
        markTemplateApplySourceForNextSwitch("manual-dropdown", lastHoveredRef?.text ?? null);
        setProductIndexes({
            manual_key: lastHoveredRef?.manual_key?.toString() ?? "",
            faq_key: lastHoveredRef?.faq_key?.toString() ?? ""
        });
        setProductText(lastHoveredRef?.text);
        const isSubProductsOnly = lastHoveredRef?.is_sub_only === "true";
        if (isSubProductsOnly) {
            setProductVersionIndexes({
                manual_version_key: "",
                faq_version_key: ""
            });
            setProductVersionText("");
            setSubProductIndexes({
                manual_sub_key: item?.manual_version_key?.toString(),
                faq_sub_key: item?.faq_version_key?.toString()
            });
            setSubProductText(item?.text);
        } else {
            setProductVersionIndexes({
                manual_version_key: item?.manual_version_key?.toString() ?? "",
                faq_version_key: item?.faq_version_key?.toString() ?? ""
            });
            setProductVersionText(item?.text);
            setSubProductIndexes({
                manual_sub_key: "0",
                faq_sub_key: "0"
            });
            setSubProductText("ALL");
        }

        saveUserSettings({
            product: {
                manual_key: lastHoveredRef?.manual_key?.toString() ?? "",
                faq_key: lastHoveredRef?.faq_key?.toString() ?? ""
            },
            productVersion:
                isSubProductsOnly === true
                    ? {
                          manual_version_key: "",
                          faq_version_key: ""
                      }
                    : {
                          manual_version_key: item?.manual_version_key?.toString() ?? "",
                          faq_version_key: item?.faq_version_key?.toString() ?? ""
                      },
            subProduct:
                isSubProductsOnly === true
                    ? {
                          manual_sub_key: item?.manual_version_key?.toString() ?? "",
                          faq_sub_key: item?.faq_version_key?.toString() ?? ""
                      }
                    : {
                          manual_sub_key: "0",
                          faq_sub_key: "0"
                      }
        });
        handleCloseDropDown();
    };

    const handleSubProductClick = (item: SubProduct, isFromSubOnly: boolean, lastHoveredRef?: Product, lastVerHoveredRef?: ProductVersion) => {
        if (!lastHoveredRef) return;
        if (!isFromSubOnly && !lastVerHoveredRef) return;
        setClarificationInfo(null);
        setClarificationMode(null);
        setClarificationOriginalQuestion("");
        clarificationSendContextRef.current = null;
        pendingQuestionSendRef.current = null;
        setIsHistoryPageInitialLoading(false);
        setIsNewChatThreadInitialLoading?.(false);
        markTemplateApplySourceForNextSwitch("manual-dropdown", lastHoveredRef?.text ?? null);
        setProductIndexes({
            manual_key: lastHoveredRef?.manual_key?.toString() ?? "",
            faq_key: lastHoveredRef?.faq_key?.toString() ?? ""
        });
        setProductText(lastHoveredRef?.text);
        if (isFromSubOnly) {
            setProductVersionIndexes({
                manual_version_key: "",
                faq_version_key: ""
            });
            setProductVersionText("");
        } else {
            setProductVersionIndexes({
                manual_version_key: lastVerHoveredRef?.manual_version_key?.toString() ?? "",
                faq_version_key: lastVerHoveredRef?.faq_version_key?.toString() ?? ""
            });
            setProductVersionText(lastVerHoveredRef?.text ?? "");
        }
        setSubProductIndexes({
            manual_sub_key: item?.manual_sub_key?.toString() ?? "",
            faq_sub_key: item?.faq_sub_key?.toString() ?? ""
        });
        setSubProductText(item.text);
        saveUserSettings({
            product: {
                manual_key: lastHoveredRef?.manual_key?.toString() ?? "",
                faq_key: lastHoveredRef?.faq_key?.toString() ?? ""
            },
            productVersion:
                isFromSubOnly === true
                    ? {
                          manual_version_key: "",
                          faq_version_key: ""
                      }
                    : {
                          manual_version_key: lastVerHoveredRef?.manual_version_key?.toString() ?? "",
                          faq_version_key: lastVerHoveredRef?.faq_version_key?.toString() ?? ""
                      },
            subProduct: {
                manual_sub_key: item?.manual_sub_key?.toString() ?? "",
                faq_sub_key: item?.faq_sub_key?.toString() ?? ""
            }
        });
        handleCloseDropDown();
    };

    const handleCloseDropDown = () => {
        setIsMainDropdownOpen(false);
        setIsVersionsOpen(false);
        setIsSubDropdownOpen(false);
    };

    const handleTemplateIcon = () => {
        handleTemplateIconClick();
    };

    const getAppTypeBasedOnSelectedValueOnProductFilter = (): AppType => {
        const defaultManualProducts: Array<string> = appInfo?.default_manual_products;
        const useAllDefaults = isAdmin ? enableAllProductForUser : enableAllProductForAllUsers && enableAllProductForUser;
        const selectedProductRetainValue =
            userInfo?.product === ""
                ? useAllDefaults
                    ? {
                          manual_key: "0",
                          faq_key: "0"
                      }
                    : {
                          manual_key: "",
                          faq_key: ""
                      }
                : userInfo?.product;
        const appTypeValue =
            defaultManualProducts.includes(selectedProductRetainValue?.faq_key) || defaultManualProducts.includes(selectedProductRetainValue?.manual_key)
                ? "Manuals"
                : appInfo?.use_all_tab === true
                  ? "ALL"
                  : "FAQ";
        return appTypeValue as AppType;
    };

    const updateDisclaimerStatus = () => {
        const status = localStorage.getItem("disclaimerHide") ?? "initial";
        if (status === "initial") {
            setSessionStatus("active");
            localStorage.setItem("disclaimerHide", "active");
        }
    };

    const resolveSelectedAnswerAfterAbort = () => {
        const preservedSelectedAnswerIndex = loadedAnswerCountAfterAbortRef.current;
        loadedAnswerCountAfterAbortRef.current = null;
        if (preservedSelectedAnswerIndex !== null && preservedSelectedAnswerIndex > 0) {
            const resolvedIndex = preservedSelectedAnswerIndex - 1;
            currentTargetQuestionId.current = resolvedIndex;
            setSelectedAnswer(resolvedIndex);
            return true;
        }
        return false;
    };

    const restorePreviousAppTypeAfterTabSwitchAbort = () => {
        const previousAppType = lastStableAppTypeBeforeTabSwitchRef.current;
        lastStableAppTypeBeforeTabSwitchRef.current = null;
        if (previousAppType) {
            const validAppType = getValidAppType(previousAppType);
            setSelectedAppType(validAppType as AppType);
            userInfo.apptype = validAppType;
        }
    };

    const handleUIPostChatAPIRequestCancelledForQuestionEdit = async (_appType: AppType, answerGenerationActiveDetails: AnswerGenerationActiveDetails) => {
        try {
            const responseStoppedSessionId = answerGenerationActiveDetails.sessionId;
            const sourceSharedSessionId = isChatShared ? sessionId || parseQueryParams(location.search).SessionId || null : null;
            const sessionIdToRestore = sourceSharedSessionId ?? responseStoppedSessionId;
            const requestTriggerType = store.getState().questionInput.requestTriggerType;
            if (!!(sessionIdToRestore && sessionIdToRestore !== "")) {
                const didRestore = await loadChatData(sessionIdToRestore, false, true);
                if (didRestore) {
                    resolveSelectedAnswerAfterAbort();
                    restorePreviousAppTypeAfterTabSwitchAbort();
                }
                if (sourceSharedSessionId) {
                    sessionStorage.setItem("sessionId", sourceSharedSessionId);
                    setSelectedSessionId(sourceSharedSessionId);
                    setCurrentIndex(sourceSharedSessionId);
                }
                currentTargetQuestionId.current = requestTriggerType && requestTriggerType !== "QuestionInput" ? currentTargetQuestionId.current : 0;
                setSelectedAnswer(requestTriggerType && requestTriggerType !== "QuestionInput" ? currentTargetQuestionId.current : 0);
                setTimeout(async () => {
                    userChatMessageRefs.current[currentTargetQuestionId.current]?.handleEditClickForAbortedChatAPIRequest(currentTargetQuestionId.current);
                    dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                    const retainedSessionId = sourceSharedSessionId ?? sessionStorage.getItem("sessionId") ?? null;
                    if (currentTargetQuestionId.current === 0 && retainedSessionId) {
                        await getSessionInfoDataForChatSummaryTitleRetain(retainedSessionId);
                    }
                }, 500);
            }
        } catch (error) {
            console.error(error);
            // safe side fall back of UI ...
            await handleUIPostChatAPIRequestCancelled(answerGenerationActiveDetails);
        }
    };

    const getSessionInfoDataForChatSummaryTitleRetain = async (sessionIdvalue: string) => {
        try {
            await getSessionInfoData(sessionIdvalue)
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    const indexTarget: number = todaySessionsRef.findIndex((session: SessionInfo) => session.session_id === sessionIdvalue);
                    const targetSession = result[String(sessionIdvalue)];
                    const todaySessionsRefCopy: SessionInfo[] = [...todaySessionsRef];
                    todaySessionsRefCopy[indexTarget] = {
                        title: targetSession.title,
                        conversation_date: targetSession.conversation_date,
                        session_id: sessionIdvalue,
                        corp_id: targetSession.corp_id,
                        user_id: targetSession.user_id,
                        group: targetSession.group,
                        chat_summary_title: targetSession.chat_summary_title
                    };
                    setTodaySessionsRef(todaySessionsRefCopy);
                    setCurrentIndex(sessionStorage.getItem("sessionId") ?? "");
                    lastActiveIndexRef.current = targetSession.group;
                    lastSessionRef.current = targetSession;
                    lastActiveSessionRef.current = todaySessionsRefCopy;
                })
                .catch(error => {
                    console.error("Error fetching data:", error);
                });
        } catch (error) {
            console.error(error);
        }
    };

    const handleUIPostChatAPIRequestCancelled = async (answerGenerationActiveDetails: AnswerGenerationActiveDetails) => {
        try {
            const responseStoppedSessionId = answerGenerationActiveDetails.sessionId;
            const sourceSharedSessionId = isChatShared ? sessionId || parseQueryParams(location.search).SessionId || null : null;
            const sessionIdToRestore = sourceSharedSessionId ?? responseStoppedSessionId;
            const requestTriggerType = store.getState().questionInput.requestTriggerType;
            if (!!(sessionIdToRestore && sessionIdToRestore !== "") && answers.length > 0) {
                const didRestore = await loadChatData(sessionIdToRestore, false, true);
                if (didRestore) {
                    resolveSelectedAnswerAfterAbort();
                    restorePreviousAppTypeAfterTabSwitchAbort();
                }
                if (sourceSharedSessionId) {
                    sessionStorage.setItem("sessionId", sourceSharedSessionId);
                    setSelectedSessionId(sourceSharedSessionId);
                    setCurrentIndex(sourceSharedSessionId);
                }
                if (isChatShared && sessionIdToRestore) {
                    await getSessionInfoDataForChatSummaryTitleRetain(sessionIdToRestore);
                    setIsChatShared(false);
                } else {
                    const indexTarget: number = todaySessionsRef.findIndex((session: SessionInfo) => session.session_id === responseStoppedSessionId);
                    if (indexTarget >= 0) {
                        const todaySessionsRefCopy: SessionInfo[] = [...todaySessionsRef];
                        const targetSession: any = todaySessionsRefCopy[indexTarget];
                        todaySessionsRefCopy[indexTarget] = {
                            title: targetSession.title,
                            conversation_date: targetSession.conversation_date,
                            session_id: targetSession.session_id,
                            corp_id: targetSession.corp_id,
                            user_id: targetSession.user_id,
                            group: targetSession.group,
                            chat_summary_title: targetSession.chat_summary_title
                        };
                        setTodaySessionsRef(todaySessionsRefCopy);
                        setCurrentIndex(sessionStorage.getItem("sessionId") ?? "");
                        lastActiveIndexRef.current = targetSession.group;
                        lastSessionRef.current = targetSession;
                        lastActiveSessionRef.current = todaySessionsRefCopy;
                    }
                }
                currentTargetQuestionId.current = requestTriggerType && requestTriggerType !== "QuestionInput" ? currentTargetQuestionId.current : 0;
                setSelectedAnswer(requestTriggerType && requestTriggerType !== "QuestionInput" ? currentTargetQuestionId.current : 0);
                if (requestTriggerType && requestTriggerType === "QuestionInput") {
                    setIsHistoryPageInitialLoading(true);
                    setTimeout(() => {
                        retainQuestionOnAbortChatAPI(answerGenerationActiveDetails, "history");
                    }, 500);
                } else if (requestTriggerType) {
                    setClearChatTriggered(false);
                    setChatContentCleared(true);
                    dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                }
            } else {
                todaySessionsRef.shift();
                suppressAppTypeUrlSyncRef.current = true;
                clearChat();
                navigate("/assistant/chat");
                setTimeout(() => {
                    retainQuestionOnAbortChatAPI(answerGenerationActiveDetails, "chat");
                }, 500);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const retainQuestionOnAbortChatAPI = (answerGenerationActiveDetails: AnswerGenerationActiveDetails, pageType: string) => {
        const answerGenerationActiveDetailsCopy = { ...answerGenerationActiveDetails };
        const pageTypeValue = String(pageType);
        resetTemplateSelection();
        questionInputRefs?.current[pageTypeValue]?.clearTemplateContentFromQuestionInput();

        const templateIdValue = answerGenerationActiveDetailsCopy.templateId;
        if (templateIdValue) {
            const filteredTemplateValue = templateContents
                .filter((tempContent: any) => tempContent.id === templateIdValue)
                .map((tempContent: any) => {
                    return tempContent.template[0];
                })[0];
            handleTemplateContentsBySuggestions(filteredTemplateValue, templateIdValue);
            setChangingTemplateContent(true);
        }
        setTimeout(() => {
            questionInputRefs?.current[pageTypeValue]?.onForceStopAIResponseGenerationRetainQuestion(answerGenerationActiveDetailsCopy);
        }, 1000);
    };

    const fallBackUIOnAbortChatAPI = async (isEditValue: boolean, appType: AppType) => {
        const responseStoppedSessionId = store.getState().questionInput.answerGenerationActiveDetails.sessionId;
        if (!!(responseStoppedSessionId && responseStoppedSessionId !== "")) {
            const answerGenerationActiveDetails = store.getState().questionInput.answerGenerationActiveDetails;
            if (isEditValue) {
                await handleUIPostChatAPIRequestCancelledForQuestionEdit(appType, answerGenerationActiveDetails);
            } else {
                await handleUIPostChatAPIRequestCancelled(answerGenerationActiveDetails);
            }
        }
        setIsOnSave(false);
        setIsOnEdit(false);
        setIsLoading(false);
        setScrollToAnswer(false);
        setSharedAnswerLoadingState(false);
    };

    const setOverAllCurrentSessionDataPoints = (
        currentSessionAnswers: [user: QuestionDetails, response: { FAQ?: ChatAppResponse; Manuals?: ChatAppResponse }][]
    ) => {
        let privateDoctype = false;
        const currentSessionDataPoints: { FAQ: Array<DataPoints>; Manuals: Array<DataPoints>; ALL: Array<DataPoints> } = { FAQ: [], Manuals: [], ALL: [] };
        currentSessionAnswers.forEach(
            (answer: [user: QuestionDetails, response: { FAQ?: ChatAppResponse; Manuals?: ChatAppResponse; ALL?: ChatAppResponse }]) => {
                const fAQcontext: any = answer[1]["FAQ"]?.context;
                if (fAQcontext?.data_points) {
                    currentSessionDataPoints.FAQ.push(fAQcontext.data_points);
                }
                const manualsContext: any = answer[1]["Manuals"]?.context;
                if (manualsContext?.data_points) {
                    currentSessionDataPoints.Manuals.push(manualsContext.data_points);
                }
                const allContext: any = answer[1]["ALL"]?.context;
                if (allContext?.data_points) {
                    currentSessionDataPoints.ALL.push(allContext.data_points);
                }
                if (
                    fAQcontext?.data_points?.some((dataPoint: { doctype: string }) => dataPoint.doctype === "Internal") ||
                    manualsContext?.data_points?.some((dataPoint: { doctype: string }) => dataPoint.doctype === "Internal") ||
                    allContext?.data_points?.some((dataPoint: { doctype: string }) => dataPoint.doctype === "Internal")
                ) {
                    privateDoctype = true;
                }
            }
        );
        if (privateDoctype) {
            localStorage.setItem("private_warning", "true");
        }
        setDocType(privateDoctype);
        dispatch(ChatComponentReducerAction.updateOverAllCurrentSessionDataPoints(currentSessionDataPoints));
    };

    const getAppTypeBasedOnUser = (appType: string): string => {
        let appTypeBasedOnLastAnswer = appType;
        if (answers.length > 0) {
            appTypeBasedOnLastAnswer = getAppTypeBasedOnLastAnswerIsRelatedToAriel(answers);
        }
        const appTypeBasedOnSelectedProduct = getAppTypeBasedOnSelectedValueOnProductFilter();
        const appTypeBasedOnUser =
            appType === "ALL"
                ? appInfo?.use_all_tab === true
                    ? "ALL"
                    : store.getState().questionInput.requestTriggerType === "QuestionInput"
                      ? currentView === "chat"
                          ? appTypeBasedOnSelectedProduct === "ALL"
                              ? "FAQ"
                              : appTypeBasedOnSelectedProduct
                          : appTypeBasedOnLastAnswer === "ALL"
                            ? "FAQ"
                            : appTypeBasedOnLastAnswer
                      : appType === "ALL"
                        ? "FAQ"
                        : appType
                : appType === "ALL"
                  ? "FAQ"
                  : appType;

        return appTypeBasedOnUser;
    };

    // Stable ref to always call the latest startProductScenarioRegenerateFlow without
    // adding it to renderUnifiedAnswer's dependency array (avoids forward-reference TDZ).
    const startProductScenarioRegenerateFlowRef = useRef<((primaryId: number, targetAppType: AppType) => Promise<void>) | undefined>(undefined);

    // Helper function to render UnifiedAnswer consistently
    const renderUnifiedAnswer = useCallback(
        (answer: any, questionDetails: any, index: number, isStreaming: boolean, isHistory: boolean = false) => {
            const response = answer[1];
            const questionDetail = answer[0] || questionDetails;
            return (
                <UnifiedAnswer
                    isStreaming={isStreaming}
                    key={index}
                    doctype={doctype}
                    response={response}
                    conversationDate={questionDetail?.conversation_date}
                    isSelected={isHistory ? selectedAnswer === index && activeAnalysisPanelTab !== undefined : false}
                    onCitationClicked={(c: string, citationIndex: number) => onShowCitation(c, citationIndex, index)}
                    onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                    onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                    onFollowupQuestionClicked={(q: string, appType: AppType) => {
                        isTabClick.current = "false";
                        currentQuestionInfo.current = null;
                        clearExternalQuestionInput(currentView === "history" ? "history" : "chat");
                        makeApiRequest(
                            q,
                            q,
                            appType,
                            appType === "ALL",
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            currentView === "history" ? "history" : "chat"
                        );
                    }}
                    showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                    onFeedbackSubmitClicked={(feedback: any, conv_id: string, appTypeValue: AppType) =>
                        onFeedbackSubmitClicked(feedback, conv_id, appTypeValue)
                    }
                    updateStreamedAnswer={(index: number, updatedAnswer: ChatAppResponse, appType: AppType) => {
                        const updatedanswers = [...answers];
                        if (index >= 0 && index < updatedanswers.length) {
                            updatedanswers[index][1][appType] = updatedAnswer;
                            setOverAllCurrentSessionDataPoints(updatedanswers);
                            setAnswers(updatedanswers);
                        }
                    }}
                    onAttachmentLinkClick={(filename: string, page?: number) => {
                        const currentFiles = answers[index]?.[0]?.filesAttached ?? [];
                        const dataPoints = answers[index]?.[1]?.[selectedAppType]?.context?.data_points ?? [];
                        const fallbackNames =
                            Array.isArray(currentFiles) && currentFiles.length === 0 ? extractAttachmentNamesFromDataPoints(dataPoints) : undefined;
                        const match = findAttachmentOwnerIndex(filename, index, fallbackNames);
                        const targetIndex = match?.index ?? index;
                        const normalized = normalizeAttachmentName(filename);
                        const targetName = match?.matchedName ?? (normalized || filename);
                        userChatMessageRefs.current[targetIndex]?.openAttachmentPreviewByName(targetName, page);
                    }}
                    questionFilesAttached={questionDetail?.filesAttached}
                    primaryId={index?.toString()}
                    onClickLikeDislike={() => {
                        lastAnswerIndexRef.current = index;
                    }}
                    isAdminUser={isAdmin}
                    displayLink={() => {
                        setShowSupportLink(true);
                    }}
                    isLastAnswer={answers?.length === 0 || (answers?.length > 0 && answers?.length - 1 === index)}
                    isFeedbackPage={false}
                    onSend={(appType: AppType) => {
                        dispatch(QuestionInputReducerAction.updateRequestTriggerType("TabSwitch"));
                        lastAnswerIndexRef.current = index;
                        currentTargetQuestionId.current = index;
                        setSelectedAppType(appType);
                        isTabClick.current = "true";
                        currentQuestionInfo.current = null;
                        makeApiRequest(
                            questionDetail.question,
                            questionDetail.extractedQuestion,
                            appType,
                            appType === "ALL",
                            questionDetail.filesAttached,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            questionDetail.templateId,
                            response,
                            undefined,
                            undefined,
                            "chat"
                        );
                    }}
                    isLoading={isLoading}
                    selectedAppType={
                        isTabClick.current === "true"
                            ? selectedAppType
                            : response && Object.keys(response).length === 1 && response["ALL"] && appInfo.use_all_tab === true
                              ? isWapUser
                                  ? selectedAppType
                                  : "ALL"
                              : selectedAppType !== "ALL"
                                ? selectedAppType
                                : appInfo.use_all_tab === true
                                  ? "ALL"
                                  : "FAQ"
                    }
                    setSelectedAppType={setSelectedAppType}
                    lastAnswerIndexRefValue={currentTargetQuestionId.current}
                    onTabClicked={() => {
                        setSelectedAnswer(index);
                        setSelectedCitation(0);
                        currentTargetQuestionId.current = index;
                        sessionStorage.setItem("isTabClicked", "true");
                        if (activeAnalysisPanelTab === AnalysisPanelTabs.ThoughtProcessTab) {
                            setActiveAnalysisPanelTab(undefined);
                        }
                    }}
                    currentPageView={currentView}
                    answerAreaRef={answerAreaRef}
                    chatResponseAreaRef={chatResponseAreaRefValue}
                    lastQuestionIndexValue={(lastQuestionIndexValue.current === 0 ? 0 : lastQuestionIndexValue.current - 1)?.toString()}
                    activateAnswerTour={activateAnswerTour}
                    setActivateAnswerTour={(tourFlag: boolean) => {
                        setActivateAnswerTour(tourFlag);
                        props.startTour(false);
                    }}
                    combinedAppTypeResponse={lastQuestionRef.current?.combinedAppTypeResponse}
                    isHistory={isHistory}
                    onRenerateClick={(option: ReasoningEffort, convId: string) => {
                        dispatch(QuestionInputReducerAction.updateRequestTriggerType("RegenerateClick"));
                        lastAnswerIndexRef.current = index;
                        currentTargetQuestionId.current = index;
                        isRegenerateActive.current = true;
                        setSelectedAnswer(index);
                        currentQuestionInfo.current = null;
                        regenerateRequest(option, index, convId);
                    }}
                    onChangeProductScenarioAndRegenerate={() => {
                        void startProductScenarioRegenerateFlowRef.current?.(index, getAnalysisAppTypeForAnswer(answer));
                    }}
                    isRegenerateActive={isRegenerateActive.current}
                    currentAnswerIndex={answerIndexMap?.[`${questionDetail["question_id"]}__${selectedAppType}`] ?? 0}
                    onAnswerVersionChange={(newVersion: number) => {
                        applyAnswerVersionChange(newVersion, questionDetail["question_id"]);
                        currentTargetQuestionId.current = index;
                    }}
                    answerVersions={regeneratedAnswers[questionDetail["question_id"]]?.[selectedAppType]?.length ?? 0}
                />
            );
        },
        [
            activeAnalysisPanelTab,
            activateAnswerTour,
            answerAreaRef,
            answerIndexMap,
            answers,
            appInfo.use_all_tab,
            chatResponseAreaRefValue,
            currentTargetQuestionId,
            currentView,
            dispatch,
            doctype,
            isLoading,
            isAdmin,
            isRegenerateActive,
            isTabClick,
            isWapUser,
            lastAnswerIndexRef,
            lastQuestionIndexValue,
            lastQuestionRef,
            makeApiRequest,
            onFeedbackSubmitClicked,
            onShowCitation,
            onToggleTab,
            props.startTour,
            regenerateRequest,
            regeneratedAnswers,
            selectedAppType,
            selectedAnswer,
            setActivateAnswerTour,
            setAnswers,
            setOverAllCurrentSessionDataPoints,
            setSelectedAnswer,
            setSelectedAppType,
            setSelectedCitation,
            setShowSupportLink,
            useSuggestFollowupQuestions,
            userChatMessageRefs
        ]
    );

    const computeSessionDataPoints = (versionIndex: number, answerIndex: number, parentMessageId: string) => {
        if (versionIndex === 0 && answerIndex === 0) {
            return allAnswersTree.map<any>((root: any) => [root?.record?.question, root?.record?.answer]);
        }
        const result = loadAnswerRowsFromTree(answers, answerIndex, allAnswersTree, parentMessageId, versionIndex);
        return result ?? answers;
    };

    const nextVersionIndexMap = (prev: Record<string, number>, parentMessageId: string, versionIndex: number, answerIndex: number): Record<string, number> => {
        if (answerIndex === 0) {
            const newMap: Record<string, number> = {};
            for (const key in prev) newMap[key] = 0;
            newMap[parentMessageId] = versionIndex;
            return newMap;
        }
        return { ...prev, [parentMessageId]: versionIndex };
    };

    const normalizeContextKey = (value: unknown): string => String(value ?? "").trim();

    const getDropdownSyncResponse = (answerEntry: any, preferredAppType?: AppType): ChatAppResponse | undefined => {
        const answerBlock = answerEntry?.[1] as Partial<Record<AppType, ChatAppResponse>> | undefined;
        if (!answerBlock || typeof answerBlock !== "object") {
            return undefined;
        }

        const preferredOrder: AppType[] = [preferredAppType ?? selectedAppType, selectedAppType, "ALL", "FAQ", "Manuals"].filter(
            (value, index, source): value is AppType => Boolean(value) && source.indexOf(value) === index
        );

        for (const appType of preferredOrder) {
            const response = answerBlock[appType];
            const productFilter = response?.context?.product_filter as any;
            const manualKey = normalizeContextKey(productFilter?.manual_key);
            const faqKey = normalizeContextKey(productFilter?.faq_key);
            if (response && (manualKey || faqKey)) {
                return response;
            }
        }

        for (const appType of preferredOrder) {
            const response = answerBlock[appType];
            if (response) {
                return response;
            }
        }

        return undefined;
    };

    const syncDropdownFromAnswerContext = (response: ChatAppResponse | undefined) => {
        const context = response?.context as any;
        if (!context || typeof context !== "object") {
            return;
        }

        const rawProductFilter = context.product_filter;
        if (!rawProductFilter || typeof rawProductFilter !== "object") {
            return;
        }

        const normalizedProductFilter = {
            manual_key: normalizeContextKey(rawProductFilter.manual_key),
            faq_key: normalizeContextKey(rawProductFilter.faq_key)
        };
        if (!normalizedProductFilter.manual_key && !normalizedProductFilter.faq_key) {
            return;
        }

        const rawProductVersion = context.productVersion ?? context.product_version;
        const normalizedProductVersion = {
            manual_version_key: normalizeContextKey(rawProductVersion?.manual_version_key),
            faq_version_key: normalizeContextKey(rawProductVersion?.faq_version_key)
        };

        const rawSubProductFilter = context.sub_product_filter ?? context.subProduct;
        const normalizedSubProductFilter = {
            manual_sub_key: normalizeContextKey(rawSubProductFilter?.manual_sub_key),
            faq_sub_key: normalizeContextKey(rawSubProductFilter?.faq_sub_key)
        };

        setDropDownFilterText(normalizedProductFilter, normalizedProductVersion, normalizedSubProductFilter);
    };

    const applyVersionChange = (versionIndex: number, parentMessageId: string, answerIndex: number) => {
        setActiveAnalysisPanelTab(undefined);
        setVersionIndexMap(prev => nextVersionIndexMap(prev, parentMessageId, versionIndex, answerIndex));
        const updatedAnswer = computeSessionDataPoints(versionIndex, answerIndex, parentMessageId);
        const switchedAnswerEntry = updatedAnswer[answerIndex];
        const switchedAppType = switchedAnswerEntry ? getAnalysisAppTypeForAnswer(switchedAnswerEntry) : undefined;
        syncDropdownFromAnswerContext(getDropdownSyncResponse(switchedAnswerEntry, switchedAppType));
        setOverAllCurrentSessionDataPoints(updatedAnswer);
        setAnswers([...updatedAnswer]);
    };

    const renderUserQuestion = useCallback(
        (index: number) => {
            const answer = answers[index];
            if (!answer) return null;
            return (
                <UserChatMessage
                    message={answer[0].extractedQuestion}
                    onSend={(question: string) => {
                        isTabClick.current = "false";
                        dispatch(QuestionInputReducerAction.updateRequestTriggerType("QuestionEdit"));
                        makeApiRequest(
                            question,
                            question,
                            currentTargetQuestionId.current === 0 ? "ALL" : selectedAppType,
                            (currentTargetQuestionId.current === 0 ? "ALL" : selectedAppType) === "ALL",
                            answer[0].filesAttached,
                            undefined,
                            undefined,
                            index,
                            IsOnEdit,
                            answer[0].templateId,
                            answer[1],
                            undefined,
                            undefined,
                            currentView === "history" ? "history" : "chat"
                        );
                        currentQuestionInfo.current = null;
                    }}
                    index={index}
                    answers={answers}
                    onEditClick={(value: boolean, questionInfo: any) => {
                        onEditClick(value, index);
                        if (value) {
                            currentQuestionInfo.current = questionInfo;
                        } else {
                            currentQuestionInfo.current = null;
                        }
                    }}
                    IsOnEdit={IsOnEdit}
                    isAdminUser={isAdmin}
                    selectedQuestion={selectedQuestion === index}
                    applyRestriction={applyRestriction}
                    reference={chatResponseAreaRefValue}
                    responseGenerationActive={isLoading || isStreaming}
                    selectedAppType={selectedAppType}
                    templateId={answer[0].templateId}
                    questionContent={answer[0].question}
                    sourcePage={"chat"}
                    fileAttachments={answer[0].filesAttached}
                    searchTerm={searchTerm}
                    setSelectedAppType={setSelectedAppType}
                    currentVersionIndex={
                        versionIndexMap[
                            (answer[0]?.parentMessageId && answer[0].parentMessageId !== "0" ? answer[0].parentMessageId : answer[0].question_id) ?? ""
                        ] || 0
                    }
                    onVersionChange={(versionIndex: number, parentMessageId: string, answerIndex: number) => {
                        applyVersionChange(versionIndex, parentMessageId, answerIndex);
                    }}
                    histories={histories}
                />
            );
        },
        [
            IsOnEdit,
            answers,
            applyRestriction,
            applyVersionChange,
            chatResponseAreaRefValue,
            currentQuestionInfo,
            currentTargetQuestionId,
            dispatch,
            histories,
            isAdmin,
            isLoading,
            isStreaming,
            isTabClick,
            makeApiRequest,
            onEditClick,
            QuestionInputReducerAction,
            searchTerm,
            selectedAppType,
            selectedQuestion,
            setSelectedAppType,
            versionIndexMap
        ]
    );

    const getFileAttachmentsWithPreviewAsBase64String = (fileAttachments: Attachment[]): Attachment[] => {
        const serverSideFileAttachmentsDetails: FileAttachmentDetails[] = store.getState().questionInput.serverSideFileAttachmentsIds;
        // Create a Map for O(1) lookup instead of O(n) find operation
        const fileDetailsMap = new Map(serverSideFileAttachmentsDetails.map(detail => [detail.frontEndId, detail]));
        const fileAttachmentsWithPreview: Attachment[] = [...fileAttachments].map((file: Attachment) => {
            const matchedFileDetails = fileDetailsMap.get(file.id);
            return {
                ...file,
                preview: matchedFileDetails ? matchedFileDetails.preview : file.preview
            };
        });
        return fileAttachmentsWithPreview;
    };

    const applyAnswerVersionChange = (currentVersion: number, question_id: string) => {
        if (isLoading || isStreaming) return;
        setActiveAnalysisPanelTab(undefined);
        setAnswerIndexMap(prev => ({
            ...prev,
            [`${question_id}__${selectedAppType}`]: currentVersion
        }));
        const fallbackAnswerByType = Object.fromEntries(
            Object.entries(regeneratedAnswers[question_id] ?? {}).map(([appType, versions]) => [appType, (versions as any)?.[currentVersion]])
        );
        const answerToBeAdded: ChatAppResponse | undefined =
            regeneratedAnswers[question_id]?.[selectedAppType]?.[currentVersion] ?? getDropdownSyncResponse([null, fallbackAnswerByType]);
        syncDropdownFromAnswerContext(answerToBeAdded);
        const updatedAnswers: any = answers.map(ans => {
            if (ans[0].question_id === question_id) {
                return [
                    { ...ans[0] },
                    {
                        ...ans[1],
                        [selectedAppType]: answerToBeAdded
                    }
                ];
            }
            return ans;
        });
        setOverAllCurrentSessionDataPoints(updatedAnswers);
        setAnswers([...updatedAnswers]);
    };

    const onDocTypeFilterChange = useCallback((_: unknown, item: { key?: string | number } | undefined) => {
        const nextValue = item?.key?.toString() ?? "";
        setDocTypeFilter(nextValue);
        saveUserSettings({
            docValue: nextValue
        });
        userInfo.docValue = nextValue;
    }, []);

    const onTemplateClear = useCallback(() => {
        if (templateAppliedFlagValue) {
            setClearedTemplateSelectionProduct(selectedProduct || null);
            questionInputRefs?.current[currentView]?.clearTemplateContentFromQuestionInput();
            handleClearTemplateContent();
        }
    }, [currentView, handleClearTemplateContent, selectedProduct, templateAppliedFlagValue]);

    const normalizeCommaSeparatedValues = (value: unknown): string[] => {
        if (value === null || value === undefined) return [];

        const normalizedValues: string[] = [];

        const appendFromUnknown = (currentValue: unknown) => {
            if (currentValue === null || currentValue === undefined) return;

            if (Array.isArray(currentValue)) {
                currentValue.forEach(appendFromUnknown);
                return;
            }

            if (typeof currentValue === "string") {
                const trimmedValue = currentValue.trim();
                if (!trimmedValue) return;

                // Some metadata values arrive as serialized JSON arrays/values (e.g. "[\"2202\"]").
                if ((trimmedValue.startsWith("[") && trimmedValue.endsWith("]")) || (trimmedValue.startsWith("{") && trimmedValue.endsWith("}"))) {
                    try {
                        const parsedValue = JSON.parse(trimmedValue);
                        appendFromUnknown(parsedValue);
                        return;
                    } catch {
                        // Fall through to delimiter-based parsing.
                    }
                }

                trimmedValue
                    .split(/[,|;]/)
                    .map(item =>
                        item
                            .trim()
                            .replace(/^[\[\(\{]+|[\]\)\}]+$/g, "")
                            .replace(/^['"`]+|['"`]+$/g, "")
                            .trim()
                    )
                    .filter(Boolean)
                    .forEach(item => normalizedValues.push(item));
                return;
            }

            const fallbackValue = String(currentValue).trim();
            if (fallbackValue) {
                normalizedValues.push(fallbackValue);
            }
        };

        appendFromUnknown(value);
        return normalizedValues;
    };

    const normalizeProductIndexesFromDataPoint = (value: unknown): string[] => normalizeCommaSeparatedValues(value);

    const normalizeSubProductIndexesFromDataPoint = (value: unknown): string[] => normalizeCommaSeparatedValues(value);

    const normalizeProductNamesFromDataPoint = (value: unknown): string[] => normalizeCommaSeparatedValues(value);

    const normalizeSubProductNamesFromDataPoint = (value: unknown): string[] => normalizeCommaSeparatedValues(value);

    const normalizeProductNameComparison = (value: unknown): string =>
        String(value ?? "")
            .trim()
            .replace(/\s+/g, " ")
            .toLowerCase();

    const normalizeComparableProductKey = (value: unknown): string => {
        let normalizedValue = String(value ?? "").trim();
        if (!normalizedValue) return "";

        // Normalize wrappers/quotes that may leak from serialized metadata payloads.
        normalizedValue = normalizedValue
            .replace(/^[\[\(\{]+|[\]\)\}]+$/g, "")
            .replace(/^['"`]+|['"`]+$/g, "")
            .trim();
        if (!normalizedValue) return "";

        const numericValue = Number(normalizedValue);
        return Number.isFinite(numericValue) ? String(numericValue) : normalizedValue;
    };

    const createProductIdentityKey = (manualKey: unknown, faqKey: unknown): string =>
        `${normalizeComparableProductKey(manualKey)}|${normalizeComparableProductKey(faqKey)}`;

    const parseProductIdentityKey = (key: string): { manual_key: string; faq_key: string } => {
        const [manual_key = "", faq_key = ""] = String(key ?? "").split("|");
        return { manual_key, faq_key };
    };

    const createHierarchyMatchKey = (
        manualKey: unknown,
        faqKey: unknown,
        manualVersionKey?: unknown,
        faqVersionKey?: unknown,
        manualSubKey?: unknown,
        faqSubKey?: unknown
    ): string =>
        [
            normalizeComparableProductKey(manualKey),
            normalizeComparableProductKey(faqKey),
            normalizeComparableProductKey(manualVersionKey),
            normalizeComparableProductKey(faqVersionKey),
            normalizeComparableProductKey(manualSubKey),
            normalizeComparableProductKey(faqSubKey)
        ].join("|");

    const toKeySet = (values: string[] | Set<string>): Set<string> => new Set(Array.isArray(values) ? values : [...values]);

    const intersectKeySets = (left: Set<string>, right: Set<string>): Set<string> => {
        if (left.size === 0 || right.size === 0) return new Set<string>();
        return new Set<string>([...left].filter(value => right.has(value)));
    };

    const isAttachmentCitationDataPoint = (dataPoint: DataPoints): boolean => {
        const sourceKind = String(dataPoint.source_kind ?? "")
            .trim()
            .toLowerCase();
        const uploadDir = String(dataPoint.uploaddir_field ?? "")
            .trim()
            .toLowerCase();
        const sourcePage = String(dataPoint.sourcepage_field ?? "")
            .trim()
            .toLowerCase();
        const docType = String(dataPoint.doctype ?? "")
            .trim()
            .toLowerCase();

        return (
            sourceKind === "attachment" ||
            uploadDir.startsWith("attachments/") ||
            uploadDir === "user_files" ||
            sourcePage.includes("attachments/") ||
            docType === "user file"
        );
    };

    const mapMatchedProductsFromDataPoints = useCallback(
        (dataPoints: DataPoints[]): AllProductMatchedProduct[] => {
            const currentLocaleProducts: Product[] = Array.isArray(products) ? products : [];
            const sourceProducts = currentLocaleProducts;

            type HierarchyOption = Omit<AllProductMatchedProduct, "count">;
            type HierarchyCatalog = {
                productLevelOptionKey: string;
                optionByKey: Map<string, HierarchyOption>;
                subIndexToOptionKeys: Map<string, Set<string>>;
                subNameToOptionKeys: Map<string, Set<string>>;
                manualVersionToOptionKeys: Map<string, Set<string>>;
                faqVersionToOptionKeys: Map<string, Set<string>>;
                faqVersionNumberToOptionKeys: Map<number, Set<string>>;
            };

            const identityToCurrentProduct = new Map<string, Product>();
            const identityToAnyLocaleName = new Map<string, string>();
            const normalizedNameToIdentities = new Map<string, Set<string>>();
            const productIndexToIdentities = new Map<string, Set<string>>();
            const pathTokenToIdentities = new Map<string, Set<string>>();
            const identityToHierarchyCatalog = new Map<string, HierarchyCatalog>();

            const normalizeNumericValuesFromDataPoint = (value: unknown): number[] =>
                normalizeCommaSeparatedValues(value)
                    .map(rawValue => Number(rawValue))
                    .filter(value => Number.isFinite(value));

            const buildFaqVersionRangesFromDataPoint = (dataPoint: DataPoints): Array<{ start: number; end: number }> => {
                const startValues = normalizeNumericValuesFromDataPoint(dataPoint.start_version);
                const endValues = normalizeNumericValuesFromDataPoint(dataPoint.end_version);
                const ranges: Array<{ start: number; end: number }> = [];
                if (startValues.length > 0 && endValues.length > 0) {
                    const maxPairs = Math.max(startValues.length, endValues.length);
                    for (let index = 0; index < maxPairs; index += 1) {
                        const startValue = startValues[index] ?? startValues[0];
                        const endValue = endValues[index] ?? endValues[0];
                        if (!Number.isFinite(startValue) || !Number.isFinite(endValue)) continue;
                        ranges.push({
                            start: Math.min(startValue, endValue),
                            end: Math.max(startValue, endValue)
                        });
                    }
                }
                return ranges;
            };

            const addOptionKeyToStringMap = (map: Map<string, Set<string>>, value: unknown, optionKey: string) => {
                const normalizedValue = normalizeComparableProductKey(value);
                if (!normalizedValue) return;
                const existingKeys = map.get(normalizedValue) ?? new Set<string>();
                existingKeys.add(optionKey);
                map.set(normalizedValue, existingKeys);
            };

            const addOptionKeyToNameMap = (map: Map<string, Set<string>>, value: unknown, optionKey: string) => {
                const normalizedName = normalizeProductNameComparison(value);
                if (!normalizedName) return;
                const existingKeys = map.get(normalizedName) ?? new Set<string>();
                existingKeys.add(optionKey);
                map.set(normalizedName, existingKeys);
            };

            const addOptionKeyToNumberMap = (map: Map<number, Set<string>>, value: unknown, optionKey: string) => {
                const numericValue = Number(value);
                if (!Number.isFinite(numericValue)) return;
                const existingKeys = map.get(numericValue) ?? new Set<string>();
                existingKeys.add(optionKey);
                map.set(numericValue, existingKeys);
            };

            const addProductIndexIdentity = (indexValue: unknown, identityKey: string) => {
                const normalizedIndex = normalizeComparableProductKey(indexValue);
                if (!normalizedIndex) return;
                const existingKeys = productIndexToIdentities.get(normalizedIndex) ?? new Set<string>();
                existingKeys.add(identityKey);
                productIndexToIdentities.set(normalizedIndex, existingKeys);
            };

            const normalizePathToken = (value: unknown): string =>
                String(value ?? "")
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9\u3040-\u30ff\u4e00-\u9faf]+/g, "");

            const extractSearchablePathTokens = (tokenValue: unknown): string[] => {
                const rawValue = String(tokenValue ?? "").trim();
                if (!rawValue) return [];

                const collectedTokens = new Set<string>();
                const normalizedWholeToken = normalizePathToken(rawValue);
                if (normalizedWholeToken && normalizedWholeToken.length >= 3) {
                    collectedTokens.add(normalizedWholeToken);
                }

                rawValue
                    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
                    .split(/[^a-zA-Z0-9\u3040-\u30ff\u4e00-\u9faf]+/)
                    .map(segment => normalizePathToken(segment))
                    .filter(segment => segment.length >= 3)
                    .forEach(segment => collectedTokens.add(segment));

                return [...collectedTokens];
            };

            const addPathTokenIdentity = (tokenValue: unknown, identityKey: string) => {
                extractSearchablePathTokens(tokenValue).forEach(token => {
                    const existingKeys = pathTokenToIdentities.get(token) ?? new Set<string>();
                    existingKeys.add(identityKey);
                    pathTokenToIdentities.set(token, existingKeys);
                });
            };

            const createDisplayPath = (parts: Array<unknown>): string =>
                parts
                    .map(part => String(part ?? "").trim())
                    .filter(Boolean)
                    .join(" > ");

            const registerHierarchyOption = (
                catalog: HierarchyCatalog,
                option: HierarchyOption,
                metadata: {
                    subIndexes?: unknown[];
                    subNames?: unknown[];
                    manualVersionKey?: unknown;
                    faqVersionKey?: unknown;
                } = {}
            ) => {
                const optionKey = createHierarchyMatchKey(
                    option.manual_key,
                    option.faq_key,
                    option.productVersion?.manual_version_key,
                    option.productVersion?.faq_version_key,
                    option.subProduct?.manual_sub_key,
                    option.subProduct?.faq_sub_key
                );
                if (!catalog.optionByKey.has(optionKey)) {
                    catalog.optionByKey.set(optionKey, option);
                }

                (metadata.subIndexes ?? []).forEach(subIndex => addOptionKeyToStringMap(catalog.subIndexToOptionKeys, subIndex, optionKey));
                (metadata.subNames ?? []).forEach(subName => addOptionKeyToNameMap(catalog.subNameToOptionKeys, subName, optionKey));

                addOptionKeyToStringMap(catalog.manualVersionToOptionKeys, metadata.manualVersionKey, optionKey);
                addOptionKeyToStringMap(catalog.faqVersionToOptionKeys, metadata.faqVersionKey, optionKey);
                addOptionKeyToNumberMap(catalog.faqVersionNumberToOptionKeys, metadata.faqVersionKey, optionKey);
            };

            currentLocaleProducts.forEach(product => {
                const identityKey = createProductIdentityKey(product?.manual_key, product?.faq_key);
                if (!identityKey) return;
                if (!identityToCurrentProduct.has(identityKey)) {
                    identityToCurrentProduct.set(identityKey, product);
                }
            });

            sourceProducts.forEach(product => {
                const identityKey = createProductIdentityKey(product?.manual_key, product?.faq_key);
                if (!identityKey || identityKey === "|") return;

                const normalizedProductName = normalizeProductNameComparison(product?.text);
                if (normalizedProductName) {
                    const existingKeys = normalizedNameToIdentities.get(normalizedProductName) ?? new Set<string>();
                    existingKeys.add(identityKey);
                    normalizedNameToIdentities.set(normalizedProductName, existingKeys);
                }
                addPathTokenIdentity(product?.text, identityKey);
                addPathTokenIdentity((product as any)?.product_name, identityKey);
                addPathTokenIdentity((product as any)?.product_key, identityKey);

                const displayName = String(product?.text ?? "").trim();
                if (displayName && !identityToAnyLocaleName.has(identityKey)) {
                    identityToAnyLocaleName.set(identityKey, displayName);
                }

                // product_index metadata from citations can be product key IDs or group IDs
                // depending on source/indexing path; map all known identifiers to the product.
                addProductIndexIdentity(product?.manual_key, identityKey);
                addProductIndexIdentity(product?.faq_key, identityKey);
                (Array.isArray(product?.faq_groupids) ? product.faq_groupids : []).forEach(groupId => addProductIndexIdentity(groupId, identityKey));
                (Array.isArray(product?.manual_groupids) ? product.manual_groupids : []).forEach(groupId => addProductIndexIdentity(groupId, identityKey));

                if (!displayName || identityToHierarchyCatalog.has(identityKey)) return;

                const productOption: HierarchyOption = {
                    productText: displayName,
                    manual_key: normalizeComparableProductKey(product?.manual_key),
                    faq_key: normalizeComparableProductKey(product?.faq_key)
                };
                const productOptionKey = createHierarchyMatchKey(productOption.manual_key, productOption.faq_key);
                const hierarchyCatalog: HierarchyCatalog = {
                    productLevelOptionKey: productOptionKey,
                    optionByKey: new Map([[productOptionKey, productOption]]),
                    subIndexToOptionKeys: new Map<string, Set<string>>(),
                    subNameToOptionKeys: new Map<string, Set<string>>(),
                    manualVersionToOptionKeys: new Map<string, Set<string>>(),
                    faqVersionToOptionKeys: new Map<string, Set<string>>(),
                    faqVersionNumberToOptionKeys: new Map<number, Set<string>>()
                };
                identityToHierarchyCatalog.set(identityKey, hierarchyCatalog);

                const productVersions: Array<any> = Array.isArray((product as any)?.versions) ? (product as any).versions : [];
                const subProductsOnlyEntries: Array<any> = Array.isArray((product as any)?.sub_products) ? (product as any).sub_products : [];
                const isSubOnlyProduct = String((product as any)?.is_sub_only ?? "") === "true";

                if (isSubOnlyProduct || subProductsOnlyEntries.length > 0) {
                    const subOnlySourceEntries = isSubOnlyProduct ? productVersions : subProductsOnlyEntries;
                    subOnlySourceEntries.forEach(subOnlyEntry => {
                        const manualSubKey = normalizeComparableProductKey(subOnlyEntry?.manual_sub_key ?? subOnlyEntry?.manual_version_key);
                        const faqSubKey = normalizeComparableProductKey(subOnlyEntry?.faq_sub_key ?? subOnlyEntry?.faq_version_key);
                        const subLabel = String(subOnlyEntry?.text ?? "").trim();
                        if (!subLabel) return;
                        if (!manualSubKey && !faqSubKey) return;

                        registerHierarchyOption(
                            hierarchyCatalog,
                            {
                                productText: displayName,
                                manual_key: normalizeComparableProductKey(product?.manual_key),
                                faq_key: normalizeComparableProductKey(product?.faq_key),
                                displayText: createDisplayPath([displayName, subLabel]),
                                subProduct: {
                                    manual_sub_key: manualSubKey,
                                    faq_sub_key: faqSubKey
                                }
                            },
                            {
                                subIndexes: [manualSubKey, faqSubKey],
                                subNames: [subLabel]
                            }
                        );
                    });
                    // is_sub_only products don't have version hierarchy. For products that expose
                    // sub_products explicitly, we still continue and register version hierarchy if present.
                    if (isSubOnlyProduct) return;
                }

                if (productVersions.length === 0) return;

                productVersions.forEach(versionEntry => {
                    const manualVersionKey = normalizeComparableProductKey(versionEntry?.manual_version_key);
                    const faqVersionKey = normalizeComparableProductKey(versionEntry?.faq_version_key);
                    const versionLabel = String(versionEntry?.text ?? "").trim();
                    if (!versionLabel) return;

                    registerHierarchyOption(
                        hierarchyCatalog,
                        {
                            productText: displayName,
                            manual_key: normalizeComparableProductKey(product?.manual_key),
                            faq_key: normalizeComparableProductKey(product?.faq_key),
                            displayText: createDisplayPath([displayName, versionLabel]),
                            productVersion: {
                                manual_version_key: manualVersionKey,
                                faq_version_key: faqVersionKey
                            }
                        },
                        {
                            manualVersionKey,
                            faqVersionKey
                        }
                    );

                    const subVersions: Array<any> = Array.isArray(versionEntry?.sub_versions) ? versionEntry.sub_versions : [];
                    subVersions.forEach(subEntry => {
                        const manualSubKey = normalizeComparableProductKey(subEntry?.manual_sub_key);
                        const faqSubKey = normalizeComparableProductKey(subEntry?.faq_sub_key);
                        const subLabel = String(subEntry?.text ?? "").trim();
                        if (!subLabel) return;
                        if (!manualSubKey && !faqSubKey) return;

                        registerHierarchyOption(
                            hierarchyCatalog,
                            {
                                productText: displayName,
                                manual_key: normalizeComparableProductKey(product?.manual_key),
                                faq_key: normalizeComparableProductKey(product?.faq_key),
                                displayText: createDisplayPath([displayName, versionLabel, subLabel]),
                                productVersion: {
                                    manual_version_key: manualVersionKey,
                                    faq_version_key: faqVersionKey
                                },
                                subProduct: {
                                    manual_sub_key: manualSubKey,
                                    faq_sub_key: faqSubKey
                                }
                            },
                            {
                                subIndexes: [manualSubKey, faqSubKey],
                                subNames: [subLabel],
                                manualVersionKey,
                                faqVersionKey
                            }
                        );
                    });
                });
            });

            const grouped = new Map<string, AllProductMatchedProduct>();
            const groupedScoreStats = new Map<string, { scoreSum: number; scoreCount: number; scoreMax: number }>();
            const groupedRerankerScores = new Map<string, number[]>();
            const parseSearchScore = (value: unknown): number | null => {
                if (value === null || value === undefined) return null;
                const numericValue = Number(String(value).trim());
                if (!Number.isFinite(numericValue)) return null;
                return numericValue;
            };

            dataPoints.forEach(dataPoint => {
                if (isAttachmentCitationDataPoint(dataPoint)) return;
                const rerankerScore = parseSearchScore((dataPoint as any).search_reranker_score);
                const searchScore = parseSearchScore((dataPoint as any).search_score);
                const evidenceScore = rerankerScore ?? searchScore;

                const productIndexesFromPoint = normalizeProductIndexesFromDataPoint(dataPoint.product_index).map(normalizeComparableProductKey);
                const productNamesFromPoint = [
                    ...normalizeProductNamesFromDataPoint(dataPoint.product_name),
                    ...normalizeProductNamesFromDataPoint(dataPoint.product_full_name)
                ];
                const subProductIndexesFromPoint = normalizeSubProductIndexesFromDataPoint(dataPoint.sub_product_index).map(normalizeComparableProductKey);
                const subProductNamesFromPoint = normalizeSubProductNamesFromDataPoint(dataPoint.sub_product_name);
                const manualVersionIndexesFromPoint = [
                    ...normalizeCommaSeparatedValues(dataPoint.version),
                    ...normalizeCommaSeparatedValues((dataPoint as any).version_index),
                    ...normalizeCommaSeparatedValues((dataPoint as any).manual_version_key)
                ].map(normalizeComparableProductKey);
                const faqVersionIndexesFromPoint = [
                    ...normalizeCommaSeparatedValues((dataPoint as any).faq_version),
                    ...normalizeCommaSeparatedValues((dataPoint as any).faq_version_key)
                ].map(normalizeComparableProductKey);
                const faqVersionRangesFromPoint = buildFaqVersionRangesFromDataPoint(dataPoint);

                const matchedIdentityKeysByName = new Set<string>();
                const matchedIdentityKeysByProductIndex = new Set<string>();
                const matchedIdentityKeysByPath = new Set<string>();

                productNamesFromPoint.forEach(nameValue => {
                    const normalizedName = normalizeProductNameComparison(nameValue);
                    if (!normalizedName) return;
                    const matchedIdentities = normalizedNameToIdentities.get(normalizedName);
                    if (!matchedIdentities) return;
                    matchedIdentities.forEach(identityKey => matchedIdentityKeysByName.add(identityKey));
                });

                productIndexesFromPoint.forEach(indexValue => {
                    if (!indexValue) return;
                    const matchedByProductIndex = productIndexToIdentities.get(indexValue);
                    if (!matchedByProductIndex) return;
                    matchedByProductIndex.forEach(identityKey => matchedIdentityKeysByProductIndex.add(identityKey));
                });

                const searchablePath = normalizePathToken(`${String(dataPoint.uploaddir_field ?? "")}/${String(dataPoint.sourcepage_field ?? "")}`);
                if (searchablePath) {
                    pathTokenToIdentities.forEach((identityKeys, token) => {
                        if (!searchablePath.includes(token)) return;
                        identityKeys.forEach(identityKey => matchedIdentityKeysByPath.add(identityKey));
                    });
                }

                const resolveMatchedIdentityKeys = (): Set<string> => {
                    const byName = toKeySet(matchedIdentityKeysByName);
                    const byProductIndex = toKeySet(matchedIdentityKeysByProductIndex);
                    const byPath = toKeySet(matchedIdentityKeysByPath);

                    if (byProductIndex.size > 0 && byName.size > 0) {
                        const intersection = intersectKeySets(byName, byProductIndex);
                        if (intersection.size > 0) return intersection;
                        // Keep product-index precedence when metadata conflicts.
                        return byProductIndex;
                    }

                    if (byProductIndex.size > 0) return byProductIndex;
                    if (byName.size > 0 && byPath.size > 0) {
                        const intersection = intersectKeySets(byName, byPath);
                        if (intersection.size > 0) return intersection;
                    }
                    if (byName.size > 0) return byName;
                    if (byPath.size > 0) return byPath;
                    return new Set<string>();
                };

                const matchedIdentityKeys = resolveMatchedIdentityKeys();
                if (matchedIdentityKeys.size === 0) return;

                const matchedGroupingKeys = new Set<string>();
                matchedIdentityKeys.forEach(identityKey => {
                    const { manual_key: manualKey, faq_key: faqKey } = parseProductIdentityKey(identityKey);
                    if (manualKey === "0" && faqKey === "0") return;

                    const hierarchyCatalog = identityToHierarchyCatalog.get(identityKey);
                    const fallbackDisplayName =
                        String(identityToCurrentProduct.get(identityKey)?.text ?? "").trim() || String(identityToAnyLocaleName.get(identityKey) ?? "").trim();

                    let selectedOption: HierarchyOption | null = null;
                    if (hierarchyCatalog) {
                        const matchedSubOptionKeys = new Set<string>();
                        subProductIndexesFromPoint.forEach(subIndex => {
                            if (!subIndex) return;
                            const mappedOptionKeys = hierarchyCatalog.subIndexToOptionKeys.get(subIndex);
                            if (!mappedOptionKeys) return;
                            mappedOptionKeys.forEach(optionKey => matchedSubOptionKeys.add(optionKey));
                        });
                        subProductNamesFromPoint.forEach(subProductName => {
                            const normalizedSubProductName = normalizeProductNameComparison(subProductName);
                            if (!normalizedSubProductName) return;
                            const mappedOptionKeys = hierarchyCatalog.subNameToOptionKeys.get(normalizedSubProductName);
                            if (!mappedOptionKeys) return;
                            mappedOptionKeys.forEach(optionKey => matchedSubOptionKeys.add(optionKey));
                        });

                        const matchedVersionOptionKeys = new Set<string>();
                        manualVersionIndexesFromPoint.forEach(versionIndex => {
                            if (!versionIndex) return;
                            const mappedOptionKeys = hierarchyCatalog.manualVersionToOptionKeys.get(versionIndex);
                            if (!mappedOptionKeys) return;
                            mappedOptionKeys.forEach(optionKey => matchedVersionOptionKeys.add(optionKey));
                        });
                        faqVersionIndexesFromPoint.forEach(versionIndex => {
                            if (!versionIndex) return;
                            const mappedOptionKeys = hierarchyCatalog.faqVersionToOptionKeys.get(versionIndex);
                            if (mappedOptionKeys) {
                                mappedOptionKeys.forEach(optionKey => matchedVersionOptionKeys.add(optionKey));
                            }
                            const mappedNumericOptionKeys = hierarchyCatalog.faqVersionNumberToOptionKeys.get(Number(versionIndex));
                            if (!mappedNumericOptionKeys) return;
                            mappedNumericOptionKeys.forEach(optionKey => matchedVersionOptionKeys.add(optionKey));
                        });
                        faqVersionRangesFromPoint.forEach(rangeValue => {
                            hierarchyCatalog.faqVersionNumberToOptionKeys.forEach((optionKeys, faqVersionNumber) => {
                                if (faqVersionNumber >= rangeValue.start && faqVersionNumber <= rangeValue.end) {
                                    optionKeys.forEach(optionKey => matchedVersionOptionKeys.add(optionKey));
                                }
                            });
                        });

                        if (matchedSubOptionKeys.size === 1) {
                            const [onlySubOptionKey] = [...matchedSubOptionKeys];
                            selectedOption = hierarchyCatalog.optionByKey.get(onlySubOptionKey) ?? null;
                        } else if (matchedSubOptionKeys.size > 1) {
                            if (matchedVersionOptionKeys.size > 0) {
                                const intersectedOptionKeys = [...matchedSubOptionKeys].filter(optionKey => matchedVersionOptionKeys.has(optionKey));
                                if (intersectedOptionKeys.length === 1) {
                                    selectedOption = hierarchyCatalog.optionByKey.get(intersectedOptionKeys[0]) ?? null;
                                }
                            }
                        } else if (matchedVersionOptionKeys.size === 1) {
                            const [onlyVersionOptionKey] = [...matchedVersionOptionKeys];
                            selectedOption = hierarchyCatalog.optionByKey.get(onlyVersionOptionKey) ?? null;
                        } else if (matchedVersionOptionKeys.size > 1) {
                            const versionOnlyOptions = [...matchedVersionOptionKeys]
                                .map(optionKey => hierarchyCatalog.optionByKey.get(optionKey))
                                .filter((option): option is HierarchyOption => !!option && !option.subProduct);
                            if (versionOnlyOptions.length === 1) {
                                selectedOption = versionOnlyOptions[0];
                            }
                        }

                        if (!selectedOption) {
                            selectedOption = hierarchyCatalog.optionByKey.get(hierarchyCatalog.productLevelOptionKey) ?? null;
                        }
                    }

                    if (!selectedOption) {
                        if (!fallbackDisplayName) return;
                        selectedOption = {
                            productText: fallbackDisplayName,
                            manual_key: manualKey,
                            faq_key: faqKey
                        };
                    }

                    const groupedKey = createHierarchyMatchKey(
                        selectedOption.manual_key,
                        selectedOption.faq_key,
                        selectedOption.productVersion?.manual_version_key,
                        selectedOption.productVersion?.faq_version_key,
                        selectedOption.subProduct?.manual_sub_key,
                        selectedOption.subProduct?.faq_sub_key
                    );
                    if (!groupedKey || matchedGroupingKeys.has(groupedKey)) return;
                    matchedGroupingKeys.add(groupedKey);

                    const existing = grouped.get(groupedKey);
                    if (existing) {
                        existing.count += 1;
                    } else {
                        grouped.set(groupedKey, {
                            ...selectedOption,
                            count: 1
                        });
                    }
                    if (evidenceScore !== null) {
                        const existingScoreStats = groupedScoreStats.get(groupedKey) ?? {
                            scoreSum: 0,
                            scoreCount: 0,
                            scoreMax: evidenceScore
                        };
                        existingScoreStats.scoreSum += evidenceScore;
                        existingScoreStats.scoreCount += 1;
                        existingScoreStats.scoreMax = Math.max(existingScoreStats.scoreMax, evidenceScore);
                        groupedScoreStats.set(groupedKey, existingScoreStats);
                    }
                    if (rerankerScore !== null) {
                        const existingRerankerScores = groupedRerankerScores.get(groupedKey) ?? [];
                        existingRerankerScores.push(rerankerScore);
                        groupedRerankerScores.set(groupedKey, existingRerankerScores);
                    }
                });
            });

            const groupedEntries = [...grouped.entries()];
            if (groupedEntries.length === 0) {
                return [];
            }

            const hasScoreEvidence = groupedEntries.some(([key]) => (groupedScoreStats.get(key)?.scoreCount ?? 0) > 0);
            const maxAverageScore = hasScoreEvidence
                ? Math.max(
                      ...groupedEntries.map(([key]) => {
                          const scoreStats = groupedScoreStats.get(key);
                          if (!scoreStats || scoreStats.scoreCount === 0) return 0;
                          return scoreStats.scoreSum / scoreStats.scoreCount;
                      })
                  )
                : 0;
            const maxPeakScore = hasScoreEvidence
                ? Math.max(
                      ...groupedEntries.map(([key]) => {
                          const scoreStats = groupedScoreStats.get(key);
                          if (!scoreStats || scoreStats.scoreCount === 0) return 0;
                          return scoreStats.scoreMax;
                      })
                  )
                : 0;

            const rankedProducts = groupedEntries.map(([key, groupedProduct]) => {
                const scoreStats = groupedScoreStats.get(key);
                const rerankerScores = [...(groupedRerankerScores.get(key) ?? [])].sort((left, right) => right - left);
                if (!hasScoreEvidence || !scoreStats || scoreStats.scoreCount === 0 || maxAverageScore <= 0) {
                    return {
                        ...groupedProduct,
                        rerankerScores
                    };
                }

                const defaultAverageWeight = clampRuntimeWeight(appInfoDefaultWeightAverage, 0.45);
                const defaultPeakWeight = clampRuntimeWeight(appInfoDefaultWeightPeak, 0.1);
                const defaultVolumeWeight = clampRuntimeWeight(appInfoDefaultWeightVolume, 0.45);
                const defaultTotalWeight = defaultAverageWeight + defaultPeakWeight + defaultVolumeWeight;
                const fallbackAverageWeight = defaultTotalWeight > 0 ? defaultAverageWeight / defaultTotalWeight : 0.45;
                const fallbackPeakWeight = defaultTotalWeight > 0 ? defaultPeakWeight / defaultTotalWeight : 0.1;
                const fallbackVolumeWeight = defaultTotalWeight > 0 ? defaultVolumeWeight / defaultTotalWeight : 0.45;

                const rawAverageWeight = clampRuntimeWeight(allProductAutoSelectWeightAverage, defaultAverageWeight);
                const rawPeakWeight = clampRuntimeWeight(allProductAutoSelectWeightPeak, defaultPeakWeight);
                const rawVolumeWeight = clampRuntimeWeight(allProductAutoSelectWeightVolume, defaultVolumeWeight);
                const totalWeight = rawAverageWeight + rawPeakWeight + rawVolumeWeight;
                const normalizedAverageWeight = totalWeight > 0 ? rawAverageWeight / totalWeight : fallbackAverageWeight;
                const normalizedPeakWeight = totalWeight > 0 ? rawPeakWeight / totalWeight : fallbackPeakWeight;
                const normalizedVolumeWeight = totalWeight > 0 ? rawVolumeWeight / totalWeight : fallbackVolumeWeight;

                const averageScore = scoreStats.scoreSum / scoreStats.scoreCount;
                const normalizedAverageScore = averageScore / maxAverageScore;
                const normalizedPeakScore = maxPeakScore > 0 ? scoreStats.scoreMax / maxPeakScore : normalizedAverageScore;
                // Volume factor: normalise sample count across all candidates so that a
                // candidate with more matching citations is rewarded over one with fewer
                // but slightly higher average quality.
                const maxSampleCount = Math.max(...groupedEntries.map(([k]) => groupedScoreStats.get(k)?.scoreCount ?? 0), 1);
                const normalizedVolume = scoreStats.scoreCount / maxSampleCount;
                const relevanceScore = Math.min(
                    1,
                    Math.max(
                        0,
                        normalizedAverageScore * normalizedAverageWeight +
                            normalizedPeakScore * normalizedPeakWeight +
                            normalizedVolume * normalizedVolumeWeight
                    )
                );

                return {
                    ...groupedProduct,
                    relevanceScore: Number(relevanceScore.toFixed(4)),
                    confidence: Math.round(relevanceScore * 100),
                    averageScore: Number(averageScore.toFixed(4)),
                    maxScore: Number(scoreStats.scoreMax.toFixed(4)),
                    scoreSampleCount: scoreStats.scoreCount,
                    rerankerScores
                };
            });

            return rankedProducts.sort((a, b) => {
                const leftConfidence = Number(a.confidence);
                const rightConfidence = Number(b.confidence);
                const leftHasConfidence = Number.isFinite(leftConfidence);
                const rightHasConfidence = Number.isFinite(rightConfidence);
                if (leftHasConfidence && rightHasConfidence && rightConfidence !== leftConfidence) {
                    return rightConfidence - leftConfidence;
                }
                if (leftHasConfidence !== rightHasConfidence) {
                    return leftHasConfidence ? -1 : 1;
                }
                if (b.count !== a.count) return b.count - a.count;
                const leftLabel = String(a.displayText ?? a.productText ?? "");
                const rightLabel = String(b.displayText ?? b.productText ?? "");
                return leftLabel.localeCompare(rightLabel);
            });
        },
        [allProductAutoSelectWeightAverage, allProductAutoSelectWeightPeak, allProductAutoSelectWeightVolume, products]
    );

    const buildSearchCitationInsights = useCallback((dataPoints: DataPoints[]): AllProductCitationInsight[] => {
        const normalizeJoinedLabel = (value: unknown, fallback = "-"): string => {
            if (Array.isArray(value)) {
                const joined = value
                    .map(item => String(item ?? "").trim())
                    .filter(Boolean)
                    .join(", ");
                return joined || fallback;
            }
            const normalized = String(value ?? "").trim();
            return normalized || fallback;
        };

        const toNumericScore = (value: unknown): number | null => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? Number(parsed.toFixed(4)) : null;
        };

        return dataPoints.slice(0, 80).map((dataPoint, index) => {
            const sourceLabel = normalizeJoinedLabel(dataPoint.sourcepage_field, normalizeJoinedLabel(dataPoint.content_field, "-"));
            const productLabel = normalizeJoinedLabel(dataPoint.product_full_name, normalizeJoinedLabel(dataPoint.product_name, "-"));
            const versionLabel = normalizeJoinedLabel(
                dataPoint.version_name,
                [dataPoint.start_version, dataPoint.end_version].some(value => value !== undefined && value !== null && String(value).trim() !== "")
                    ? `${normalizeJoinedLabel(dataPoint.start_version, "-")} ~ ${normalizeJoinedLabel(dataPoint.end_version, "-")}`
                    : "-"
            );
            const subProductLabel = normalizeJoinedLabel(dataPoint.sub_product_name, "-");
            const searchScore = toNumericScore((dataPoint as any).search_score);
            const rerankerScore = toNumericScore((dataPoint as any).search_reranker_score);
            return {
                id: [
                    String(dataPoint.uploaddir_field ?? ""),
                    String(dataPoint.sourcepage_field ?? ""),
                    String(dataPoint.content_field ?? "").slice(0, 48),
                    String(index)
                ].join("|"),
                sourceLabel,
                productLabel,
                versionLabel,
                subProductLabel,
                searchScore,
                rerankerScore
            };
        });
    }, []);

    const buildScoreCandidateInsights = useCallback((matchedProducts: AllProductMatchedProduct[]): AllProductScoreInsight[] => {
        return matchedProducts
            .map((product, index) => ({
                id: [
                    product.manual_key,
                    product.faq_key,
                    product.productVersion?.manual_version_key ?? "",
                    product.productVersion?.faq_version_key ?? "",
                    product.subProduct?.manual_sub_key ?? "",
                    product.subProduct?.faq_sub_key ?? "",
                    index
                ].join("|"),
                productLabel: String(product.displayText ?? product.productText ?? "-"),
                confidence: Number.isFinite(Number(product.confidence)) ? Math.round(Number(product.confidence)) : null,
                citationCount: Number.isFinite(Number(product.count)) ? Math.max(0, Number(product.count)) : 0,
                averageScore: Number.isFinite(Number(product.averageScore)) ? Number(Number(product.averageScore).toFixed(4)) : null,
                maxScore: Number.isFinite(Number(product.maxScore)) ? Number(Number(product.maxScore).toFixed(4)) : null,
                scoreSampleCount: Number.isFinite(Number(product.scoreSampleCount)) ? Math.max(0, Number(product.scoreSampleCount)) : 0
            }))
            .sort((left, right) => {
                const leftConfidence = Number(left.confidence ?? -1);
                const rightConfidence = Number(right.confidence ?? -1);
                if (rightConfidence !== leftConfidence) return rightConfidence - leftConfidence;
                if (right.citationCount !== left.citationCount) return right.citationCount - left.citationCount;
                return left.productLabel.localeCompare(right.productLabel);
            });
    }, []);

    const runAllProductCitationPrecheck = useCallback(
        async (sendRequest: PreparedSendRequest, onProgress?: (update: AllProductPrecheckProgressUpdate) => void): Promise<AllProductPrecheckResult> => {
            const userDefinedTemplateId = userInfo?.["userFavourite"]?.[selectedProduct];
            const systemDefinedTemplateID = templateContents.filter((temp: any) => temp.template[0].isDefault === true).map((temp: any) => temp.id)[0];
            const templateId = selectedTemplateContentId ? selectedTemplateContentId : userDefinedTemplateId ? userDefinedTemplateId : systemDefinedTemplateID;
            setClarificationInfo(null);
            const questionForAI =
                sendRequest.questionContainsTemplateValue && !!(templateId && templateId !== "") && templateId !== "None"
                    ? reformQuestionContentForAI(sendRequest.questionText, templateId, templateContents)
                    : sendRequest.questionText;

            const selectedLocaleValue = sessionStorage.getItem("selectedLocale") ?? "ja";
            const precheckProductFilter = setProductFilterForRequestPayload(
                undefined,
                sendRequest.appTypeBasedOnUser,
                currentView,
                productIndexes,
                selectedLocaleValue
            );
            const precheckSubProductFilter = setSubProductFilterForRequestPayload(
                undefined,
                sendRequest.appTypeBasedOnUser,
                currentView,
                subProductIndexes,
                selectedLocaleValue
            );
            const precheckProductVersion = setProductVersionForRequestPayload(
                undefined,
                sendRequest.appTypeBasedOnUser,
                currentView,
                productVersionIndexes,
                selectedLocaleValue
            );

            const groupedFilterMeta = setGroupIdsByAppTypeForRequestPayload(
                sendRequest.appTypeBasedOnUser,
                precheckProductFilter,
                precheckSubProductFilter,
                selectedLocaleValue
            );

            const groupIds =
                groupedFilterMeta && groupedFilterMeta.groupIds && groupedFilterMeta.groupIds.size > 0
                    ? [...groupedFilterMeta.groupIds].filter(id => id !== "").join(",")
                    : "0";
            const subgroupIds =
                groupedFilterMeta && groupedFilterMeta.sub_group_ids && groupedFilterMeta.sub_group_ids.size > 0
                    ? [...groupedFilterMeta.sub_group_ids].filter(id => id !== "").join(",")
                    : "0";

            const currentFileAttachmentIds = store
                .getState()
                .questionInput.serverSideFileAttachmentsIds.map(v => v.serverSideId)
                .slice()
                .sort()
                .join(",");

            const precheckFingerprint = JSON.stringify({
                q: questionForAI,
                appType: sendRequest.appTypeBasedOnUser,
                templateId: templateId ?? "",
                locale: selectedLocaleValue,
                product_filter: precheckProductFilter,
                sub_product_filter: precheckSubProductFilter,
                product_version: precheckProductVersion,
                group_ids: groupIds,
                sub_group_ids: subgroupIds,
                doctype_filter: !isWapUser ? "External" : docTypeFilter,
                language: selectedLanguage,
                chat_model: chatModel,
                retrieval_mode: retrievalMode,
                cosmosdb_retrieval_mode: cosmosDBRetrievalMode,
                top: retrieveCount,
                retrieval_reasoning_effort: retrievalReasoningEffort,
                reasoning_effort: isReasoningModel(chatModel) ? reasoningEffort : null,
                semantic_ranker: useSemanticRanker,
                semantic_captions: useSemanticCaptions,
                use_oid_security_filter: useOidSecurityFilter,
                use_groups_security_filter: useGroupsSecurityFilter,
                enable_OCR: appInfo?.enable_OCR ?? false,
                use_agentic_knowledgebase: useAgenticKnowledgebase,
                agentic_retrieval_output_size: normalizeAgenticRetrievalOutputSize(agenticRetrievalOutputSize),
                agentic_retrieval_document_count: normalizeAgenticRetrievalDocumentCount(agenticRetrievalDocumentCount),
                agentic_retrieval_source_document_count: normalizeAgenticRetrievalSourceDocumentCount(agenticRetrievalSourceDocumentCount),
                file_attachment_ids: currentFileAttachmentIds
            });

            const cached = precheckResultCacheRef.current;
            if (cached && cached.fingerprint === precheckFingerprint) {
                onProgress?.({
                    phase: "search",
                    matchedProducts: cached.matchedProducts,
                    searchCitations: cached.searchCitations
                });
                onProgress?.({
                    phase: "score",
                    matchedProducts: cached.matchedProducts,
                    scoreCandidates: cached.scoreCandidates
                });
                // Precheck already validated — skip re-validation in the final makeApiRequest.
                skipValidationOnceRef.current = true;
                return { matchedProducts: cached.matchedProducts, selectionDecision: cached.selectionDecision ?? null };
            }

            const request: ChatAppRequest = {
                messages: [{ content: questionForAI, role: "user" }],
                stream: true,
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        language: selectedLanguage,
                        top: retrieveCount,
                        chat_model: chatModel,
                        reasoning_effort: isReasoningModel(chatModel) ? reasoningEffort : undefined,
                        retrieval_reasoning_effort: retrievalReasoningEffort,
                        only_citations: true,
                        skip_save: true,
                        retrieval_mode: retrievalMode,
                        cosmosdb_retrieval_mode: cosmosDBRetrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        suggest_followup_questions: false,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        enable_OCR: appInfo?.enable_OCR ?? false,
                        doctype_filter: !isWapUser ? "External" : docTypeFilter,
                        product_filter: precheckProductFilter,
                        sub_product_filter: precheckSubProductFilter,
                        product_version: precheckProductVersion,
                        group_ids: groupIds,
                        sub_group_ids: subgroupIds,
                        template_keys: "",
                        templateContents: "",
                        templateId: templateId,
                        extractedQuestion: sendRequest.questionText,
                        latest_user_request: sendRequest.questionText,
                        promptInstructions: "",
                        chat_summary_title: sendRequest.questionText,
                        fromHistory: false,
                        sessionId: (() => {
                            const stored = sessionStorage.getItem("sessionId");
                            if (stored && stored !== "") return stored;
                            if (selectedSessionId && selectedSessionId !== "") {
                                sessionStorage.setItem("sessionId", selectedSessionId);
                                return selectedSessionId;
                            }
                            const newId = generateNewSessionId();
                            sessionStorage.setItem("sessionId", newId);
                            setSelectedSessionId(newId);
                            return newId;
                        })(),
                        versions: {},
                        question_id: generateNewSessionId(),
                        has_question_id: false,
                        old_chat_id: undefined,
                        edited_question_id: null,
                        is_chat_shared: false,
                        source_shared_session_id: null,
                        combinedAppTypeResponse: false,
                        isAppTypeIntegrated: sendRequest.appTypeBasedOnUser === "ALL",
                        isRegenerated: false,
                        file_attachment_ids: deduplicateFileAttachments(
                            store.getState().questionInput.serverSideFileAttachmentsIds.map(value => ({
                                serverSideId: value.serverSideId,
                                fileName: value.original_filename
                            }))
                        ),
                        use_agentic_knowledgebase: useAgenticKnowledgebase,
                        agentic_retrieval_output_size: normalizeAgenticRetrievalOutputSize(agenticRetrievalOutputSize),
                        agentic_retrieval_document_count: normalizeAgenticRetrievalDocumentCount(agenticRetrievalDocumentCount),
                        agentic_retrieval_source_document_count: normalizeAgenticRetrievalSourceDocumentCount(agenticRetrievalSourceDocumentCount),
                        // Consumed (true) after "Send anyway" in the clarification dialog.
                        // Also skipped when file attachments are present or this is a follow-up (not the first question).
                        skip_validation: (() => {
                            const val = skipValidationOnceRef.current;
                            skipValidationOnceRef.current = false;
                            const hasAttachments = store.getState().questionInput.serverSideFileAttachmentsIds.length > 0;
                            return val || hasAttachments || answers.length > 0;
                        })()
                    }
                },
                session_state: null
            };

            if (allProductPrecheckControllerRef.current) {
                allProductPrecheckControllerRef.current.abort();
            }
            const controller = new AbortController();
            allProductPrecheckControllerRef.current = controller;

            try {
                const response = await chatPrecheckApi(request, sendRequest.appTypeBasedOnUser, userInfo.corp_id, controller.signal);
                if (!response?.ok || response?.status > 299) {
                    return { matchedProducts: [], selectionDecision: null };
                }

                const accumulatedDataPoints: DataPoints[] = [];
                const seenDataPointKeys = new Set<string>();
                let latestMatchedProducts: AllProductMatchedProduct[] = [];
                let latestSearchCitations: AllProductCitationInsight[] = [];
                let latestSelectionDecision: AllProductPrecheckSelectionDecision | null = null;
                let latestQuestionValidation: AllProductPrecheckResult["questionValidation"] = null;
                let latestKeywords = "";

                const buildDataPointKey = (dataPoint: DataPoints, index: number): string =>
                    [
                        String(dataPoint.uploaddir_field ?? ""),
                        String(dataPoint.sourcepage_field ?? ""),
                        String(dataPoint.content_field ?? "").slice(0, 64),
                        String((dataPoint as any).search_score ?? ""),
                        String((dataPoint as any).search_reranker_score ?? ""),
                        String(index)
                    ].join("|");

                const emitSearchProgress = () => {
                    latestMatchedProducts = mapMatchedProductsFromDataPoints(accumulatedDataPoints);
                    latestSearchCitations = buildSearchCitationInsights(accumulatedDataPoints);
                    onProgress?.({
                        phase: "search",
                        matchedProducts: latestMatchedProducts,
                        searchCitations: latestSearchCitations
                    });
                };

                const appendDataPoints = (incomingDataPoints: DataPoints[]) => {
                    let hasNewData = false;
                    incomingDataPoints.forEach((dataPoint, index) => {
                        const dataPointKey = buildDataPointKey(dataPoint, index);
                        if (seenDataPointKeys.has(dataPointKey)) return;
                        seenDataPointKeys.add(dataPointKey);
                        accumulatedDataPoints.push(dataPoint);
                        hasNewData = true;
                    });
                    if (hasNewData) {
                        emitSearchProgress();
                    }
                };

                if (response.body) {
                    for await (const event of readNDJSONStream(response.body)) {
                        if (!event || typeof event !== "object") continue;
                        if (event["error"]) {
                            throw Error(String(event["error"]));
                        }
                        const context = event["context"];
                        if (!context || typeof context !== "object") continue;
                        const streamedChunk = Array.isArray((context as any).precheck_data_points_chunk)
                            ? ((context as any).precheck_data_points_chunk as DataPoints[])
                            : [];
                        const fullContextPoints = Array.isArray((context as any).data_points) ? ((context as any).data_points as DataPoints[]) : [];
                        if (streamedChunk.length > 0) appendDataPoints(streamedChunk);
                        if (fullContextPoints.length > 0) appendDataPoints(fullContextPoints);
                        if ((context as any).precheck_selection_decision && typeof (context as any).precheck_selection_decision === "object") {
                            latestSelectionDecision = (context as any).precheck_selection_decision as AllProductPrecheckSelectionDecision;
                        }
                        // Capture keywords from the backend query-rewrite so the main call can skip its own rewrite
                        if (typeof (context as any).keywords === "string" && (context as any).keywords) {
                            latestKeywords = (context as any).keywords;
                        }
                        // Detect backend question-validation rejection
                        if ((context as any).question_validation?.is_vague) {
                            latestQuestionValidation = (context as any).question_validation as AllProductPrecheckResult["questionValidation"];
                        }
                    }
                }

                // Question was rejected by the backend — show dialog and reset QuestionInput to idle
                if (latestQuestionValidation?.is_vague) {
                    const capturedMode: RequestSourceMode = sendRequest.sourceMode ?? "chat";
                    pendingQuestionSendRef.current = () => {
                        // Re-trigger through QuestionInput so the full ALL-product precheck flow
                        // (progress bar → suggestion panel) runs again with skip_validation:true.
                        const inputRef = questionInputRefs.current[capturedMode];
                        if (inputRef) {
                            inputRef.triggerSendWithQuestion(sendRequest.questionText);
                        } else {
                            handleQuestionInputSendRef.current(
                                capturedMode,
                                sendRequest.questionText,
                                sendRequest.questionContainsTemplateValue,
                                sendRequest.appTypeBasedOnUser
                            );
                        }
                    };
                    setClarificationInfo({ type: latestQuestionValidation.type, message: latestQuestionValidation.message || "" });
                    setClarificationOriginalQuestion(sendRequest.questionText);
                    clarificationSendContextRef.current = {
                        mode: capturedMode,
                        appType: sendRequest.appTypeBasedOnUser,
                        questionContainsTemplate: sendRequest.questionContainsTemplateValue
                    };
                    // Show validation feedback inline in the question input instead of a dialog
                    setClarificationMode(capturedMode);
                    setTimeout(() => {
                        questionInputRefs.current[capturedMode]?.setQuestionInputValue(sendRequest.questionText);
                    }, 0);
                    return { matchedProducts: [], selectionDecision: null, questionValidation: latestQuestionValidation };
                }

                if (accumulatedDataPoints.length === 0) {
                    // Validation passed but no products matched — skip re-validation in makeApiRequest.
                    skipValidationOnceRef.current = true;
                    return { matchedProducts: [], selectionDecision: null };
                }

                if (latestMatchedProducts.length === 0) {
                    latestMatchedProducts = mapMatchedProductsFromDataPoints(accumulatedDataPoints);
                    latestSearchCitations = buildSearchCitationInsights(accumulatedDataPoints);
                }
                const effectiveBackendSelection = resolvePrecheckAutoSelectedProduct(latestMatchedProducts, latestSelectionDecision, products);
                if (effectiveBackendSelection && !latestMatchedProducts.includes(effectiveBackendSelection)) {
                    latestMatchedProducts = [effectiveBackendSelection, ...latestMatchedProducts];
                }
                const scoreCandidates = buildScoreCandidateInsights(latestMatchedProducts);

                if (scoreCandidates.length > 0) {
                    const maxAnimatedRows = Math.min(scoreCandidates.length, 8);
                    const scoringPhaseTotalHoldMs = 500;
                    const perRowDelayMs = scoringPhaseTotalHoldMs / maxAnimatedRows;
                    for (let visibleCount = 1; visibleCount <= maxAnimatedRows; visibleCount += 1) {
                        onProgress?.({
                            phase: "score",
                            matchedProducts: latestMatchedProducts,
                            scoreCandidates: scoreCandidates.slice(0, visibleCount)
                        });
                        await new Promise(resolve => setTimeout(resolve, perRowDelayMs));
                    }
                    if (scoreCandidates.length > maxAnimatedRows) {
                        onProgress?.({
                            phase: "score",
                            matchedProducts: latestMatchedProducts,
                            scoreCandidates
                        });
                    }
                } else {
                    onProgress?.({
                        phase: "score",
                        matchedProducts: latestMatchedProducts,
                        scoreCandidates: []
                    });
                }

                precheckResultCacheRef.current = {
                    fingerprint: precheckFingerprint,
                    keywords: latestKeywords,
                    matchedProducts: latestMatchedProducts,
                    searchCitations: latestSearchCitations,
                    scoreCandidates,
                    selectionDecision: latestSelectionDecision
                };
                // Precheck validated the question — skip re-validation in the final makeApiRequest.
                skipValidationOnceRef.current = true;
                return { matchedProducts: latestMatchedProducts, selectionDecision: latestSelectionDecision };
            } catch (error) {
                if (isAbortError(error)) {
                    // Do not clear the cache on abort — user may re-send with the same request.
                    return { matchedProducts: [], selectionDecision: null };
                }
                console.error("Error while running ALL product precheck:", error);
                return { matchedProducts: [], selectionDecision: null };
            } finally {
                if (allProductPrecheckControllerRef.current === controller) {
                    allProductPrecheckControllerRef.current = null;
                }
            }
        },
        [
            answers,
            chatModel,
            cosmosDBRetrievalMode,
            currentView,
            docTypeFilter,
            excludeCategory,
            mapMatchedProductsFromDataPoints,
            buildSearchCitationInsights,
            buildScoreCandidateInsights,
            productIndexes,
            productVersionIndexes,
            promptTemplate,
            reasoningEffort,
            retrievalMode,
            retrievalReasoningEffort,
            retrieveCount,
            selectedAppType,
            selectedLanguage,
            selectedProduct,
            selectedTemplateContentId,
            subProductIndexes,
            templateContents,
            useGroupsSecurityFilter,
            useOidSecurityFilter,
            useSemanticCaptions,
            useSemanticRanker,
            useAgenticKnowledgebase,
            agenticRetrievalOutputSize,
            agenticRetrievalDocumentCount,
            agenticRetrievalSourceDocumentCount
        ]
    );

    const syncSuggestionsPanelState = useCallback(
        (stage: "idle" | "loading" | "confirm" | "select", matchedProducts: AllProductMatchedProduct[], message: string, isLocked: boolean) => {
            dispatch(
                AllProductSuggestionsAction.syncSuggestionsState({
                    stage,
                    matchedProducts,
                    message,
                    isLocked
                })
            );
        },
        [dispatch]
    );

    const resetProductScenarioSuggestions = useCallback(
        (abortInFlight = false) => {
            if (abortInFlight) {
                abortAllProductCitationPrecheck();
            }
            answerProductScenarioRequestIdRef.current += 1;
            setProductScenarioRegenerateContext(null);
            dispatch(AllProductSuggestionsAction.resetSuggestions());
        },
        [abortAllProductCitationPrecheck, dispatch]
    );

    const setSuggestionsPanelLock = useCallback(
        (isLocked: boolean) => {
            const currentSuggestionsState = store.getState().allProductSuggestions;
            syncSuggestionsPanelState(currentSuggestionsState.stage, currentSuggestionsState.matchedProducts, currentSuggestionsState.message, isLocked);
        },
        [syncSuggestionsPanelState]
    );

    const startProductScenarioRegenerateFlow = useCallback(
        async (primaryId: number, targetAppType: AppType) => {
            const previousQuestionDetails: any = JSON.parse(JSON.stringify(answers[primaryId]));
            if (!previousQuestionDetails?.[0]) {
                return;
            }

            const targetResponse = getAnswerResponseForAppType(previousQuestionDetails?.[1], targetAppType);
            if (!targetResponse) {
                return;
            }

            const nextRequestId = answerProductScenarioRequestIdRef.current + 1;
            answerProductScenarioRequestIdRef.current = nextRequestId;
            const flowSessionId = normalizeSessionIdentity(store.getState().chatComponent.currentSessionId ?? sessionStorage.getItem("sessionId"));
            activeSuggestionsSessionIdRef.current = flowSessionId;
            const shouldApplyScenarioResponse = () =>
                answerProductScenarioRequestIdRef.current === nextRequestId && activeSuggestionsSessionIdRef.current === flowSessionId;
            const reasoningEffortForRegenerate = (targetResponse?.context?.reasoning_effort ?? reasoningEffort ?? ReasoningEffort.Low) as ReasoningEffort;
            const nextContext: ProductScenarioRegenerateContext = {
                primaryId,
                targetAppType,
                reasoningEffort: reasoningEffortForRegenerate,
                convId: targetResponse?.context?.conv_id ?? ""
            };

            setSelectedAnswer(primaryId);
            setSelectedAppType(targetAppType);
            currentTargetQuestionId.current = primaryId;
            currentQuestionInfo.current = null;
            isRegenerateActive.current = false;
            setProductScenarioRegenerateContext(nextContext);
            syncSuggestionsPanelState("loading", [], "", false);

            const selectedLocaleValue = sessionStorage.getItem("selectedLocale") ?? "ja";
            const questionDetails = previousQuestionDetails[0];
            let { question, extractedQuestion, templateId } = questionDetails;
            const resolvedTemplatePayload = resolveTemplatePayload(
                question,
                extractedQuestion,
                templateContents,
                templateId,
                undefined,
                undefined,
                isValidTemplateId
            );
            const questionContentForAI = resolvedTemplatePayload.questionContentForAI;
            templateId = resolvedTemplatePayload.templateId;

            const baseProductFilter = targetResponse?.context?.product_filter ?? { manual_key: "", faq_key: "" };
            const regenerateBaseProductIndexes = {
                manual_key: normalizeComparableProductKey(baseProductFilter?.manual_key),
                faq_key: normalizeComparableProductKey(baseProductFilter?.faq_key)
            };
            const { messages, previousQuestionIds } = buildRegenerateMessages(
                answers,
                primaryId,
                targetAppType,
                regenerateBaseProductIndexes,
                appInfo,
                selectedLocaleValue
            );
            const idsToLookup = Array.from(new Set([targetResponse?.context?.question_id as string, ...previousQuestionIds])).filter(Boolean);
            const previousAttachmentIds = idsToLookup.length > 0 ? await getServerSideIdsOfAttachments(idsToLookup) : [];

            const request: ChatAppRequest = {
                messages: [...messages, { content: questionContentForAI, role: "user" }],
                stream: false,
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        language: selectedLanguage,
                        top: retrieveCount,
                        chat_model: chatModel,
                        reasoning_effort: isReasoningModel(chatModel) ? reasoningEffortForRegenerate : undefined,
                        retrieval_reasoning_effort: retrievalReasoningEffort,
                        only_citations: true,
                        skip_save: true,
                        retrieval_mode: retrievalMode,
                        cosmosdb_retrieval_mode: cosmosDBRetrievalMode,
                        semantic_ranker: useSemanticRanker,
                        semantic_captions: useSemanticCaptions,
                        suggest_followup_questions: false,
                        use_oid_security_filter: useOidSecurityFilter,
                        use_groups_security_filter: useGroupsSecurityFilter,
                        enable_OCR: appInfo?.enable_OCR ?? false,
                        doctype_filter: !isWapUser ? "External" : (targetResponse?.context?.document_category as string) || docTypeFilter,
                        product_filter: { manual_key: "0", faq_key: "0" },
                        sub_product_filter: { manual_sub_key: "0", faq_sub_key: "0" },
                        product_version: { manual_version_key: "0", faq_version_key: "0" },
                        group_ids: "0",
                        sub_group_ids: "0",
                        template_keys: "",
                        templateContents: "",
                        templateId,
                        extractedQuestion,
                        latest_user_request: extractedQuestion,
                        promptInstructions: "",
                        chat_summary_title: extractedQuestion,
                        fromHistory: false,
                        sessionId: (() => {
                            const stored = sessionStorage.getItem("sessionId");
                            if (stored && stored !== "") return stored;
                            if (selectedSessionId && selectedSessionId !== "") {
                                sessionStorage.setItem("sessionId", selectedSessionId);
                                return selectedSessionId;
                            }
                            const newId = generateNewSessionId();
                            sessionStorage.setItem("sessionId", newId);
                            setSelectedSessionId(newId);
                            return newId;
                        })(),
                        versions: {},
                        question_id: targetResponse?.context?.question_id ?? generateNewSessionId(),
                        has_question_id: true,
                        old_chat_id: targetResponse?.context?.conv_id,
                        edited_question_id: null,
                        is_chat_shared: false,
                        source_shared_session_id: null,
                        combinedAppTypeResponse: Boolean(questionDetails?.combinedAppTypeResponse),
                        isAppTypeIntegrated: targetAppType === "ALL",
                        isRegenerated: true,
                        file_attachment_ids: Array.from(new Set(previousAttachmentIds)),
                        use_agentic_knowledgebase: useAgenticKnowledgebase,
                        agentic_retrieval_output_size: normalizeAgenticRetrievalOutputSize(agenticRetrievalOutputSize),
                        agentic_retrieval_document_count: normalizeAgenticRetrievalDocumentCount(agenticRetrievalDocumentCount),
                        agentic_retrieval_source_document_count: normalizeAgenticRetrievalSourceDocumentCount(agenticRetrievalSourceDocumentCount),
                        skip_validation: true
                    }
                },
                session_state: null
            };

            const controller = new AbortController();
            allProductPrecheckControllerRef.current?.abort();
            allProductPrecheckControllerRef.current = controller;

            try {
                const response = await chatPrecheckApi(request, getAppTypeBasedOnUser(targetAppType), userInfo.corp_id, controller.signal);
                const parsedResponse = await response?.json();
                if (!response?.ok || response?.status > 299) {
                    if (shouldApplyScenarioResponse()) {
                        syncSuggestionsPanelState(
                            "confirm",
                            [],
                            i18next.t("questionInput.allProductValidation.fallbackConfirmMessage", {
                                defaultValue: "No direct match was found. Continue with ALL or cancel."
                            }),
                            false
                        );
                    }
                    return;
                }

                const dataPoints: DataPoints[] = Array.isArray(parsedResponse?.context?.data_points) ? parsedResponse.context.data_points : [];
                const matchedProducts = mapMatchedProductsFromDataPoints(dataPoints);
                const selectionDecision: AllProductPrecheckSelectionDecision | null = parsedResponse?.context?.precheck_selection_decision ?? null;
                if (!shouldApplyScenarioResponse()) {
                    return;
                }

                if (matchedProducts.length > 0) {
                    // Always show product list for user to select (no auto-select on answer panel switch)
                    const manualSelectionMessage =
                        selectionDecision?.action === "manual_select" && selectionDecision.reason?.trim()
                            ? selectionDecision.reason.trim()
                            : i18next.t("questionInput.allProductValidation.matchedMessage", {
                                  defaultValue: "Matched products from citations are shown below. Select one product to continue, or continue with ALL."
                              });
                    const scenarioSearchCitations = buildSearchCitationInsights(dataPoints);
                    const scenarioScoreCandidates = buildScoreCandidateInsights(matchedProducts);
                    dispatch(
                        AllProductSuggestionsAction.syncSuggestionsState({
                            stage: "select",
                            matchedProducts,
                            message: manualSelectionMessage,
                            isLocked: false,
                            skipReasonCode: selectionDecision?.action === "manual_select" ? (selectionDecision.reason_code ?? "model_unsure") : null,
                            searchCitations: scenarioSearchCitations,
                            scoreCandidates: scenarioScoreCandidates
                        })
                    );
                } else {
                    syncSuggestionsPanelState(
                        "confirm",
                        [],
                        i18next.t("questionInput.allProductValidation.fallbackConfirmMessage", {
                            defaultValue: "No direct match was found. Continue with ALL or cancel."
                        }),
                        false
                    );
                }
            } catch (error) {
                if (isAbortError(error)) {
                    return;
                }
                console.error("Error while running product-change precheck:", error);
                if (shouldApplyScenarioResponse()) {
                    syncSuggestionsPanelState(
                        "confirm",
                        [],
                        i18next.t("questionInput.allProductValidation.fallbackConfirmMessage", {
                            defaultValue: "No direct match was found. Continue with ALL or cancel."
                        }),
                        false
                    );
                }
            } finally {
                if (allProductPrecheckControllerRef.current === controller) {
                    allProductPrecheckControllerRef.current = null;
                }
            }
        },
        [
            answers,
            buildRegenerateMessages,
            chatModel,
            cosmosDBRetrievalMode,
            currentTargetQuestionId,
            dispatch,
            docTypeFilter,
            excludeCategory,
            getServerSideIdsOfAttachments,
            mapMatchedProductsFromDataPoints,
            promptTemplate,
            reasoningEffort,
            regenerateRequest,
            resetProductScenarioSuggestions,
            retrievalMode,
            retrievalReasoningEffort,
            retrieveCount,
            selectedLanguage,
            setSelectedAnswer,
            buildSearchCitationInsights,
            buildScoreCandidateInsights,
            syncSuggestionsPanelState,
            templateContents,
            useAgenticKnowledgebase,
            agenticRetrievalOutputSize,
            agenticRetrievalDocumentCount,
            agenticRetrievalSourceDocumentCount,
            useGroupsSecurityFilter,
            useOidSecurityFilter,
            useSemanticCaptions,
            useSemanticRanker
        ]
    );

    // Keep the ref in sync with the latest callback on every render.
    startProductScenarioRegenerateFlowRef.current = startProductScenarioRegenerateFlow;

    const handleProductScenarioRegenerateCancel = useCallback(() => {
        resetProductScenarioSuggestions(true);
    }, [resetProductScenarioSuggestions]);

    const handleProductScenarioRegenerateContinueWithAll = useCallback(async () => {
        if (!productScenarioRegenerateContext) {
            resetProductScenarioSuggestions(false);
            return;
        }

        const { primaryId, targetAppType, reasoningEffort: nextReasoningEffort, convId } = productScenarioRegenerateContext;
        setSuggestionsPanelLock(true);
        dispatch(QuestionInputReducerAction.updateRequestTriggerType("RegenerateClick"));
        lastAnswerIndexRef.current = primaryId;
        currentTargetQuestionId.current = primaryId;
        setSelectedAnswer(primaryId);
        setSelectedAppType(targetAppType);
        currentQuestionInfo.current = null;
        isRegenerateActive.current = true;
        resetProductScenarioSuggestions(false);
        const isAlreadyAllSelection =
            normalizeComparableProductKey(productIndexes.manual_key) === "0" && normalizeComparableProductKey(productIndexes.faq_key) === "0";
        if (!isAlreadyAllSelection || productText !== "ALL") {
            markTemplateApplySourceForNextSwitch("programmatic", "ALL");
            setProductIndexes({ manual_key: "0", faq_key: "0" });
            setProductText("ALL");
            setProductVersionIndexes({ manual_version_key: "0", faq_version_key: "0" });
            setProductVersionText("ALL");
            setSubProductIndexes({ manual_sub_key: "0", faq_sub_key: "0" });
            setSubProductText("ALL");
            dispatch(setSelectedProductInStore("ALL"));
        }
        await regenerateRequest(nextReasoningEffort, primaryId, convId, {
            targetAppType,
            forcedProductSelection: {
                product_filter: { manual_key: "0", faq_key: "0" },
                sub_product_filter: { manual_sub_key: "0", faq_sub_key: "0" },
                product_version: { manual_version_key: "0", faq_version_key: "0" }
            }
        });
    }, [
        currentTargetQuestionId,
        currentQuestionInfo,
        dispatch,
        productIndexes.faq_key,
        productIndexes.manual_key,
        productText,
        productScenarioRegenerateContext,
        regenerateRequest,
        resetProductScenarioSuggestions,
        setProductIndexes,
        setProductText,
        setProductVersionIndexes,
        setProductVersionText,
        setSuggestionsPanelLock,
        setSelectedAnswer,
        setSelectedAppType,
        setSubProductIndexes,
        setSubProductText
    ]);

    const handleProductScenarioRegenerateSelectProduct = useCallback(
        async (selectedProductOption: AllProductMatchedProduct) => {
            if (!productScenarioRegenerateContext) {
                resetProductScenarioSuggestions(false);
                return;
            }

            const { primaryId, targetAppType, reasoningEffort: nextReasoningEffort, convId } = productScenarioRegenerateContext;
            setSuggestionsPanelLock(true);
            isRegenerateActive.current = true;
            suppressTemplateAutoApplyWhileRegenerateSelectionRef.current = true;

            // Track whether resetProductScenarioSuggestions has been called so the catch
            // block knows whether the panel is still visible and needs a fallback state.
            let scenarioPanelWasReset = false;
            try {
                const nextAppType = getValidAppType(targetAppType);
                const { forcedProductSelection } = await applyMatchedProductSelection(selectedProductOption, nextAppType);
                dispatch(QuestionInputReducerAction.updateRequestTriggerType("RegenerateClick"));
                lastAnswerIndexRef.current = primaryId;
                currentTargetQuestionId.current = primaryId;
                setSelectedAnswer(primaryId);
                currentQuestionInfo.current = null;
                resetProductScenarioSuggestions(false);
                scenarioPanelWasReset = true;
                await regenerateRequest(nextReasoningEffort, primaryId, convId, {
                    targetAppType: nextAppType,
                    forcedProductSelection
                });
            } catch (error) {
                console.error("Failed to regenerate answer with changed product scenario", {
                    error,
                    selectedProductOption,
                    productScenarioRegenerateContext
                });
                isRegenerateActive.current = false;
                if (!scenarioPanelWasReset) {
                    // applyMatchedProductSelection threw before the panel was cleared —
                    // show the confirm fallback so the user can still cancel or continue with ALL.
                    dispatch(
                        AllProductSuggestionsAction.syncSuggestionsState({
                            stage: "confirm",
                            matchedProducts: [],
                            message: i18next.t("questionInput.allProductValidation.fallbackConfirmMessage"),
                            isLocked: false
                        })
                    );
                } else {
                    setSuggestionsPanelLock(false);
                }
            } finally {
                suppressTemplateAutoApplyWhileRegenerateSelectionRef.current = false;
            }
        },
        [
            currentTargetQuestionId,
            currentQuestionInfo,
            dispatch,
            productScenarioRegenerateContext,
            regenerateRequest,
            resetProductScenarioSuggestions,
            setSuggestionsPanelLock,
            setSelectedAnswer
        ]
    );

    const toolbarProps = useMemo(
        () => ({
            products,
            productText,
            subProductText,
            productVersionText,
            productIndexes,
            subProductIndexes,
            productVersionIndexes,
            windowSize,
            shouldDisableInputBox: () => shouldDisableInputBox(resolvedProductLocale, appInfo),
            onProductClick: handleProductClick,
            onSubProductClick: handleSubProductClick,
            onVersionClick: handleVersionClick,
            handleTopPosition: (refs: any, index: number, versionsLength?: number) => handleTopPosition(refs, index, versionsLength || 0).toString(),
            isWapUser,
            isAdmin,
            enableAllProductForAllUsers,
            enableAllProductForUser,
            docTypeFilter,
            categories,
            chatModel,
            reasoningEffort,
            aiModel_admin,
            templateAppliedFlagValue,
            activeAnalysisPanelTab,
            applyRestriction,
            isStreaming,
            isLoading,
            isAnswerLoading,
            activateTemplateTour,
            setActivateTemplateTour,
            styles,
            onDocTypeFilterChange,
            onChatModelChange,
            onReasoningEffortChange,
            onConfigPanelToggle: () => setIsConfigPanelOpen(previous => !previous),
            onTemplateIconClick: handleTemplateIcon,
            onTemplateClear,
            saveUserSettings
        }),
        [
            activeAnalysisPanelTab,
            aiModel_admin,
            applyRestriction,
            categories,
            chatModel,
            docTypeFilter,
            isAnswerLoading,
            isLoading,
            isStreaming,
            onChatModelChange,
            onDocTypeFilterChange,
            onReasoningEffortChange,
            onTemplateClear,
            productIndexes,
            productText,
            productVersionIndexes,
            productVersionText,
            products,
            reasoningEffort,
            selectedLocale,
            subProductIndexes,
            subProductText,
            templateAppliedFlagValue,
            windowSize,
            activateTemplateTour,
            setActivateTemplateTour,
            enableAllProductForAllUsers,
            enableAllProductForUser
        ]
    );

    const handleQuestionInputSend = useCallback(
        (
            mode: "chat" | "history",
            question: string,
            questionContainsTemplate: boolean,
            appTypeValue: AppType,
            forcedProductSelection?: ForcedProductSelection,
            templateLookupProductOverride?: string,
            selectionMeta?: AllProductSelectionMeta
        ) => {
            if (question === "invalid_selection") {
                setEmptyProduct(true);
                return;
            }

            dispatch(QuestionInputReducerAction.updateRequestTriggerType("QuestionInput"));
            setIsHistoryPageInitialLoading(false);
            setIsNewChatThreadInitialLoading?.(false);
            currentTargetQuestionId.current = mode === "chat" ? 0 : answers?.length;
            isTabClick.current = "false";
            if (mode === "history") {
                setCurrentIndex(sessionStorage.getItem("sessionId") ?? "");
            }

            const templateLookupProduct = templateLookupProductOverride ?? selectedProduct;
            const userDefinedTemplateId = userInfo?.["userFavourite"]?.[templateLookupProduct];
            const systemDefinedTemplateID = templateContents.filter((temp: any) => temp.template[0].isDefault === true).map((temp: any) => temp.id)[0];

            const templateId = selectedTemplateContentId ? selectedTemplateContentId : userDefinedTemplateId ? userDefinedTemplateId : systemDefinedTemplateID;

            setSaveTemplateIdInStore(templateId);
            currentQuestionInfo.current = null;
            setIsOnEdit(false);
            makeApiRequest(
                questionContainsTemplate && !!(templateId && templateId !== "") && templateId !== "None"
                    ? getTemplateContentForPayload(templateId, question, templateContents)
                    : question,
                question,
                appTypeValue,
                appTypeValue === "ALL",
                store.getState().questionInput.fileAttachments,
                undefined,
                undefined,
                undefined,
                undefined,
                questionContainsTemplate ? templateId : "",
                undefined,
                forcedProductSelection,
                selectionMeta,
                mode
            );
            setEmptyProduct(false);
            setHideDynamicTemplatePopup(hideDynamicTemplatePopup);
            resetTemplateSelection(true);
            setSaveTemplateIdInStore("");
        },
        [
            answers?.length,
            dispatch,
            hideDynamicTemplatePopup,
            makeApiRequest,
            resetTemplateSelection,
            selectedProduct,
            selectedTemplateContentId,
            setCurrentIndex,
            setIsHistoryPageInitialLoading,
            setIsNewChatThreadInitialLoading,
            templateContents
        ]
    );
    // Keep the ref in sync so early callbacks can call the latest version.
    handleQuestionInputSendRef.current = handleQuestionInputSend;

    const handleChatQuestionInputSend = useCallback(
        (question: string, questionContainsTemplate: boolean, appTypeValue: AppType) =>
            handleQuestionInputSend("chat", question, questionContainsTemplate, appTypeValue),
        [handleQuestionInputSend]
    );

    const handleHistoryQuestionInputSend = useCallback(
        (question: string, questionContainsTemplate: boolean, appTypeValue: AppType) =>
            handleQuestionInputSend("history", question, questionContainsTemplate, appTypeValue),
        [handleQuestionInputSend]
    );

    const applyMatchedProductSelection = useCallback(
        async (
            selectedProductOption: AllProductMatchedProduct,
            nextAppType: AppType,
            options?: { updateDropdown?: boolean; persistServer?: boolean }
        ): Promise<{ matchedProduct: Product; forcedProductSelection: ForcedProductSelection; didSwitchProduct: boolean }> => {
            const updateDropdown = options?.updateDropdown !== false; // default true
            const persistServer = options?.persistServer !== false; // default true
            // Normalize both sides so numeric coercion, leading zeros, brackets, and
            // whitespace in the catalog keys cannot prevent a match against the
            // already-normalized keys produced by mapMatchedProductsFromDataPoints.
            const matchedProduct = products.find(
                product =>
                    normalizeComparableProductKey(product.manual_key) === normalizeComparableProductKey(selectedProductOption.manual_key) &&
                    normalizeComparableProductKey(product.faq_key) === normalizeComparableProductKey(selectedProductOption.faq_key)
            );
            if (!matchedProduct) {
                throw new Error("Matched product not found in available product categories");
            }

            const didSwitchProduct =
                updateDropdown &&
                (normalizeComparableProductKey(productIndexes.manual_key) !== normalizeComparableProductKey(matchedProduct?.manual_key) ||
                    normalizeComparableProductKey(productIndexes.faq_key) !== normalizeComparableProductKey(matchedProduct?.faq_key));

            if (updateDropdown) {
                const isBrowseSelection = selectedProductOption.fromBrowse === true;

                if (isBrowseSelection) {
                    setSuggestionDrivenTemplateSuppressedProduct(null);
                } else {
                    // Suggestions-panel selections should never rehydrate template text,
                    // even when product keys stay the same and only version/sub changes.
                    setSuggestionDrivenTemplateSuppressedProduct(matchedProduct.text);
                    setClearedTemplateSelectionProduct(matchedProduct.text);
                    dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
                    setSelectedTemplateContentId("");
                    setSelectedTemplateContent("");
                    setChatContentCleared(true);
                }

                // Suggestions-panel selections are programmatic, even when they keep
                // the same product keys (for example parent/version group clicks).
                if (!isBrowseSelection) {
                    markTemplateApplySourceForNextSwitch("programmatic", matchedProduct.text, {
                        manual_key: matchedProduct?.manual_key ?? null,
                        faq_key: matchedProduct?.faq_key ?? null
                    });
                }
                setSelectedAppType(nextAppType);
                setProductIndexes({
                    manual_key: matchedProduct?.manual_key?.toString() ?? "",
                    faq_key: matchedProduct?.faq_key?.toString() ?? ""
                });
                setProductText(matchedProduct.text);
                dispatch(setSelectedProductInStore(matchedProduct.text));
            }

            const hasVersions = matchedProduct?.versions?.length > 0;
            const isSubOnlyProduct = matchedProduct?.is_sub_only === "true";
            const productVersions: Array<any> = Array.isArray((matchedProduct as any)?.versions) ? (matchedProduct as any).versions : [];
            const normalizedSelectedVersion = selectedProductOption.productVersion
                ? {
                      manual_version_key: normalizeComparableProductKey(selectedProductOption.productVersion.manual_version_key),
                      faq_version_key: normalizeComparableProductKey(selectedProductOption.productVersion.faq_version_key)
                  }
                : null;
            const normalizedSelectedSubProduct = selectedProductOption.subProduct
                ? {
                      manual_sub_key: normalizeComparableProductKey(selectedProductOption.subProduct.manual_sub_key),
                      faq_sub_key: normalizeComparableProductKey(selectedProductOption.subProduct.faq_sub_key)
                  }
                : null;
            const hasExplicitVersionSelection = !!(
                normalizedSelectedVersion &&
                (normalizedSelectedVersion.manual_version_key || normalizedSelectedVersion.faq_version_key)
            );
            const hasExplicitSubSelection = !!(
                normalizedSelectedSubProduct &&
                (normalizedSelectedSubProduct.manual_sub_key || normalizedSelectedSubProduct.faq_sub_key)
            );

            const hasMatchingKeyPair = (leftManual: unknown, leftFaq: unknown, rightManual: unknown, rightFaq: unknown): boolean => {
                const normalizedLeftManual = normalizeComparableProductKey(leftManual);
                const normalizedLeftFaq = normalizeComparableProductKey(leftFaq);
                const normalizedRightManual = normalizeComparableProductKey(rightManual);
                const normalizedRightFaq = normalizeComparableProductKey(rightFaq);

                return (
                    (!!normalizedLeftManual && !!normalizedRightManual && normalizedLeftManual === normalizedRightManual) ||
                    (!!normalizedLeftFaq && !!normalizedRightFaq && normalizedLeftFaq === normalizedRightFaq)
                );
            };

            const findSelectedVersionEntry = (versionSelection: { manual_version_key: string; faq_version_key: string } | null) =>
                versionSelection
                    ? productVersions.find(versionEntry =>
                          hasMatchingKeyPair(
                              versionEntry?.manual_version_key,
                              versionEntry?.faq_version_key,
                              versionSelection.manual_version_key,
                              versionSelection.faq_version_key
                          )
                      )
                    : null;

            const findSelectedSubOnlyEntry = (subSelection: { manual_sub_key: string; faq_sub_key: string } | null) =>
                subSelection
                    ? productVersions.find(subEntry =>
                          hasMatchingKeyPair(
                              subEntry?.manual_sub_key ?? subEntry?.manual_version_key,
                              subEntry?.faq_sub_key ?? subEntry?.faq_version_key,
                              subSelection.manual_sub_key,
                              subSelection.faq_sub_key
                          )
                      )
                    : null;

            const findVersionForSubSelection = (subSelection: { manual_sub_key: string; faq_sub_key: string } | null) => {
                if (!subSelection) return null;
                return productVersions.find(versionEntry => {
                    const subVersions: Array<any> = Array.isArray(versionEntry?.sub_versions) ? versionEntry.sub_versions : [];
                    return subVersions.some(subEntry =>
                        hasMatchingKeyPair(subEntry?.manual_sub_key, subEntry?.faq_sub_key, subSelection.manual_sub_key, subSelection.faq_sub_key)
                    );
                });
            };

            let selectedProductVersion = isSubOnlyProduct
                ? { manual_version_key: "", faq_version_key: "" }
                : hasVersions
                  ? { manual_version_key: "0", faq_version_key: "0" }
                  : { manual_version_key: "", faq_version_key: "" };
            let selectedSubProduct = isSubOnlyProduct
                ? { manual_sub_key: "0", faq_sub_key: "0" }
                : hasVersions
                  ? { manual_sub_key: "0", faq_sub_key: "0" }
                  : { manual_sub_key: "", faq_sub_key: "" };
            let selectedVersionTextValue = isSubOnlyProduct ? "" : hasVersions ? "ALL" : "";
            let selectedSubTextValue = isSubOnlyProduct || hasVersions ? "ALL" : "";

            if (hasExplicitVersionSelection && normalizedSelectedVersion) {
                selectedProductVersion = {
                    manual_version_key: normalizedSelectedVersion.manual_version_key,
                    faq_version_key: normalizedSelectedVersion.faq_version_key
                };
                const selectedVersionEntry = findSelectedVersionEntry(normalizedSelectedVersion);
                if (selectedVersionEntry?.text) {
                    selectedVersionTextValue = selectedVersionEntry.text;
                }
                if (!hasExplicitSubSelection && hasVersions && !isSubOnlyProduct) {
                    selectedSubProduct = { manual_sub_key: "0", faq_sub_key: "0" };
                    selectedSubTextValue = "ALL";
                }
            }

            if (hasExplicitSubSelection && normalizedSelectedSubProduct) {
                selectedSubProduct = {
                    manual_sub_key: normalizedSelectedSubProduct.manual_sub_key,
                    faq_sub_key: normalizedSelectedSubProduct.faq_sub_key
                };

                if (isSubOnlyProduct) {
                    selectedProductVersion = { manual_version_key: "", faq_version_key: "" };
                    selectedVersionTextValue = "";
                    const selectedSubOnlyEntry = findSelectedSubOnlyEntry(normalizedSelectedSubProduct);
                    if (selectedSubOnlyEntry?.text) {
                        selectedSubTextValue = selectedSubOnlyEntry.text;
                    } else if (selectedSubProduct.manual_sub_key === "0" || selectedSubProduct.faq_sub_key === "0") {
                        selectedSubTextValue = "ALL";
                    }
                } else {
                    let selectedVersionEntry = hasExplicitVersionSelection ? findSelectedVersionEntry(normalizedSelectedVersion) : null;
                    if (!selectedVersionEntry) {
                        selectedVersionEntry = findVersionForSubSelection(normalizedSelectedSubProduct);
                        if (selectedVersionEntry) {
                            selectedProductVersion = {
                                manual_version_key: normalizeComparableProductKey(selectedVersionEntry?.manual_version_key),
                                faq_version_key: normalizeComparableProductKey(selectedVersionEntry?.faq_version_key)
                            };
                            if (selectedVersionEntry?.text) {
                                selectedVersionTextValue = selectedVersionEntry.text;
                            }
                        }
                    }

                    const selectedVersionSubEntries: Array<any> = Array.isArray(selectedVersionEntry?.sub_versions) ? selectedVersionEntry?.sub_versions : [];
                    const selectedSubEntry = selectedVersionSubEntries.find(subEntry =>
                        hasMatchingKeyPair(
                            subEntry?.manual_sub_key,
                            subEntry?.faq_sub_key,
                            normalizedSelectedSubProduct.manual_sub_key,
                            normalizedSelectedSubProduct.faq_sub_key
                        )
                    );
                    if (selectedSubEntry?.text) {
                        selectedSubTextValue = selectedSubEntry.text;
                    } else if (selectedSubProduct.manual_sub_key === "0" || selectedSubProduct.faq_sub_key === "0") {
                        selectedSubTextValue = "ALL";
                    }
                }
            }

            if (updateDropdown) {
                setProductVersionIndexes(selectedProductVersion);
                setSubProductIndexes(selectedSubProduct);
                setProductVersionText(selectedVersionTextValue);
                setSubProductText(selectedSubTextValue);

                userInfo.product = {
                    manual_key: matchedProduct?.manual_key?.toString() ?? "",
                    faq_key: matchedProduct?.faq_key?.toString() ?? ""
                };

                if (persistServer) {
                    void saveUserSettings({
                        product: {
                            manual_key: matchedProduct?.manual_key?.toString() ?? "",
                            faq_key: matchedProduct?.faq_key?.toString() ?? ""
                        },
                        productVersion: selectedProductVersion,
                        subProduct: selectedSubProduct
                    }).catch(error => {
                        console.error("Failed to persist matched product selection before send", {
                            error,
                            selectedProductOption
                        });
                    });
                }
            }

            return {
                matchedProduct,
                didSwitchProduct,
                forcedProductSelection: {
                    product_filter: {
                        manual_key: matchedProduct?.manual_key?.toString() ?? "",
                        faq_key: matchedProduct?.faq_key?.toString() ?? ""
                    },
                    sub_product_filter: selectedSubProduct,
                    product_version: selectedProductVersion
                }
            };
        },
        [
            dispatch,
            productIndexes.faq_key,
            productIndexes.manual_key,
            products,
            setChatContentCleared,
            setSelectedAppType,
            selectedProduct,
            setSelectedTemplateContent,
            setSelectedTemplateContentId
        ]
    );

    const handleAllProductTagSelected = useCallback(
        async (
            mode: "chat" | "history",
            selectedProductOption: AllProductMatchedProduct,
            sendRequest: PreparedSendRequest,
            selectionMeta?: AllProductSelectionMeta
        ) => {
            setIsHistoryPageInitialLoading(false);
            setIsNewChatThreadInitialLoading?.(false);
            dispatch(QuestionInputReducerAction.resetQuestionInputHistoryStacks());

            const nextAppType: AppType = getValidAppType(sendRequest.appTypeBasedOnUser);

            try {
                const { matchedProduct, forcedProductSelection } = await applyMatchedProductSelection(selectedProductOption, nextAppType);
                handleQuestionInputSend(
                    mode,
                    sendRequest.questionText,
                    sendRequest.questionContainsTemplateValue,
                    nextAppType,
                    forcedProductSelection,
                    matchedProduct.text,
                    selectionMeta
                );
                if (selectedProductOption.fromBrowse === true) {
                    setSuggestionDrivenTemplateSuppressedProduct(null);
                }
            } catch (error) {
                console.error("Matched product not found in available product categories", {
                    error,
                    selectedProductOption
                });
                handleQuestionInputSend(mode, sendRequest.questionText, sendRequest.questionContainsTemplateValue, sendRequest.appTypeBasedOnUser);
            }
        },
        [applyMatchedProductSelection, dispatch, handleQuestionInputSend, setIsHistoryPageInitialLoading, setIsNewChatThreadInitialLoading]
    );

    const allProductAutoSelectRuntimeConfig = useMemo(
        () => ({
            enabled: allProductAutoSelectEnabled,
            minConfidence: allProductAutoSelectMinConfidence,
            minMargin: allProductAutoSelectMinMargin,
            minHits: allProductAutoSelectMinHits,
            minAvgRerankerScore: allProductAutoSelectMinAvgRerankerScore,
            minAvgRerankerGap: allProductAutoSelectMinAvgRerankerGap,
            weightAverage: allProductAutoSelectWeightAverage,
            weightPeak: allProductAutoSelectWeightPeak,
            weightVolume: allProductAutoSelectWeightVolume
        }),
        [
            allProductAutoSelectEnabled,
            allProductAutoSelectMinConfidence,
            allProductAutoSelectMinMargin,
            allProductAutoSelectMinHits,
            allProductAutoSelectMinAvgRerankerScore,
            allProductAutoSelectMinAvgRerankerGap,
            allProductAutoSelectWeightAverage,
            allProductAutoSelectWeightPeak,
            allProductAutoSelectWeightVolume
        ]
    );

    const handleActiveFileAttachmentTour = useCallback(
        (tourFlag: boolean) => {
            dispatch(QuestionInputReducerAction.updateActiveFileAttachmentTour(tourFlag));
            proceedAppTourForAnswer();
        },
        [dispatch, proceedAppTourForAnswer]
    );

    const sharedChatInputWrapperProps = useMemo(
        () => ({
            clearOnSend: true,
            disabled: isLoading || isAnswerLoading || applyRestriction || isStreaming || showSuggestionsPanel,
            isAdmin,
            productIndex: productIndexes,
            templateContent: selectedTemplateContent,
            selectedTemplateContentId,
            onClearTemplateContent: handleClearTemplateContent,
            disableTextarea: applyRestriction || showSuggestionsPanel || isLoading || isAnswerLoading || isStreaming,
            isEdited,
            setIsEdited,
            clearChatTriggered,
            setClearChatTriggered,
            chatContentCleared,
            setChatContentCleared,
            selectedAppType,
            isCitationActive: activeAnalysisPanelTab !== undefined,
            isHistoryPageInitialLoading,
            setIsHistoryPageInitialLoading,
            changingTemplateContent,
            setChangingTemplateContent,
            handleTemplateContentsBySuggestions,
            isNewChatThreadInitialLoading,
            setIsNewChatThreadInitialLoading,
            answers,
            setSelectedAppType,
            setActiveFileAttachmentTour: handleActiveFileAttachmentTour,
            onAllProductPrecheck: runAllProductCitationPrecheck,
            onAbortAllProductPrecheck: abortAllProductCitationPrecheck,
            onAllProductTagSelected: handleAllProductTagSelected,
            allProductAutoSelectConfig: allProductAutoSelectRuntimeConfig,
            showInlineAllProductValidationFallback: false,
            toolbarProps
        }),
        [
            activeAnalysisPanelTab,
            answers,
            applyRestriction,
            changingTemplateContent,
            chatContentCleared,
            clearChatTriggered,
            handleActiveFileAttachmentTour,
            abortAllProductCitationPrecheck,
            handleClearTemplateContent,
            isAdmin,
            isAnswerLoading,
            isEdited,
            isHistoryPageInitialLoading,
            isLoading,
            isNewChatThreadInitialLoading,
            isStreaming,
            productIndexes,
            allProductAutoSelectRuntimeConfig,
            selectedAppType,
            selectedTemplateContent,
            selectedTemplateContentId,
            showSuggestionsPanel,
            toolbarProps,
            runAllProductCitationPrecheck,
            handleAllProductTagSelected
        ]
    );

    const chatInputWrapperProps = useMemo(
        () => ({
            ...sharedChatInputWrapperProps,
            mode: "chat" as const,
            onSend: handleChatQuestionInputSend,
            clarificationBanner: clarificationMode === "chat" ? clarificationInfo : null,
            onSendAnyway: clarificationMode === "chat" ? handleClarificationSendAnyway : undefined,
            onDismissClarification: clarificationMode === "chat" ? handleClarificationDismiss : undefined
        }),
        [
            handleChatQuestionInputSend,
            sharedChatInputWrapperProps,
            clarificationMode,
            clarificationInfo,
            handleClarificationSendAnyway,
            handleClarificationDismiss
        ]
    );

    const historyInputWrapperProps = useMemo(
        () => ({
            ...sharedChatInputWrapperProps,
            mode: "history" as const,
            onSend: handleHistoryQuestionInputSend,
            clarificationBanner: clarificationMode === "history" ? clarificationInfo : null,
            onSendAnyway: clarificationMode === "history" ? handleClarificationSendAnyway : undefined,
            onDismissClarification: clarificationMode === "history" ? handleClarificationDismiss : undefined
        }),
        [
            handleHistoryQuestionInputSend,
            sharedChatInputWrapperProps,
            clarificationMode,
            clarificationInfo,
            handleClarificationSendAnyway,
            handleClarificationDismiss
        ]
    );

    const selectedAnalysisAppType = answers[selectedAnswer] ? getAnalysisAppTypeForAnswer(answers[selectedAnswer]) : undefined;
    const selectedAnalysisAnswer = selectedAnalysisAppType ? answers[selectedAnswer]?.[1]?.[selectedAnalysisAppType] : undefined;
    const shouldShowRightSidePanel = showSuggestionsPanel || (!!selectedAnalysisAnswer && activeAnalysisPanelTab !== undefined);

    const chatMessagesAreaProps = useMemo(
        () => ({
            answerAreaRef,
            answers,
            userChatMessageRefs,
            answersRefs,
            isTabClick,
            dispatch,
            QuestionInputReducerAction,
            makeApiRequest,
            currentTargetQuestionId,
            selectedAppType,
            currentQuestionInfo,
            onEditClick,
            IsOnEdit,
            isAdmin,
            selectedQuestion,
            applyRestriction,
            chatResponseAreaRefValue,
            isLoading,
            isStreaming,
            searchTerm,
            setSelectedAppType,
            versionIndexMap,
            applyVersionChange,
            histories,
            setRefsForAnswersRender,
            lastAnswerIndexRef,
            setSelectedAnswer,
            setSelectedCitation,
            doctype,
            selectedAnswer,
            activeAnalysisPanelTab,
            renderUnifiedAnswer,
            lastQuestionRef,
            error,
            chatMessageStreamEnd,
            onShowCitation,
            onToggleTab,
            setOverAllCurrentSessionDataPoints,
            setAnswers,
            onFeedbackSubmitClicked,
            isRegenerateActive,
            expandedQAIndex,
            onExpandQA: (index: number) => {
                setExpandedQAIndex(index);
                setSelectedAnswer(index);
            },
            clearActiveAnalysisPanelTab: () => setActiveAnalysisPanelTab(undefined)
        }),
        [
            IsOnEdit,
            activeAnalysisPanelTab,
            answerAreaRef,
            answers,
            answersRefs,
            applyRestriction,
            applyVersionChange,
            chatMessageStreamEnd,
            chatResponseAreaRefValue,
            currentQuestionInfo,
            currentTargetQuestionId,
            dispatch,
            doctype,
            error,
            histories,
            isAdmin,
            isLoading,
            isRegenerateActive,
            isStreaming,
            isTabClick,
            lastAnswerIndexRef,
            lastQuestionRef,
            makeApiRequest,
            onEditClick,
            onFeedbackSubmitClicked,
            onShowCitation,
            onToggleTab,
            renderUnifiedAnswer,
            searchTerm,
            selectedAnswer,
            selectedAppType,
            selectedQuestion,
            setOverAllCurrentSessionDataPoints,
            setRefsForAnswersRender,
            setSelectedAnswer,
            setSelectedAppType,
            setSelectedCitation,
            setAnswers,
            userChatMessageRefs,
            versionIndexMap,
            expandedQAIndex,
            setExpandedQAIndex
        ]
    );

    return (
        <div className={styles.overall_div}>
            {!isLoading && isAnswerLoading && !isStreaming && !isRegenerateRequestInFlight && <Spinner />}
            <AppTour run={tourOne.run} steps={tourOne.steps} onJoyrideCallback={handleJoyrideCallback} />
            <Sidebar
                sourcePage={"chat"}
                todaySessionsRef={todaySessionsRef}
                setTodaySessions={setTodaySessionsRef}
                setLast7DaySessions={setLast7SessionsRef}
                setLast30DaySessions={setLast30SessionsRef}
                last7SessionsRef={last7SessionsRef}
                last30SessionsRef={last30SessionsRef}
                monthlySessions={monthlySessions}
                isLoading={isLoading}
                lastActiveIndexRef={lastActiveIndexRef}
                lastSessionRef={lastSessionRef}
                lastActiveSessionRef={lastActiveSessionRef}
                currentIndex={currentIndex}
                loadChatData={async (session_id: string) => {
                    if (serverSideFileAttachmentsIds && serverSideFileAttachmentsIds.length > 0) {
                        deleteFile(serverSideFileAttachmentsIds);
                    }
                    updateAppTypeValueInSessionStorage(selectedAppType);
                    dispatch(QuestionInputReducerAction.resetQuestionInputHistoryStacks());
                    dispatch(QuestionInputReducerAction.resetAnswerGenerationActiveDetails());
                    try {
                        await loadChatData(session_id, true, false);
                    } catch (error) {
                        console.error("Error loading chat data:", error);
                    }
                }}
                setCurrentIndex={(session_id: string) => {
                    setCurrentIndex(session_id);
                }}
                nextDate={nextDateToLoad}
                loadSessionsData={async (searchTerm: string, isNextLoadClicked: boolean) => {
                    setSearchTerm(searchTerm);
                    if (!isNextLoadClicked && !isSessionSearchActive) {
                        setBackupTodaySessions([...todaySessionsRef]);
                        setBackupLast7Sessions([...last7SessionsRef]);
                        setBackupLast30Sessions([...last30SessionsRef]);
                        setBackupMonthlySessions([...monthlySessions]);
                        if (nextDateToLoadRef.current === "") {
                            nextDateToLoadRef.current = nextDateToLoad;
                        }
                    }
                    try {
                        await loadSessionData("", searchTerm, isNextLoadClicked);
                    } catch (error) {
                        console.error("Error loading session data:", error);
                    }
                }}
                isOnSave={isOnSave || isAnswerLoading}
                clearChat={() => {
                    suppressAppTypeUrlSyncRef.current = true;
                    clearChat();
                    navigate("/assistant/chat");
                }}
                isSessionLoading={isSessionLoading}
                isAdmin={isAdmin}
                clearSearchChat={() => {
                    setSearchTerm("");
                    setNextDateToLoad(nextDateToLoadRef.current);
                    setTodaySessionsRef([...backupTodaySessions]);
                    setLast7SessionsRef([...backupLast7Sessions]);
                    setLast30SessionsRef([...backupLast30Sessions]);
                    setMonthlySessions([...backupMonthlySessions]);
                    nextDateToLoadRef.current = "";
                }}
                refreshSessionsAfterDelete={async (deletedSessionId?: string) => {
                    if (deletedSessionId) {
                        await handleDeleteSession(deletedSessionId);
                    }
                }}
            />
            <div className={styles.mainContainer}>
                <Snackbar
                    open={isSnackOpen}
                    autoHideDuration={1000}
                    onClose={handleSnackBarClose}
                    message={i18next.t("template.templateApplied")}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    ContentProps={{
                        className: styles.snackbarSuccess
                    }}
                />
                <Box className={styles.container}>
                    <Box className={`${styles.chatRoot} ${styles.chatRootScrollable}`}>
                        {isSessionNotFound && (
                            <SessionNotFound
                                open={isSessionNotFound}
                                sourcePage="chat"
                                onConfirm={() => {
                                    setIsSessionNotFound(false);
                                    suppressAppTypeUrlSyncRef.current = true;
                                    clearChat();
                                    navigate("/assistant/chat");
                                }}
                            />
                        )}
                        {!isSessionNotFound && (
                            <div id="chatAndCitationContainer" className={styles.chatAndCitationContainer}>
                                <div className={`${styles.chatContentWrapper} ${shouldShowRightSidePanel ? styles.chatContentWrapperWithPanel : ""}`}>
                                    <Box className={styles.chatContainer}>
                                        <div className={styles.chatSurface}>
                                            {!lastQuestionRef.current.question && currentView !== "history" ? (
                                                <EmptyChatState
                                                    sessionStatus={sessionStatus}
                                                    onDisclaimerDismiss={() => setSessionStatus("active")}
                                                    showSupportLink={showSupportLink}
                                                    supportEnvBoard={supportEnvBoard}
                                                    questionInputRef={el => {
                                                        questionInputRefs.current["chat"] = el;
                                                    }}
                                                    chatInputWrapperProps={chatInputWrapperProps}
                                                />
                                            ) : (
                                                <ChatMessagesArea {...chatMessagesAreaProps} />
                                            )}
                                            <DynamicTemplatePrompt
                                                hideDynamicTemplatePopup={hideDynamicTemplatePopup}
                                                handleTemplateIconClick={handleTemplateIconClick}
                                                selectedTemplate={selectedTemplate}
                                                handleTemplateClick={handleTemplateClick}
                                                handleTemplateContents={handleTemplateContents}
                                                handleTemplateContentsBySuggestions={handleTemplateContentsBySuggestions}
                                                handleClearTemplateContent={handleClearTemplateContent}
                                                products={products}
                                                selectedProduct={productText}
                                                snackbar={handleSnackBar}
                                                setIsEdited={setIsEdited}
                                            />
                                            {(newChatQuestion !== "" || currentView === "history") && (
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        minWidth: "45%",
                                                        maxWidth: "1040px",
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    <ChatInputWrapper
                                                        ref={el => {
                                                            questionInputRefs.current["history"] = el;
                                                        }}
                                                        {...historyInputWrapperProps}
                                                        showSupportLink={showSupportLink}
                                                        supportEnvBoard={supportEnvBoard}
                                                    />
                                                </Box>
                                            )}
                                        </div>
                                    </Box>
                                    {shouldShowRightSidePanel &&
                                        (showSuggestionsPanel ? (
                                            <div className={styles.chatSuggestionsSidePanel}>
                                                <AllProductSuggestionsPanel
                                                    className={styles.chatSuggestionsPanelCard}
                                                    allProducts={products}
                                                    allProductAutoSelectConfig={allProductAutoSelectRuntimeConfig}
                                                    isAdminUser={isAdmin}
                                                    allowContinueWithAll={allowContinueWithAll}
                                                    onSelectProduct={
                                                        productScenarioRegenerateContext ? handleProductScenarioRegenerateSelectProduct : undefined
                                                    }
                                                    onContinueWithAll={
                                                        productScenarioRegenerateContext ? handleProductScenarioRegenerateContinueWithAll : undefined
                                                    }
                                                    onCancel={productScenarioRegenerateContext ? handleProductScenarioRegenerateCancel : undefined}
                                                />
                                            </div>
                                        ) : (
                                            <AnalysisPanel
                                                className={styles.chatAnalysisPanel}
                                                activeCitation={activeCitation}
                                                onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                                                citationHeight="810px"
                                                answer={selectedAnalysisAnswer}
                                                activeTab={activeAnalysisPanelTab}
                                                index={selectedCitation}
                                                isAdminUser={isAdmin}
                                                onActiveContentChanged={citationIndex => {
                                                    setSelectedCitation(citationIndex);
                                                }}
                                                isStreaming={isStreaming}
                                                sourcePage={"Chat"}
                                                isThoughtsLoading={
                                                    activeAnalysisPanelTab === AnalysisPanelTabs.ThoughtProcessTab && thoughtsLoadingIndex === selectedAnswer
                                                }
                                                appType={selectedAnalysisAppType ?? selectedAppType}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}
                        <SettingsPanel
                            isConfigPanelOpen={isConfigPanelOpen}
                            setIsConfigPanelOpen={setIsConfigPanelOpen}
                            hidetoken={hidetoken}
                            useSemanticRanker={useSemanticRanker}
                            useSemanticCaptions={useSemanticCaptions}
                            useSuggestFollowupQuestions={useSuggestFollowupQuestions}
                            useOidSecurityFilter={useOidSecurityFilter}
                            useGroupsSecurityFilter={useGroupsSecurityFilter}
                            retrieveCount={retrieveCount}
                            agenticRetrievalOutputSize={agenticRetrievalOutputSize}
                            agenticRetrievalDocumentCount={agenticRetrievalDocumentCount}
                            agenticRetrievalSourceDocumentCount={agenticRetrievalSourceDocumentCount}
                            promptTemplate={promptTemplate}
                            excludeCategory={excludeCategory}
                            retrievalMode={retrievalMode}
                            cosmosDBRetrievalMode={cosmosDBRetrievalMode}
                            shouldStream={shouldStream}
                            onlyCitations={onlyCitations}
                            retrievalOptions={retrievalOptions}
                            cosmosDBRetrievalOptions={cosmosDBRetrievalOptions}
                            retrievalReasoningEffort={retrievalReasoningEffort}
                            retrievalReasoningOptions={retrievalReasoningOptions}
                            onRetrievalReasoningEffortChange={onRetrievalReasoningEffortChange}
                            onRetrieveCountChange={onRetrieveCountChange}
                            onAgenticRetrievalOutputSizeChange={onAgenticRetrievalOutputSizeChange}
                            onAgenticRetrievalOutputSizeBlur={onAgenticRetrievalOutputSizeBlur}
                            onAgenticRetrievalDocumentCountChange={onAgenticRetrievalDocumentCountChange}
                            onAgenticRetrievalDocumentCountBlur={onAgenticRetrievalDocumentCountBlur}
                            onAgenticRetrievalSourceDocumentCountChange={onAgenticRetrievalSourceDocumentCountChange}
                            onAgenticRetrievalSourceDocumentCountBlur={onAgenticRetrievalSourceDocumentCountBlur}
                            onRetrievalModeChange={onRetrievalModeChange}
                            onCosmosDBRetrievalModeChange={onCosmosDBRetrievalModeChange}
                            onUseSemanticRankerChange={onUseSemanticRankerChange}
                            onUseSemanticCaptionsChange={onUseSemanticCaptionsChange}
                            onShouldStreamChange={onShouldStreamChange}
                            onPromptTemplateChange={onPromptTemplateChange}
                            onExcludeCategoryChanged={onExcludeCategoryChanged}
                            onUseSuggestFollowupQuestionsChange={onUseSuggestFollowupQuestionsChange}
                            onUseOidSecurityFilterChange={onUseOidSecurityFilterChange}
                            onUseGroupsSecurityFilterChange={onUseGroupsSecurityFilterChange}
                            onOnlyCitationsChange={onOnlyCitationsChange}
                            useAgenticKnowledgebase={useAgenticKnowledgebase}
                            onUseAgenticKnowledgebaseChange={onUseAgenticKnowledgebaseChange}
                            enableAllProductForAllUsers={enableAllProductForAllUsers}
                            onEnableAllProductForAllUsersChange={onEnableAllProductForAllUsersChange}
                            enableAllProductForUser={enableAllProductForUser}
                            onEnableAllProductForUserChange={onEnableAllProductForUserChange}
                            allProductAutoSelectEnabled={allProductAutoSelectEnabled}
                            allProductAutoSelectMinConfidence={allProductAutoSelectMinConfidence}
                            allProductAutoSelectMinMargin={allProductAutoSelectMinMargin}
                            allProductAutoSelectMinHits={allProductAutoSelectMinHits}
                            allProductAutoSelectMinAvgRerankerScore={allProductAutoSelectMinAvgRerankerScore}
                            allProductAutoSelectMinAvgRerankerGap={allProductAutoSelectMinAvgRerankerGap}
                            allProductAutoSelectWeightAverage={allProductAutoSelectWeightAverage}
                            allProductAutoSelectWeightPeak={allProductAutoSelectWeightPeak}
                            allProductAutoSelectWeightVolume={allProductAutoSelectWeightVolume}
                            onAllProductAutoSelectEnabledChange={onAllProductAutoSelectEnabledChange}
                            onAllProductAutoSelectMinConfidenceChange={onAllProductAutoSelectMinConfidenceChange}
                            onAllProductAutoSelectMinMarginChange={onAllProductAutoSelectMinMarginChange}
                            onAllProductAutoSelectMinHitsChange={onAllProductAutoSelectMinHitsChange}
                            onAllProductAutoSelectMinAvgRerankerScoreChange={onAllProductAutoSelectMinAvgRerankerScoreChange}
                            onAllProductAutoSelectMinAvgRerankerGapChange={onAllProductAutoSelectMinAvgRerankerGapChange}
                            onAllProductAutoSelectWeightAverageChange={onAllProductAutoSelectWeightAverageChange}
                            onAllProductAutoSelectWeightPeakChange={onAllProductAutoSelectWeightPeakChange}
                            onAllProductAutoSelectWeightVolumeChange={onAllProductAutoSelectWeightVolumeChange}
                        />
                        {show206ErrorDialog && (
                            <TokenLimitErrorDialog show={show206ErrorDialog} errorMessage={error206Message} onConfirm={handle206ErrorDialogConfirm} />
                        )}
                        {/* QuestionClarificationDialog replaced by inline banner in QuestionInput */}
                    </Box>
                    <WarningPopup
                        type="private"
                        isVisible={localStorage.getItem("private_warning") === "true" && localStorage.getItem("show_warning") === "true"}
                        onDismiss={showWarning}
                    />
                    <WarningPopup type="emptyProduct" isVisible={emptyProduct} onDismiss={clearWarning} />
                    {expandedQAIndex !== null && (
                        <QAExpandOverlay
                            expandedQAIndex={expandedQAIndex}
                            answers={answers}
                            isStreaming={isStreaming}
                            selectedAnswer={selectedAnswer}
                            onClose={() => setExpandedQAIndex(null)}
                            onNavigate={index => {
                                // Close sources/thought-process panel when switching Q&A pairs
                                if (activeAnalysisPanelTab !== undefined) {
                                    setActiveAnalysisPanelTab(undefined);
                                }
                                setExpandedQAIndex(index);
                                setSelectedAnswer(index);
                            }}
                            renderUnifiedAnswer={renderUnifiedAnswer}
                            renderUserQuestion={renderUserQuestion}
                            shouldShowRightSidePanel={shouldShowRightSidePanel}
                            showSuggestionsPanel={showSuggestionsPanel}
                            activeCitation={activeCitation}
                            selectedAnalysisAnswer={selectedAnalysisAnswer}
                            activeAnalysisPanelTab={activeAnalysisPanelTab}
                            selectedCitation={selectedCitation}
                            isAdmin={isAdmin}
                            selectedAppType={selectedAppType}
                            selectedAnalysisAppType={selectedAnalysisAppType}
                            thoughtsLoadingIndex={thoughtsLoadingIndex}
                            onToggleTab={onToggleTab}
                            setSelectedCitation={setSelectedCitation}
                            products={products}
                            allProductAutoSelectConfig={allProductAutoSelectRuntimeConfig}
                            allowContinueWithAll={allowContinueWithAll}
                            productScenarioRegenerateContext={productScenarioRegenerateContext}
                            handleProductScenarioRegenerateSelectProduct={handleProductScenarioRegenerateSelectProduct}
                            handleProductScenarioRegenerateContinueWithAll={handleProductScenarioRegenerateContinueWithAll}
                            handleProductScenarioRegenerateCancel={handleProductScenarioRegenerateCancel}
                        />
                    )}
                </Box>
            </div>
        </div>
    );
};

export default Chat;
